/** 生产环境 HTTPS 站点勿用 http:// 绝对地址，否则浏览器 mixed-content 会拦截登录等请求 */
import { isIpeLoginPath, isIpeWorkbenchPath } from '@/config/ipeRoutes';
import { interceptIpeHttpError, interceptIpeNetworkError } from './ipeRequestErrorInterceptor';
import { resolveSheinManualSyncQuotaKindFromPath } from './sheinManualSyncQuotaKind';
import {
  currentSheinManualSyncClickId,
  parseSheinShopIdFromPath,
  SHEIN_MANUAL_SYNC_CLICK_HEADER,
} from './sheinManualSyncClick';
import { resolveTemuManualSyncQuotaKindFromPath } from './temuManualSyncQuotaKind';
import {
  currentTemuManualSyncClickId,
  currentTemuManualSyncKind,
  parseTemuShopIdFromPath,
  TEMU_MANUAL_SYNC_CLICK_HEADER,
  TEMU_MANUAL_SYNC_KIND_HEADER,
} from './temuManualSyncClick';
import { defaultMessageForHttpStatus } from './ipeErrorSanitizer';
import { getIpeUiLocale } from '@/i18n/storage';
import { localizeApiErrorMessage } from '@/i18n/translateApiError';
import { isJwtExpiredByDailyMidnight } from './jwtDailyExpiry';
import { compressFileForUpload, compressFilesForUpload } from './imageUpload';
import {
  buildUploadFileFromBlob,
  normalizeUploadFileName,
  validateIpeUploadFile,
} from './ipeImageUploadFormats';
import { UPLOAD_CONFIG } from './uploadConfig';
import { runWithRetry, isUploadBackpressureError } from './uploadImageUtils';
import { enrichImageSpaceRecordsForIngest } from './imageSpaceIngestAspect';
import {
  directUploadFilesBatch,
  directUploadOneFile,
  fetchUploadCapabilities,
  isDirectUploadFatalError,
  resetUploadCapabilitiesCache,
} from './ossDirectUpload';
import {
  listSystemModelsWithCache,
} from '@/pages/ipe/workbench/utils/systemModelUserListCache';
import { normalizeTemuAttrsToEditorShape } from '@/temu-platform/utils/normalizeTemuAttrsForEditor';
import { normalizeTiktokAttrsToEditorShape } from '@/tiktok-platform/utils/normalizeTiktokAttrsForEditor';
import {
  batchDeleteTiktokLocalPublishDrafts,
  createTiktokLocalPublishDraft,
  deleteTiktokLocalPublishDraft,
  getTiktokLocalPublishDraft,
  listTiktokLocalPublishDrafts,
  updateTiktokLocalPublishDraft,
} from '@/tiktok-platform/utils/tiktokLocalPublishDraftStore';
import {
  batchDeleteTiktokLocalPublishRecords,
  createTiktokLocalPublishRecord,
  findTiktokLocalPublishRecordByProductId,
  getTiktokLocalPublishRecord,
  listTiktokLocalPublishRecords,
} from '@/tiktok-platform/utils/tiktokLocalPublishRecordStore';
import {
  cancelTiktokPublishRecordSyncJob,
  getTiktokPublishRecordSyncJob,
  startTiktokPublishRecordSyncJob,
} from '@/tiktok-platform/utils/tiktokPublishRecordSync';

import {
  batchDeleteTemuLocalPublishDrafts,
  createTemuLocalPublishDraft,
  deleteTemuLocalPublishDraft,
  getTemuLocalPublishDraft,
  listTemuLocalPublishDrafts,
  updateTemuLocalPublishDraft,
} from '@/temu-platform/utils/temuLocalPublishDraftStore';
import {
  batchDeleteTemuLocalPublishRecords,
  collectPublishRecordLifecycleQueryIds,
  createTemuLocalPublishRecord,
  enrichTemuLocalPublishRecord,
  findTemuLocalPublishRecordBySpu,
  getTemuLocalPublishRecord,
  getTemuPublishLifecycleCache,
  listTemuLocalPublishRecords,
  setTemuPublishLifecycleCache,
  setTemuPublishLifecycleSyncedAt,
} from '@/temu-platform/utils/temuLocalPublishRecordStore';
import {
  buildDiscussStatusCounts,
  mergeTemuAdjustWithDemos,
  mergeTemuDiscussWithDemos,
  seedTemuAdjustDemos,
  seedTemuDiscussDemos,
} from '@/temu-platform/utils/temuPricingDemoSeed';
import {
  mergeTiktokAdjustWithDemos,
  mergeTiktokDiscussWithDemos,
  seedTiktokAdjustDemos,
  seedTiktokDiscussDemos,
} from '@/tiktok-platform/utils/tiktokPricingDemoSeed';
import { sanitizeFundingTaskResultForApi } from '@/pages/ipe/workbench/constants/fundingTaskImageUrlFields';
import {
  isImageSpaceClientLocalId,
  rememberImageSpaceLookupMiss,
  shouldSkipImageSpaceServerLookup,
} from './imageSpaceImportId';

function resolveApiBaseUrl() {
  let base = (process.env.REACT_APP_API_URL || '/api').trim();
  if (base.startsWith('/')) {
    return base.replace(/\/$/, '') || '/api';
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && base.startsWith('http://')) {
    base = base.replace(/^http:\/\//i, 'https://');
  }
  return base.replace(/\/$/, '');
}
export const API_BASE_URL = resolveApiBaseUrl();
const AUTH_TOKEN_KEY = 'auth_token';
const IPE_AUTH_TOKEN_KEY = 'ipe_auth_token';
const ADMIN_AUTH_TOKEN_KEY = 'ipe_admin_token';
const IPE_WORKSPACE_MODE_KEY = 'ipe_workspace_mode';
const LEGACY_FUNDING_AUTH_TOKEN_KEY = 'funding_auth_token';
let otherDeviceLoginHandler = null;
let authInvalidHandler = null;
let ipeAuthRequiredHandler = null;
let ipeCreditsInsufficientHandler = null;
let ipeAccountDeactivatedHandler = null;
let sheinWorkspaceAuthRequiredHandler = null;
let temuWorkspaceAuthRequiredHandler = null;
let tiktokWorkspaceAuthRequiredHandler = null;

/** 进程内 token 缓存，避免 login→localStorage 与并发请求之间的极短空窗 */
let ipeTokenMemory = null;

let ipeAuthReadyResolve = null;
let ipeAuthReadyPromise = new Promise((resolve) => {
  ipeAuthReadyResolve = resolve;
});
let ipeAuthReady = false;

export function resetIpeAuthReady() {
  ipeAuthReady = false;
  ipeAuthReadyPromise = new Promise((resolve) => {
    ipeAuthReadyResolve = resolve;
  });
}

export function markIpeAuthReady() {
  ipeAuthReady = true;
  if (ipeAuthReadyResolve) {
    ipeAuthReadyResolve();
    ipeAuthReadyResolve = null;
  }
}

export function waitForIpeAuthReady(timeoutMs = 12000) {
  if (ipeAuthReady || typeof window === 'undefined') {
    return Promise.resolve();
  }
  const onIpe =
    isIpeWorkbenchPath(window.location.pathname) || isIpeLoginPath(window.location.pathname);
  if (!onIpe || !getFundingToken()) {
    return Promise.resolve();
  }
  return Promise.race([
    ipeAuthReadyPromise,
    new Promise((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
}
export function setOtherDeviceLoginHandler(fn) {
  otherDeviceLoginHandler = fn;
}
export function setAuthInvalidHandler(fn) {
  authInvalidHandler = fn;
}
export function setIpeAuthRequiredHandler(fn) {
  ipeAuthRequiredHandler = fn;
}
export function setIpeCreditsInsufficientHandler(fn) {
  ipeCreditsInsufficientHandler = fn;
}
export function setIpeAccountDeactivatedHandler(fn) {
  ipeAccountDeactivatedHandler = fn;
}
export function setSheinWorkspaceAuthRequiredHandler(fn) {
  sheinWorkspaceAuthRequiredHandler = fn;
}
export function setTemuWorkspaceAuthRequiredHandler(fn) {
  temuWorkspaceAuthRequiredHandler = fn;
}
export function setTiktokWorkspaceAuthRequiredHandler(fn) {
  tiktokWorkspaceAuthRequiredHandler = fn;
}

function isSheinWorkspaceExpiredError({ status, code, message } = {}) {
  if (code === 'SHEIN_WORKSPACE_EXPIRED') return true;
  if (Number(status) !== 403) return false;
  return String(message || '').includes('工作区未验证或已过期');
}

function isTemuWorkspaceExpiredError({ status, code, message } = {}) {
  if (code === 'TEMU_WORKSPACE_EXPIRED' || code === 'TEMU_SHOP_AUTH_EXPIRED') return true;
  if (Number(status) !== 403) return false;
  const msg = String(message || '');
  return msg.includes('TEMU 工作区未验证或已过期')
    || (msg.includes('没有可用的') && msg.includes('TEMU'))
    || msg.includes('店铺授权已过期');
}

function isTiktokWorkspaceExpiredError({ status, code, message } = {}) {
  if (code === 'TIKTOK_WORKSPACE_EXPIRED') return true;
  if (Number(status) !== 403) return false;
  return String(message || '').includes('TikTok 工作区未验证或已过期')
    || String(message || '').includes('TIKTOK 工作区未验证或已过期');
}

/** SHEIN 门禁二次验密：密码错误是业务失败，绝不能当成 IPE 登录失效整页踢走 */
function isSheinSessionUnlockPath(pathOnly) {
  return pathOnly === '/shein/session/unlock';
}

/**
 * 阶段 9 T11：判断 IPE 401 是否应触发整页重登。
 * unlock 路径永远不踢；TOKEN_REVOKED / SHEIN 业务接口用 immediate。
 */
export function resolveIpeAuthRequiredOn401({
  pathOnly,
  code,
  isSheinApi = false,
} = {}) {
  if (isSheinSessionUnlockPath(pathOnly)) {
    return { invoke: false, immediate: false };
  }
  return {
    invoke: true,
    immediate: Boolean(isSheinApi) || code === 'TOKEN_REVOKED',
  };
}

/** 是否为账号已注销（后端 code 或文案） */
export function isAccountDeactivatedError(err) {
  var _err$payload;
  if (!err) return false;
  if (err.code === 'ACCOUNT_DEACTIVATED') return true;
  const msg = String(err.message || ((_err$payload = err.payload) === null || _err$payload === void 0 ? void 0 : _err$payload.message) || '').trim();
  return err.status === 403 && msg.includes('已注销');
}

export const IPE_ACCOUNT_SUSPENDED_MESSAGE = '该账号已停用';
export const IPE_ACCOUNT_NOT_REGISTERED_MESSAGE = '账号未注册，请先完成注册';
export const IPE_SMS_CODE_INVALIDATED_MESSAGE = '当前错误次数过多，验证码已失效';

export function isSmsCodeInvalidatedError(err) {
  var _err$payloadSms;
  if (!err) return false;
  if (err.code === 'SMS_CODE_INVALIDATED') return true;
  const msg = String(err.message || ((_err$payloadSms = err.payload) === null || _err$payloadSms === void 0 ? void 0 : _err$payloadSms.message) || '').trim();
  return msg.includes('验证码已失效');
}

export function isAccountNotRegisteredLoginError(err) {
  var _err$payload0;
  if (!err) return false;
  if (err.code === 'ACCOUNT_NOT_REGISTERED') return true;
  const msg = String(err.message || ((_err$payload0 = err.payload) === null || _err$payload0 === void 0 ? void 0 : _err$payload0.message) || '').trim();
  if (Number(err.status) !== 401) return false;
  return msg.includes('尚未注册')
    || msg.includes('账号未注册')
    || msg === IPE_ACCOUNT_NOT_REGISTERED_MESSAGE;
}

export function isAccountSuspendedError(err) {
  var _err$payload;
  if (!err) return false;
  if (err.code === 'ACCOUNT_SUSPENDED') return true;
  const msg = String(err.message || ((_err$payload = err.payload) === null || _err$payload === void 0 ? void 0 : _err$payload.message) || '').trim();
  return err.status === 403 && msg.includes('已停用');
}

/** 登录表单展示用：停用/注销账号固定文案 */
export function resolveIpeLoginErrorMessage(err, fallback = '登录失败') {
  if (isAccountSuspendedError(err)) return IPE_ACCOUNT_SUSPENDED_MESSAGE;
  if (isAccountNotRegisteredLoginError(err)) return IPE_ACCOUNT_NOT_REGISTERED_MESSAGE;
  if (isSmsCodeInvalidatedError(err)) return IPE_SMS_CODE_INVALIDATED_MESSAGE;
  if (isAccountDeactivatedError(err)) return '该账号已注销';
  return err?.message || fallback;
}

function isIpeLoginAttemptPath(pathOnly) {
  return pathOnly === '/ipe-users/login'
    || pathOnly === '/ipe-users/login-by-sms'
    || pathOnly === '/auth/login'
    || pathOnly === '/auth/login-by-sms';
}

function isIpeUserVerificationCodePath(pathOnly) {
  return pathOnly === '/ipe-users/verification-code'
    || pathOnly === '/auth/verification-code';
}

/** 是否为 IPE豆不足（后端 code 或文案） */
export function isCreditsInsufficientError(err) {
  var _err$payload2;
  if (!err) return false;
  if (err.code === 'CREDITS_INSUFFICIENT') return true;
  const msg = String(err.message || ((_err$payload2 = err.payload) === null || _err$payload2 === void 0 ? void 0 : _err$payload2.message) || '').trim();
  return err.status === 403 && (msg.includes('IPE豆不足') || msg.includes('积分不足'));
}

/** 是否为用户图片空间配额不足 */
export function isStorageInsufficientError(err) {
  var _err$payload3;
  if (!err) return false;
  if (err.code === 'STORAGE_INSUFFICIENT') return true;
  const msg = String(err.message || ((_err$payload3 = err.payload) === null || _err$payload3 === void 0 ? void 0 : _err$payload3.message) || '').trim();
  return err.status === 403 && msg.includes('容量不足');
}

/** 是否为平台 OSS 物理存储已满 */
export function isStoragePlatformFullError(err) {
  var _err$payload4;
  if (!err) return false;
  if (err.code === 'STORAGE_PLATFORM_FULL') return true;
  const msg = String(err.message || ((_err$payload4 = err.payload) === null || _err$payload4 === void 0 ? void 0 : _err$payload4.message) || '').trim();
  return (err.status === 503 || err.status === 403) && msg.includes('平台存储空间不足');
}
function clearIpeTokenStorage() {
  ipeTokenMemory = null;
  localStorage.removeItem(IPE_AUTH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_FUNDING_AUTH_TOKEN_KEY);
}

function normalizeStoredToken(raw) {
  const trimmed = raw && String(raw).trim() ? String(raw).trim() : null;
  if (!trimmed) return null;
  if (isJwtExpiredByDailyMidnight(trimmed)) {
    return null;
  }
  return trimmed;
}

export const getFundingToken = () => getIpeToken();

/** 运营后台（/ipe/admin）登录态令牌；与 funding JWT 相互独立 */
function getAdminTokenFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const t = localStorage.getItem(ADMIN_AUTH_TOKEN_KEY);
    return t && String(t).trim() ? String(t).trim() : null;
  } catch {
    return null;
  }
}

/** 是否处于运营后台控制台路径 */
export function isIpeAdminConsole() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname || '';
  return p === '/ipe/admin' || p.startsWith('/ipe/admin/');
}

export function getIpeWorkspaceMode() {
  if (typeof window === 'undefined') return 'personal';
  const mode = String(localStorage.getItem(IPE_WORKSPACE_MODE_KEY) || 'personal').trim().toLowerCase();
  return mode === 'team' ? 'team' : 'personal';
}

/** 短窗缓存：合并频繁 getAccountCenter / getTeamOverview */
const ACCOUNT_CENTER_TTL_MS = 1500;
const TEAM_OVERVIEW_TTL_MS = 1500;
let accountCenterInflight = null;
let accountCenterCache = { at: 0, data: null };
let teamOverviewInflight = null;
let teamOverviewCache = { at: 0, data: null };

export function clearIpeWorkspaceRequestCaches() {
  accountCenterInflight = null;
  accountCenterCache = { at: 0, data: null };
  teamOverviewInflight = null;
  teamOverviewCache = { at: 0, data: null };
}

export function isRequestAborted(error) {
  return Boolean(
    error
    && (error.code === 'ABORT' || error.name === 'AbortError'),
  );
}

function createAbortError(message = '请求已取消') {
  const err = new Error(message);
  err.code = 'ABORT';
  err.name = 'AbortError';
  return err;
}

/** timeoutMs 与外部 AbortSignal 同时生效：用户点终止仍可取消，阻塞同步也不会无限挂起。 */
function createTimeoutSignal(timeoutMs, externalSignal) {
  if (!timeoutMs) {
    return { signal: externalSignal || undefined, cleanup() {} };
  }
  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
  if (!externalSignal) {
    return {
      signal: timeoutController.signal,
      cleanup() { clearTimeout(timer); },
    };
  }
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
    return {
      signal: AbortSignal.any([externalSignal, timeoutController.signal]),
      cleanup() { clearTimeout(timer); },
    };
  }
  const merged = new AbortController();
  const abortMerged = () => {
    if (!merged.signal.aborted) merged.abort();
  };
  if (externalSignal.aborted || timeoutController.signal.aborted) {
    abortMerged();
  } else {
    externalSignal.addEventListener('abort', abortMerged, { once: true });
    timeoutController.signal.addEventListener('abort', abortMerged, { once: true });
  }
  return {
    signal: merged.signal,
    cleanup() {
      clearTimeout(timer);
      externalSignal.removeEventListener('abort', abortMerged);
    },
  };
}

export function setIpeWorkspaceMode(mode) {
  if (typeof window === 'undefined') return getIpeWorkspaceMode();
  const normalized = String(mode || 'personal').trim().toLowerCase() === 'team' ? 'team' : 'personal';
  const prev = getIpeWorkspaceMode();
  localStorage.setItem(IPE_WORKSPACE_MODE_KEY, normalized);
  if (normalized === prev) {
    return normalized;
  }
  clearIpeWorkspaceRequestCaches();
  const detail = { mode: normalized };
  // 历史并存两种事件名：hyphen（auth 等）与 colon（工作台/图片空间 hook）
  window.dispatchEvent(new CustomEvent('ipe-workspace-mode-changed', { detail }));
  window.dispatchEvent(new CustomEvent('ipe:workspace-mode-changed', { detail }));
  return normalized;
}

/** 图片空间 OSS 目录：团队与个人隔离 */
export function getImageSpaceUploadFolder() {
  return getIpeWorkspaceMode() === 'team' ? 'image-space-team' : 'image-space';
}

export const getIpeToken = () => {
  if (ipeTokenMemory) {
    if (isJwtExpiredByDailyMidnight(ipeTokenMemory)) {
      clearIpeTokenStorage();
      return null;
    }
    return ipeTokenMemory;
  }
  let t = localStorage.getItem(IPE_AUTH_TOKEN_KEY);
  if (!t) {
    t = localStorage.getItem(LEGACY_FUNDING_AUTH_TOKEN_KEY);
    if (t) localStorage.setItem(IPE_AUTH_TOKEN_KEY, t);
  }
  const trimmed = normalizeStoredToken(t);
  if (!trimmed) {
    clearIpeTokenStorage();
    return null;
  }
  ipeTokenMemory = trimmed;
  return trimmed;
};
export const getToken = () => {
  const t = localStorage.getItem(AUTH_TOKEN_KEY);
  const trimmed = normalizeStoredToken(t);
  if (t && !trimmed) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
  return trimmed;
};
export const setToken = token => {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token); else localStorage.removeItem(AUTH_TOKEN_KEY);
};
export const setFundingToken = token => setIpeToken(token);
const setIpeToken = token => {
  if (token) {
    const trimmed = String(token).trim();
    ipeTokenMemory = trimmed;
    localStorage.setItem(IPE_AUTH_TOKEN_KEY, trimmed);
    localStorage.removeItem(LEGACY_FUNDING_AUTH_TOKEN_KEY);
  } else {
    ipeTokenMemory = null;
    localStorage.removeItem(IPE_AUTH_TOKEN_KEY);
    localStorage.removeItem(LEGACY_FUNDING_AUTH_TOKEN_KEY);
    resetIpeAuthReady();
  }
};
const toQueryString = (query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params.append(k, String(v));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};
const buildUrl = (path, query) => `${API_BASE_URL}${path}${toQueryString(query)}`;

/** IPE 工作台：有 funding JWT 时优先用于业务 API，避免残留 auth_token 导致整站 401 */
function pickAuthForRequest(tokenType, path) {
  const pathOnly = String(path || '').split('?')[0];
  const isPublicAuthPath =
    pathOnly === '/ipe-users/login'
    || pathOnly === '/ipe-users/register'
    || pathOnly === '/ipe-users/login-by-sms'
    || pathOnly === '/ipe-users/verification-code'
    || pathOnly === '/ipe-users/sms-send-status'
    || pathOnly === '/ipe-users/reset-password'
    || pathOnly === '/ipe-users/wechat/login-session'
    || pathOnly === '/ipe-users/wechat/poll'
    || pathOnly === '/auth/login'
    || pathOnly === '/auth/register'
    || pathOnly === '/auth/login-by-sms'
    || pathOnly === '/auth/verification-code'
    || pathOnly === '/auth/sms-send-status'
    || pathOnly === '/auth/reset-password'
    || pathOnly === '/auth/wechat/login-session'
    || pathOnly === '/auth/wechat/poll'
    || pathOnly.startsWith('/auth/trial-applications');

  const fundingToken = getFundingToken();
  const onIpe =
    typeof window !== 'undefined'
    && (isIpeWorkbenchPath(window.location.pathname) || isIpeLoginPath(window.location.pathname));

  if (tokenType === 'admin') {
    return { token: getAdminTokenFromStorage(), ipeCall: false };
  }

  if (onIpe && fundingToken && !isPublicAuthPath && !pathOnly.startsWith('/admin-auth/')) {
    return { token: fundingToken, ipeCall: true };
  }

  if (tokenType === 'funding') {
    return { token: fundingToken, ipeCall: true };
  }

  const authToken = getToken();
  if (!isPublicAuthPath && fundingToken && !pathOnly.startsWith('/auth/') && !pathOnly.startsWith('/admin-auth/')) {
    if (!authToken) {
      return { token: fundingToken, ipeCall: true };
    }
  }

  return { token: authToken, ipeCall: false };
}

/** 与后端 IpeCreditApiMapping 对齐：可能扣 IPE豆 的写操作 */
function isBillableIpePath(path, method) {
  if (!path || String(method).toUpperCase() !== 'POST') return false;
  const p = String(path).split('?')[0];
  if (p.includes('/batch-delete') || p.includes('/lingtu-title/result') || p.includes('/lingtu-image/result')) {
    return false;
  }
  if (
    p.endsWith('/crop-images/crop')
    || p.endsWith('/crop-images/crop-by-bbox')
    || p.includes('/image-enhance/process')
    || p.includes('/lingtu-title/submit')
    || p.includes('/lingtu-image/cutout/submit')
    || p.includes('/lingtu-image/upscale/submit')
    || p.includes('/lingtu-image/detect/submit')
    || p.includes('/ai-copywriting/submit-and-wait')
    || p.includes('/goods-studio/submit-and-wait')
    || (p.includes('/infringement/batches') && !/\/infringement\/batches\/[^/]+/.test(p))
    || (p.includes('/image-fission/batches') && !/\/image-fission\/batches\/[^/]+$/.test(p))
    || p.includes('/image-fission/process')
    || (p.includes('/admin/image-space/batch') && !p.includes('batch-delete'))
    || p === '/platform/shops'
    || /\/composite-images\/[^/]+\/batch$/.test(p)
    || p === '/listing-templates'
    || p === '/templates'
  ) {
    return true;
  }
  return false;
}
async function request(path, {
  method = 'GET',
  data,
  query,
  tokenType = 'auth',
  signal,
  timeoutMs,
  cache,
  headers: extraHeaders,
  /** 为 true 时 HTTP 304 不抛错，返回 { __notModified, etag } */
  acceptNotModified = false,
} = {}) {
  const isIpeUserLoginPath = path === '/ipe-users/login' || path === '/ipe-users/login-by-sms';
  const pathOnly = String(path || '').split('?')[0];
  const isPublicIpeAuthPath =
    isIpeUserLoginPath
    || pathOnly === '/ipe-users/register'
    || pathOnly === '/ipe-users/verification-code'
    || pathOnly === '/ipe-users/sms-send-status'
    || pathOnly === '/ipe-users/reset-password'
    || pathOnly === '/ipe-users/logout'
    || pathOnly === '/ipe-users/wechat/login-session'
    || pathOnly === '/ipe-users/wechat/poll'
    || pathOnly === '/ipe-users/module-credits';
  if (
    path !== '/ipe-users/me'
    && !isIpeUserLoginPath
    && !isPublicIpeAuthPath
    && tokenType === 'funding'
  ) {
    await waitForIpeAuthReady();
  }

  let { token: tokenAtStart, ipeCall } = pickAuthForRequest(tokenType, path);
  const isIpeUserLogin = ipeCall && isIpeUserLoginPath;
  if (!ipeCall && !tokenAtStart && path === '/auth/me') {
    const err = new Error('未登录');
    err.code = 'LOGIN_REQUIRED';
    err.status = 401;
    throw err;
  }
  if (ipeCall && !isIpeUserLogin && !isPublicIpeAuthPath && !tokenAtStart) {
    const err = new Error('请先登录后再使用此功能');
    err.code = 'LOGIN_REQUIRED';
    err.status = 401;
    throw err;
  }
  const token = tokenAtStart;
  const headers = {};
  if (extraHeaders && typeof extraHeaders === 'object') {
    Object.assign(headers, extraHeaders);
  }
  const hasBody = data !== undefined && data !== null;
  const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
  if ((hasBody && !isFormData) || (String(method).toUpperCase() !== 'GET' && !hasBody)) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers.Authorization = `Bearer ${token}`;
  headers['Accept-Language'] = getIpeUiLocale();
  if (ipeCall && !isPublicIpeAuthPath) {
    headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
  }
  const { signal: fetchSignal, cleanup: cleanupTimeout } = createTimeoutSignal(timeoutMs, signal);
  let response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: hasBody ? (isFormData ? data : JSON.stringify(data)) : undefined,
      signal: fetchSignal,
      ...(cache ? { cache } : {}),
    });
  } catch (fetchError) {
    cleanupTimeout();
    if (fetchError?.name === 'AbortError') {
      // 外部 signal 取消 ≠ 超时；勿一律当成 TIMEOUT
      if (signal?.aborted) {
        throw createAbortError();
      }
      const err = new Error('请求超时，请稍后重试');
      err.code = 'TIMEOUT';
      throw interceptIpeNetworkError(err, { path: pathOnly, method, ipeCall });
    }
    throw interceptIpeNetworkError(fetchError, { path: pathOnly, method, ipeCall });
  }
  cleanupTimeout();
  if (acceptNotModified && response.status === 304) {
    return {
      __notModified: true,
      etag: response.headers.get('ETag') || response.headers.get('etag') || '',
    };
  }
  let payload = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }
  if (!response.ok) {
    var _payload, _payload2, _payload3;
    let message = ((_payload = payload) === null || _payload === void 0 ? void 0 : _payload.message) || ((_payload2 = payload) === null || _payload2 === void 0 ? void 0 : _payload2.error) || defaultMessageForHttpStatus(response.status);
    const code = (_payload3 = payload) === null || _payload3 === void 0 ? void 0 : _payload3.code;
    if ((isIpeLoginAttemptPath(pathOnly) || isIpeUserVerificationCodePath(pathOnly)) && isAccountSuspendedError({
      status: response.status,
      code,
      message,
    })) {
      message = IPE_ACCOUNT_SUSPENDED_MESSAGE;
    } else if ((isIpeLoginAttemptPath(pathOnly) || isIpeUserVerificationCodePath(pathOnly)) && isAccountNotRegisteredLoginError({
      status: response.status,
      code,
      message,
    })) {
      message = IPE_ACCOUNT_NOT_REGISTERED_MESSAGE;
    }
    const isSheinApi = pathOnly === '/shein' || pathOnly.startsWith('/shein/');
    const isTemuApi = pathOnly === '/temu' || pathOnly.startsWith('/temu/');
    const isTiktokApi = pathOnly === '/tiktok' || pathOnly.startsWith('/tiktok/');
    /** Funding 接口使用独立 JWT：401 不应清空主站会话或整页跳转，否则工作台会与 sourcing 等接口形成死循环 */
    if (response.status === 401 && !ipeCall && tokenType !== 'admin' && code === 'OTHER_DEVICE_LOGGED_IN' && typeof otherDeviceLoginHandler === 'function') {
      otherDeviceLoginHandler();
    } else if (response.status === 401 && !ipeCall && tokenType !== 'admin' && typeof authInvalidHandler === 'function') {
      // IPE 工作台使用 funding_auth_token；勿因旧 auth_token 调 /auth/me 401 而整页踢回登录
      const ipeSessionActive =
        typeof window !== 'undefined'
        && getFundingToken()
        && isIpeWorkbenchPath(window.location.pathname);
      if (!ipeSessionActive) {
        authInvalidHandler();
      }
    } else if (
      response.status === 401
      && ipeCall
      && !isIpeUserLogin
      && !isPublicIpeAuthPath
      && path !== '/ipe-users/me'
      && typeof ipeAuthRequiredHandler === 'function'
    ) {
      // SHEIN 业务接口 401：登录已失效，立即退回登录页，避免页面误显示「服务器繁忙」
      // 其它接口仍 debounce 校验，避免登录后并发旧请求误踢
      // 排除 /shein/session/unlock：密码错误即使误返回 401 也只应展示错误，不可踢系统登录
      // TOKEN_REVOKED：改密/注销后旧票立即踢，少一次 debounce
      const decision = resolveIpeAuthRequiredOn401({ pathOnly, code, isSheinApi });
      if (decision.invoke && tokenAtStart && getFundingToken() === tokenAtStart) {
        ipeAuthRequiredHandler({ immediate: decision.immediate });
      }
    } else if (
      ipeCall
      && isSheinApi
      && typeof sheinWorkspaceAuthRequiredHandler === 'function'
      && isSheinWorkspaceExpiredError({ status: response.status, code, message })
      && !isIpeWorkbenchPath(window.location.pathname)
    ) {
      sheinWorkspaceAuthRequiredHandler();
    } else if (
      ipeCall
      && isTemuApi
      && typeof temuWorkspaceAuthRequiredHandler === 'function'
      && isTemuWorkspaceExpiredError({ status: response.status, code, message })
      && !isIpeWorkbenchPath(window.location.pathname)
    ) {
      temuWorkspaceAuthRequiredHandler();
    } else if (
      ipeCall
      && isTiktokApi
      && typeof tiktokWorkspaceAuthRequiredHandler === 'function'
      && isTiktokWorkspaceExpiredError({ status: response.status, code, message })
      && !isIpeWorkbenchPath(window.location.pathname)
    ) {
      tiktokWorkspaceAuthRequiredHandler();
    } else if (ipeCall && !isIpeUserLogin && !isPublicIpeAuthPath && typeof ipeAccountDeactivatedHandler === 'function' && isAccountDeactivatedError({
      status: response.status,
      code,
      message
    })) {
      ipeAccountDeactivatedHandler();
    } else if (ipeCall && !isIpeUserLogin && !isPublicIpeAuthPath && typeof ipeAccountDeactivatedHandler === 'function' && isAccountSuspendedError({
      status: response.status,
      code,
      message
    })) {
      ipeAccountDeactivatedHandler({ reason: 'suspended' });
    } else if (ipeCall && !isIpeUserLogin && typeof ipeCreditsInsufficientHandler === 'function' && (code === 'CREDITS_INSUFFICIENT' || (response.status === 403 && (String(message).includes('IPE豆不足') || String(message).includes('积分不足'))))) {
      ipeCreditsInsufficientHandler({
        message,
        code
      });
    }
    const err = new Error(localizeApiErrorMessage(code, message));
    err.status = response.status;
    err.code = code;
    err.payload = payload;
    err.rawMessage = message;
    const traceId = payload != null && payload.traceId != null ? String(payload.traceId).trim() : '';
    if (traceId) {
      err.traceId = traceId;
    }
    if (payload != null && payload.retryAfterSeconds != null) {
      const retryAfterSeconds = Number(payload.retryAfterSeconds);
      if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
        err.retryAfterSeconds = retryAfterSeconds;
      }
    }
    throw interceptIpeHttpError(err, { path: pathOnly, method, ipeCall });
  }
  if (ipeCall && !isIpeUserLogin && String(method).toUpperCase() !== 'GET' && isBillableIpePath(path, method) && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ipe:credits-consumed'));
  }
  return payload || {};
}
export const authAPI = {
  login: (phone, password, extra = {}) => request('/auth/login', {
    method: 'POST',
    data: {
      phone,
      password,
      ...extra
    }
  }).then(d => {
    if (d !== null && d !== void 0 && d.token) setToken(d.token);
    return d;
  }),
  register: (phone, password, nickname, code) => request('/auth/register', {
    method: 'POST',
    data: { phone, password, nickname, code },
  }).then((d) => {
    if (d?.token) setToken(d.token);
    return d;
  }),
  sendVerificationCode: (phone, scene, captchaToken) => request('/auth/verification-code', {
    method: 'POST',
    data: { phone, scene, ...(captchaToken ? { captchaToken } : {}) },
  }),
  getSmsSendStatus: (phone, scene = 'login') => request('/auth/sms-send-status', {
    query: { phone, scene },
  }),
  loginBySms: (phone, code, loginContext = {}) => request('/auth/login-by-sms', {
    method: 'POST',
    data: { phone, code, ...loginContext },
  }).then((d) => {
    if (d?.token) setToken(d.token);
    return d;
  }),
  resetPassword: (phone, code, newPassword) => request('/auth/reset-password', {
    method: 'POST',
    data: { phone, code, newPassword },
  }),
  getWeChatLoginSession: () => request('/auth/wechat/login-session'),
  pollWeChatLogin: (state) => request('/auth/wechat/poll', { query: { state } }),
  updatePassword: payload => request('/auth/update-password', {
    method: 'PATCH',
    data: payload
  }),
  updatePhone: (newPhone, code) => request('/auth/update-phone', {
    method: 'PATCH',
    data: {
      newPhone,
      code
    }
  }),
  deleteAccount: () => request('/auth/account', {
    method: 'DELETE'
  }),
  getCurrentUser: () => request('/auth/me'),
  logout: () => setToken(null),
  submitTrialApplication: payload => request('/auth/trial-applications', {
    method: 'POST',
    data: payload
  })
};
export const fundingAuthAPI = {
  login: (phone, password, extra = {}) => request('/ipe-users/login', {
    method: 'POST',
    data: typeof extra === 'string'
      ? { phone, password, businessName: extra }
      : { phone, password, ...extra },
    tokenType: 'funding'
  }).then(d => {
    if (d !== null && d !== void 0 && d.token) {
      setFundingToken(d.token);
      setToken(null);
    }
    return d;
  }),
  register: (phone, password, nickname, code, loginContext = {}) => request('/ipe-users/register', {
    method: 'POST',
    data: { phone, password, nickname, code, ...loginContext },
    tokenType: 'funding'
  }).then((d) => {
    if (d?.token) {
      setFundingToken(d.token);
      setToken(null);
    }
    return d;
  }),
  sendVerificationCode: (phone, scene, captchaToken) => request('/ipe-users/verification-code', {
    method: 'POST',
    data: { phone, scene, ...(captchaToken ? { captchaToken } : {}) },
    tokenType: 'funding'
  }),
  getSmsSendStatus: (phone, scene = 'login') => request('/ipe-users/sms-send-status', {
    query: { phone, scene },
    tokenType: 'funding',
  }),
  loginBySms: (phone, code, loginContext = {}, captchaToken) => request('/ipe-users/login-by-sms', {
    method: 'POST',
    data: {
      phone,
      code,
      ...loginContext,
      ...(captchaToken ? { captchaToken } : {}),
    },
    tokenType: 'funding'
  }).then((d) => {
    if (d?.token) {
      setFundingToken(d.token);
      setToken(null);
    }
    return d;
  }),
  resetPassword: (phone, code, newPassword) => request('/ipe-users/reset-password', {
    method: 'POST',
    data: { phone, code, newPassword },
    tokenType: 'funding'
  }),
  recordLoginEvent: (loginContext = {}) => request('/ipe-users/login-event', {
    method: 'POST',
    data: loginContext,
    tokenType: 'funding',
  }),
  reportClientError: (payload = {}) => request('/ipe-users/client-error-log', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  submitFeedback: (payload = {}) => request('/ipe-users/feedback', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  getWeChatLoginSession: () => request('/ipe-users/wechat/login-session', { tokenType: 'funding' }),
  pollWeChatLogin: (state) => request('/ipe-users/wechat/poll', { query: { state }, tokenType: 'funding' }),
  getMe: () => request('/ipe-users/me', {
    tokenType: 'funding'
  }),
  getAccountCenter: (options = {}) => {
    const { signal, bypassCache = false } = options && typeof options === 'object' ? options : {};
    const now = Date.now();
    if (!bypassCache && accountCenterCache.data && now - accountCenterCache.at < ACCOUNT_CENTER_TTL_MS) {
      if (signal?.aborted) return Promise.reject(createAbortError());
      return Promise.resolve(accountCenterCache.data);
    }
    if (!accountCenterInflight) {
      accountCenterInflight = request('/ipe-users/account', {
        tokenType: 'funding',
      }).then((data) => {
        accountCenterCache = { at: Date.now(), data };
        return data;
      }).finally(() => {
        accountCenterInflight = null;
      });
    }
    if (!signal) return accountCenterInflight;
    return new Promise((resolve, reject) => {
      const onAbort = () => reject(createAbortError());
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
      accountCenterInflight.then(
        (data) => {
          signal.removeEventListener('abort', onAbort);
          resolve(data);
        },
        (err) => {
          signal.removeEventListener('abort', onAbort);
          reject(err);
        },
      );
    });
  },
  getModuleCredits: () => request('/ipe-users/module-credits', {
    tokenType: 'funding',
  }),
  updateWorkspaceMode: (workspaceMode, options = {}) => request('/ipe-users/account/workspace-mode', {
    method: 'PATCH',
    data: { workspaceMode },
    tokenType: 'funding',
    signal: options?.signal,
  }).then((data) => {
    const next = data?.workspaceMode || workspaceMode;
    if (next) setIpeWorkspaceMode(next);
    clearIpeWorkspaceRequestCaches();
    return data;
  }),
  getOrderCenter: (query = {}) => request('/ipe-users/account/orders', {
    query,
    tokenType: 'funding'
  }),
  getConsumptionLogs: (query = {}) => request('/ipe-users/account/consumption-logs', {
    query,
    tokenType: 'funding',
  }),
  submitPurchaseIntent: (payload) => request('/ipe-users/account/purchase-intents', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  getLastPurchaseIntentContact: () => request('/ipe-users/account/purchase-intents/last-contact', {
    tokenType: 'funding',
  }),
  updateNickname: nickname => request('/ipe-users/account/nickname', {
    method: 'PATCH',
    data: {
      nickname
    },
    tokenType: 'funding'
  }),
  updateAvatar: avatarUrl => request('/ipe-users/account/avatar', {
    method: 'PATCH',
    data: {
      avatarUrl
    },
    tokenType: 'funding'
  }),
  updatePassword: (oldPassword, newPassword, confirmNewPassword) => request('/ipe-users/account/password', {
    method: 'PATCH',
    data: {
      oldPassword,
      newPassword,
      confirmNewPassword
    },
    tokenType: 'funding'
  }),
  sendAccountChangePasswordCode: () => request('/ipe-users/account/verification-code', {
    method: 'POST',
    data: {},
    tokenType: 'funding'
  }),
  updatePasswordByCode: (code, newPassword, confirmNewPassword) => request('/ipe-users/account/password-by-code', {
    method: 'PATCH',
    data: {
      code,
      newPassword,
      confirmNewPassword
    },
    tokenType: 'funding'
  }),
  deactivateAccount: (password) => request('/ipe-users/account/deactivate', {
    method: 'POST',
    data: password ? { password } : {},
    tokenType: 'funding'
  }),
  getTeamOverview: (options = {}) => {
    const { signal, bypassCache = false } = options && typeof options === 'object' ? options : {};
    const now = Date.now();
    if (!bypassCache && teamOverviewCache.data && now - teamOverviewCache.at < TEAM_OVERVIEW_TTL_MS) {
      if (signal?.aborted) return Promise.reject(createAbortError());
      return Promise.resolve(teamOverviewCache.data);
    }
    if (!teamOverviewInflight) {
      teamOverviewInflight = request('/ipe-users/team', {
        tokenType: 'funding',
      }).then((data) => {
        teamOverviewCache = { at: Date.now(), data };
        return data;
      }).finally(() => {
        teamOverviewInflight = null;
      });
    }
    if (!signal) return teamOverviewInflight;
    return new Promise((resolve, reject) => {
      const onAbort = () => reject(createAbortError());
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
      teamOverviewInflight.then(
        (data) => {
          signal.removeEventListener('abort', onAbort);
          resolve(data);
        },
        (err) => {
          signal.removeEventListener('abort', onAbort);
          reject(err);
        },
      );
    });
  },
  ensureTeamWorkspace: () => request('/ipe-users/team/ensure', {
    method: 'POST',
    tokenType: 'funding',
  }).then((data) => {
    teamOverviewCache = { at: Date.now(), data };
    return data;
  }),
  inviteTeamMember: payload => request('/ipe-users/team/members', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  batchInviteTeamMembers: members => request('/ipe-users/team/members/batch', {
    method: 'POST',
    data: {
      members
    },
    tokenType: 'funding'
  }),
  createTeamInviteLink: (payload = {}) => request('/ipe-users/team/invite-links', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  acceptTeamInvite: token => request('/ipe-users/team/invite-links/accept', {
    method: 'POST',
    data: {
      token
    },
    tokenType: 'funding'
  }),
  joinTeamByInviteCode: inviteCode => request('/ipe-users/team/invite-code/join', {
    method: 'POST',
    data: { inviteCode },
    tokenType: 'funding'
  }),
  getTeamJoinApplications: () => request('/ipe-users/team/join-applications', {
    tokenType: 'funding'
  }),
  approveTeamJoinApplication: applicationId => request(`/ipe-users/team/join-applications/${encodeURIComponent(applicationId)}/approve`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  rejectTeamJoinApplication: applicationId => request(`/ipe-users/team/join-applications/${encodeURIComponent(applicationId)}/reject`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  updateTeamSettings: payload => request('/ipe-users/team/settings', {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  updateTeamMember: (memberUserId, payload) => request(`/ipe-users/team/members/${encodeURIComponent(memberUserId)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  removeTeamMember: memberUserId => request(`/ipe-users/team/members/${encodeURIComponent(memberUserId)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  leaveTeam: () => request('/ipe-users/team/leave', {
    method: 'POST',
    tokenType: 'funding'
  }),
  /** 后端拉黑当前 jti；失败由调用方 finally 清本地 */
  logoutRemote: () => request('/ipe-users/logout', {
    method: 'POST',
    tokenType: 'funding',
  }),
  /** 仅清本地 token，不调后端 */
  logout: () => setFundingToken(null)
};
export const workflowTaskAPI = {
  list: (params = {}) => {
    const { signal, ...query } = params && typeof params === 'object' ? params : {};
    return request('/workflow/tasks', {
      query,
      tokenType: 'funding',
      signal,
      cache: 'no-store',
    });
  },
  getById: id => request(`/workflow/tasks/${encodeURIComponent(id)}`, { tokenType: 'funding' }),
  create: payload => request('/workflow/tasks', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  patch: (id, payload) => request(`/workflow/tasks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  acquireSlot: id => request(`/workflow/tasks/${encodeURIComponent(id)}/acquire-slot`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  releaseSlot: id => request(`/workflow/tasks/${encodeURIComponent(id)}/release-slot`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  heartbeat: () => request('/workflow/tasks/client-presence', {
    method: 'POST',
    tokenType: 'funding',
  }),
  delete: id => request(`/workflow/tasks/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};

/** 自定义工作流模板（非官方）：按当前工作区隔离，团队模式下同 scope 共享 */
export const workflowTemplateAPI = {
  list: (params = {}) => {
    const { signal, ...query } = params && typeof params === 'object' ? params : {};
    return request('/workflow-templates', {
      query,
      tokenType: 'funding',
      signal,
      cache: 'no-store',
    });
  },
  getById: id => request(`/workflow-templates/${encodeURIComponent(id)}`, { tokenType: 'funding' }),
  upsert: (id, payload) => request(`/workflow-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }),
  delete: id => request(`/workflow-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 裁剪 / 超分提质 / 智能抠图批处理：Redis 跨模块并发槽（默认每用户同时 2 个任务） */
export const processingQueueAPI = {
  status: () => request('/processing-queue/status', { tokenType: 'funding' }),
  acquireSlot: ({ taskId, module }) => request('/processing-queue/acquire-slot', {
    method: 'POST',
    data: { taskId, module },
    tokenType: 'funding'
  }),
  releaseSlot: taskId => request('/processing-queue/release-slot', {
    method: 'POST',
    data: { taskId },
    tokenType: 'funding'
  })
};
/** 下载中心：服务端 RabbitMQ 队列打包 */
export const downloadCenterAPI = {
  createTask: (payload) => request('/download-center/tasks', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  listTasks: (params = {}) => request('/download-center/tasks', {
    query: params,
    tokenType: 'funding',
  }),
  getTask: (externalId) => request(`/download-center/tasks/${encodeURIComponent(externalId)}`, {
    tokenType: 'funding',
  }),
  cancelTask: (externalId) => request(`/download-center/tasks/${encodeURIComponent(externalId)}/cancel`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  retryTask: (externalId) => request(`/download-center/tasks/${encodeURIComponent(externalId)}/retry`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  /** T-B03：仅失败卷重试 */
  retryPart: (externalId, partIndex) => request(
    `/download-center/tasks/${encodeURIComponent(externalId)}/parts/${encodeURIComponent(partIndex)}/retry`,
    {
      method: 'POST',
      tokenType: 'funding',
    },
  ),
  markDownloaded: (externalId) => request(`/download-center/tasks/${encodeURIComponent(externalId)}/downloaded`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  /** 解析下载入口：OSS 直链 mode=direct；local ticket 短链 mode=ticket */
  getFileUrl: (externalId, params = {}) => request(`/download-center/tasks/${encodeURIComponent(externalId)}/file-url`, {
    query: params,
    tokenType: 'funding',
  }),
  downloadFileUrl: (externalId, params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v != null && String(v).trim() !== '') qs.set(k, String(v));
    });
    const q = qs.toString();
    return `${API_BASE_URL}/download-center/tasks/${encodeURIComponent(externalId)}/file${q ? `?${q}` : ''}`;
  },
};
/** /tasks/progress 分块缓存：etag + 上次 tasks + 单任务指纹（known 增量） */
const progressChunkCache = new Map();

function progressTaskFingerprint(task) {
  if (!task || typeof task !== 'object') return '';
  return [
    task.id,
    task.status,
    task.total,
    task.successCount,
    task.failedCount,
    task.phase,
    task.message,
    task.updatedAt,
  ].map((v) => (v == null ? '' : String(v).trim())).join('|');
}

export const taskAPI = {
  getAll: (params = {}) => request('/tasks', {
    query: params,
    tokenType: 'funding'
  }),
  getById: id => request(`/tasks/${encodeURIComponent(id)}`, {
    tokenType: 'funding'
  }),
  /** 批量查询任务进度 DTO（与 SSE progress 同契约；单次最多 50 个 id） */
  batchProgress: ids => request('/tasks/progress', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  /**
   * 分批拉取多任务进度 DTO（按 50 切块；支持 ETag/304 与 known 增量）。
   * 304 时回放该 chunk 上次结果，避免工作流 wait 误回退 getById。
   */
  fetchProgressBatch: async (ids) => {
    const unique = [...new Set((ids || []).filter(Boolean).map(String))];
    if (unique.length === 0) return [];
    const all = [];
    const chunkSize = 50;
    for (let i = 0; i < unique.length; i += chunkSize) {
      const chunk = unique.slice(i, i + chunkSize);
      const cacheKey = chunk.slice().sort().join('\0');
      const cached = progressChunkCache.get(cacheKey);
      try {
        const headers = {};
        if (cached?.etag) {
          headers['If-None-Match'] = cached.etag;
        }
        const data = { ids: chunk };
        if (cached?.known && Object.keys(cached.known).length > 0) {
          data.known = cached.known;
        }
        if (cached?.etag) {
          data.etag = cached.etag;
        }
        const res = await request('/tasks/progress', {
          method: 'POST',
          data,
          tokenType: 'funding',
          headers,
          acceptNotModified: true,
        });
        if (res?.__notModified) {
          const prev = Array.isArray(cached?.tasks) ? cached.tasks : [];
          if (res.etag && cached) {
            progressChunkCache.set(cacheKey, { ...cached, etag: res.etag || cached.etag });
          }
          all.push(...prev);
          continue;
        }
        const changed = Array.isArray(res?.tasks) ? res.tasks : [];
        const unchangedIds = Array.isArray(res?.unchangedIds) ? res.unchangedIds : [];
        const byId = new Map((cached?.tasks || []).map((t) => [String(t.id), t]));
        changed.forEach((t) => {
          if (t?.id != null) byId.set(String(t.id), t);
        });
        // 增量：合并变更 + 未变更；全量：直接用返回列表
        const merged = unchangedIds.length > 0 || (cached?.known && changed.length < chunk.length)
          ? chunk.map((id) => byId.get(String(id))).filter(Boolean)
          : changed;
        const etag = res?.etag || '';
        const known = {};
        merged.forEach((t) => {
          if (!t?.id) return;
          known[String(t.id)] = progressTaskFingerprint(t);
        });
        progressChunkCache.set(cacheKey, { etag, tasks: merged, known });
        all.push(...merged);
      } catch {
        /* try next chunk */
      }
    }
    return all;
  },

  create: (payload) => {
    const data = payload?.result
      ? { ...payload, result: sanitizeFundingTaskResultForApi(payload.result) }
      : payload;
    return request('/tasks', {
      method: 'POST',
      data,
      tokenType: 'funding',
    });
  },
  update: (id, payload) => {
    const data = payload?.result
      ? { ...payload, result: sanitizeFundingTaskResultForApi(payload.result) }
      : payload;
    return request(`/tasks/${encodeURIComponent(id)}`, {
      method: 'PUT',
      data,
      tokenType: 'funding',
    });
  },
  /** 提交批处理到服务端 Worker（超分提质 / 智能抠图 / 图片裁剪 / 万能改图） */
  submit: (id, { force = false } = {}) => request(`/tasks/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
    data: force ? { force: true } : undefined,
    tokenType: 'funding'
  }),
  /** 终止服务端批处理 Worker（图片裁剪 / 超分提质 / 智能抠图） */
  cancelBatch: id => request(`/tasks/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  /** 取消工作台视频生成轮询 */
  cancelVideo: id => request(`/tasks/${encodeURIComponent(id)}/cancel-video`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  /** 重试工作台视频生成（清空 remoteId / 终态 phase 后重新提交） */
  restartVideo: id => request(`/tasks/${encodeURIComponent(id)}/restart-video`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  delete: id => request(`/tasks/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};

/** 找图选品：插件抓取批次、候选打分、待上品流转 */
export const sourcingAPI = {
  ingestBatch: payload => request('/sourcing/capture/batches', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  listBatches: (params = {}) => request('/sourcing/capture/batches', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      batchKey: params.batchKey,
      pageUrl: params.pageUrl,
      createdFrom: params.createdFrom,
      createdTo: params.createdTo,
      phase: params.phase,
      ownerUserId: params.ownerUserId,
    },
    tokenType: 'funding'
  }),
  getBatch: batchKey => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}`, {
    tokenType: 'funding'
  }),
  listItems: (params = {}) => request('/sourcing/capture/items', {
    query: params,
    tokenType: 'funding'
  }),
  rescoreBatch: batchKey => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/rescore`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  autoShortlist: (batchKey, query = {}) => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/auto-shortlist`, {
    method: 'POST',
    query,
    tokenType: 'funding'
  }),
  analyzeBatch: (batchKey, query = {}, ids = null) => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/analyze`, {
    method: 'POST',
    query,
    data: Array.isArray(ids) && ids.length > 0 ? { ids } : undefined,
    tokenType: 'funding'
  }),
  promoteItems: (ids, options = {}) => request('/sourcing/capture/items/promote', {
    method: 'POST',
    data: {
      ids,
      ...(options.bindListingAutoRule
        ? {
          bindListingAutoRule: true,
          listingAutoRuleId: options.listingAutoRuleId ?? null,
        }
        : {}),
    },
    tokenType: 'funding'
  }),
  bindListingAutoRule: (batchKey, listingAutoRuleId) => request(
    `/sourcing/capture/batches/${encodeURIComponent(batchKey)}/listing-auto-rule`,
    {
      method: 'POST',
      data: { listingAutoRuleId: listingAutoRuleId ?? null },
      tokenType: 'funding',
    },
  ),
  dismissItems: ids => request('/sourcing/capture/items/dismiss', {
    method: 'POST',
    data: {
      ids
    },
    tokenType: 'funding'
  }),
  shortlistItems: ids => request('/sourcing/capture/items/shortlist', {
    method: 'POST',
    data: {
      ids
    },
    tokenType: 'funding'
  }),
  deleteBatches: batchKeys => request('/sourcing/capture/batches/delete', {
    method: 'POST',
    data: {
      batchKeys
    },
    tokenType: 'funding'
  }),
  terminateCaptureBatch: batchKey => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/terminate-capture`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  cancelListingAutoRule: batchKey => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/cancel-auto-rule`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  retryListingAutoRule: batchKey => request(`/sourcing/capture/batches/${encodeURIComponent(batchKey)}/retry-auto-rule`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  seedDemoCollect: () => request('/sourcing/capture/batches/demo-seed', {
    method: 'POST',
    tokenType: 'funding'
  }),
};

/** 电商平台：1688 OAuth 店铺授权、上架任务 */
export const platformAPI = {
  getIntegrationConfig: () => request('/platform/integration-config', {
    tokenType: 'funding'
  }),
  listShops: (options = {}) => request('/platform/shops', {
    tokenType: 'funding',
    signal: options?.signal,
    query: options?.shopType ? { shopType: options.shopType } : undefined,
  }),
  createShop: payload => request('/platform/shops', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  patchShop: (id, payload) => request(`/platform/shops/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  getTemuTokens: (id) => request(`/platform/shops/${encodeURIComponent(id)}/temu-tokens`, {
    tokenType: 'funding'
  }),
  mockAuthorizeShop: id => request(`/platform/shops/${encodeURIComponent(id)}/mock-authorize`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  ensureTemuDesignShops: () => request('/platform/shops/ensure-temu-design-shops', {
    method: 'POST',
    tokenType: 'funding'
  }),
  reauthorizeShop: id => request(`/platform/shops/${encodeURIComponent(id)}/reauthorize`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  syncTiktokShopProfile: id => request(`/platform/shops/${encodeURIComponent(id)}/tiktok-shop-profile/sync`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  /** TEMU 授权后同步店铺名 / 过期时间等基础信息（店铺管理页动画等待） */
  syncTemuShopProfile: id => request(`/platform/shops/${encodeURIComponent(id)}/temu-shop-profile/sync`, {
    method: 'POST',
    tokenType: 'funding',
    timeoutMs: 120000,
  }),
  unbindShop: id => request(`/platform/shops/${encodeURIComponent(id)}/unbind`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  deleteShop: id => request(`/platform/shops/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  batchUnbindShops: (ids = []) => request('/platform/shops/batch-unbind', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  batchDeleteShops: (ids = []) => request('/platform/shops/batch-delete', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  /** 店铺回收站：已软删、可恢复或彻底删除的店铺 */
  listShopRecycleBin: (options = {}) => request('/platform/shops/recycle-bin', {
    tokenType: 'funding',
    signal: options?.signal,
  }),
  restoreShop: id => request(`/platform/shops/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  permanentDeleteShop: id => request(`/platform/shops/${encodeURIComponent(id)}/permanent`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  batchPermanentDeleteShops: (ids = []) => request('/platform/shops/batch-permanent-delete', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  batchRestoreShops: (ids = []) => request('/platform/shops/batch-restore', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  batchMoveShopsToTeam: (ids = []) => request('/platform/shops/batch-move-to-team', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding'
  }),
  batchAssignShopsGroup: (ids = [], shopGroupId = null) => request('/platform/shops/batch-assign-group', {
    method: 'POST',
    data: {
      ids,
      shopGroupId: shopGroupId == null || shopGroupId === '' ? null : Number(shopGroupId),
    },
    tokenType: 'funding'
  }),
  previewSheinTestKeys: (id, payload) => request(`/platform/shops/${encodeURIComponent(id)}/preview-shein-test-keys`, {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  bindSheinTestKeys: (id, payload) => request(`/platform/shops/${encodeURIComponent(id)}/bind-shein-test-keys`, {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  getOAuthUrl: (id, options = {}) => request(`/platform/shops/${encodeURIComponent(id)}/oauth-url`, {
    tokenType: 'funding',
    query: {
      ...(options?.tiktokApp ? { tiktokApp: options.tiktokApp } : {}),
      ...(options?.tiktokMarket ? { tiktokMarket: options.tiktokMarket } : {}),
      ...(options?.temuSlot ? { temuSlot: options.temuSlot } : {}),
    },
  }).then(d => {
    var _d$url;
    return (_d$url = d === null || d === void 0 ? void 0 : d.url) !== null && _d$url !== void 0 ? _d$url : '';
  }),
  armTiktokTestOAuth: (id) => request(`/platform/shops/${encodeURIComponent(id)}/tiktok-test-oauth/arm`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  exchangeTiktokAuthCode: (id, payload) => {
    const hasState = Boolean(payload?.state);
    const path = hasState || id == null || id === 0
      ? '/platform/tiktok/oauth/complete'
      : `/platform/shops/${encodeURIComponent(id)}/tiktok/exchange-auth-code`;
    return request(path, {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
    });
  },
  createTiktokShopWithAuthCode: (payload) => request('/platform/shops/tiktok/create-with-auth-code', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  listOperators: () => request('/platform/operators', {
    tokenType: 'funding'
  }),
  listShopGroups: () => request('/platform/shop-groups', {
    tokenType: 'funding'
  }),
  createShopGroup: payload => request('/platform/shop-groups', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  patchShopGroup: (id, payload) => request(`/platform/shop-groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  deleteShopGroup: id => request(`/platform/shop-groups/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  assignOperator: (shopId, operatorId) => request(`/platform/shops/${encodeURIComponent(shopId)}/assign-operator`, {
    method: 'POST',
    data: {
      operatorId: operatorId === '' || operatorId === undefined ? null : Number(operatorId)
    },
    tokenType: 'funding'
  }),
  createListingJob: payload => request('/platform/listing-jobs', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  /** 本地商品库手动新增：本地视频上传（OSS + 视频空间，不依赖店铺） */
  uploadManualListingVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/platform/listing-manual-video', {
      method: 'POST',
      data: formData,
      tokenType: 'funding',
      timeoutMs: 180000,
    });
  },
  /** TEMU：先创建刊登任务，状态为 preparing，转换完成后再入队 */
  createPreparingListingJob: payload => request('/platform/listing-jobs/preparing', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  updateListingJobPrepare: (id, payload) => request(
    `/platform/listing-jobs/${encodeURIComponent(id)}/prepare`,
    {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
    },
  ),
  /** 商品库刊登：店铺发品额度；syncIfStale 时过期会先同步店铺资料 */
  getShopPublishQuota: (shopId, options = {}) => request(
    `/platform/shops/${encodeURIComponent(shopId)}/publish-quota`,
    {
      tokenType: 'funding',
      query: options?.syncIfStale ? { syncIfStale: true } : undefined,
      signal: options?.signal,
    },
  ),
  assignSourcingItemsShop: (sourcingItemIds, shopId) => request('/platform/sourcing-items/assign-shop', {
    method: 'POST',
    data: {
      sourcingItemIds: Array.isArray(sourcingItemIds) ? sourcingItemIds : [],
      shopId: shopId === '' || shopId === undefined || shopId === null ? null : Number(shopId),
    },
    tokenType: 'funding'
  }),
  listListingJobs: (params = {}) => request('/platform/listing-jobs', {
    query: params,
    tokenType: 'funding'
  }),
  getListingJob: id => request(`/platform/listing-jobs/${encodeURIComponent(id)}`, {
    tokenType: 'funding'
  }),
  retryListingJob: id => request(`/platform/listing-jobs/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  /** 复色任务重新刊登：按复色链路重新组包后写回编辑体并入队 */
  retryRecolorListingJob: (id, body = {}) => request(
    `/platform/listing-jobs/${encodeURIComponent(id)}/recolor-retry`,
    {
      method: 'POST',
      data: body,
      tokenType: 'funding',
    },
  ),
  updateListingJobLineTitle: (jobId, lineId, title) => request(
    `/platform/listing-jobs/${encodeURIComponent(jobId)}/lines/${encodeURIComponent(lineId)}/title`,
    {
      method: 'PATCH',
      data: { title },
      tokenType: 'funding',
    },
  ),
  cancelListingJob: id => request(`/platform/listing-jobs/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  deleteListingJob: id => request(`/platform/listing-jobs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  listScheduledListings: (params = {}) => request('/platform/scheduled-listings', {
    query: params,
    tokenType: 'funding',
  }),
  getScheduledListing: id => request(`/platform/scheduled-listings/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }),
  cancelScheduledListing: id => request(`/platform/scheduled-listings/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  runScheduledListingNow: id => request(`/platform/scheduled-listings/${encodeURIComponent(id)}/run-now`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  rescheduleScheduledListing: (id, scheduledAt) => request(
    `/platform/scheduled-listings/${encodeURIComponent(id)}/reschedule`,
    {
      method: 'POST',
      body: { scheduledAt },
      tokenType: 'funding',
    },
  ),
  updateScheduledListing: (id, body = {}) => request(
    `/platform/scheduled-listings/${encodeURIComponent(id)}/update`,
    {
      method: 'POST',
      body,
      tokenType: 'funding',
    },
  ),
  deleteScheduledListing: id => request(`/platform/scheduled-listings/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
  listProducts: (params = {}) => request('/platform/products', {
    query: params,
    tokenType: 'funding'
  }),
  syncShopProducts: shopId => request(`/platform/shops/${encodeURIComponent(shopId)}/products/sync`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  syncProductPrice: productId => request(`/platform/products/${encodeURIComponent(productId)}/sync-price`, {
    method: 'POST',
    tokenType: 'funding'
  }),
};

const SHEIN_WORKSPACE_TOKEN_KEY = 'ipe_shein_workspace_token';

/**
 * SHEIN 工作区二次验证 token。
 * 使用 localStorage，便于同浏览器新标签复用（sessionStorage 不跨标签）。
 */
export function getSheinWorkspaceToken() {
  try {
    if (typeof localStorage !== 'undefined') {
      const fromLocal = localStorage.getItem(SHEIN_WORKSPACE_TOKEN_KEY);
      if (fromLocal) return fromLocal;
    }
    // 兼容旧版：从 sessionStorage 迁移一次
    if (typeof sessionStorage !== 'undefined') {
      const fromSession = sessionStorage.getItem(SHEIN_WORKSPACE_TOKEN_KEY);
      if (fromSession) {
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(SHEIN_WORKSPACE_TOKEN_KEY, fromSession);
          }
          sessionStorage.removeItem(SHEIN_WORKSPACE_TOKEN_KEY);
        } catch {
          /* ignore */
        }
        return fromSession;
      }
    }
  } catch {
    return null;
  }
  return null;
}

const TEMU_WORKSPACE_TOKEN_KEY = 'ipe_temu_workspace_token';
const TIKTOK_WORKSPACE_TOKEN_KEY = 'ipe_tiktok_workspace_token';

export function getTemuWorkspaceToken() {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(TEMU_WORKSPACE_TOKEN_KEY);
    }
  } catch {
    return null;
  }
  return null;
}

export function persistTemuWorkspaceToken(token) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(TEMU_WORKSPACE_TOKEN_KEY, token);
    else localStorage.removeItem(TEMU_WORKSPACE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function temuRequest(path, options = {}) {
  const token = getTemuWorkspaceToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['X-Temu-Workspace-Token'] = token;
  if (isTemuManualSyncWrite(path, options.method)) {
    const clickId = currentTemuManualSyncClickId();
    if (clickId && !headers[TEMU_MANUAL_SYNC_CLICK_HEADER]) {
      headers[TEMU_MANUAL_SYNC_CLICK_HEADER] = clickId;
    }
    const kind = currentTemuManualSyncKind() || resolveTemuManualSyncQuotaKindFromPath(path);
    if (kind && !headers[TEMU_MANUAL_SYNC_KIND_HEADER]) {
      headers[TEMU_MANUAL_SYNC_KIND_HEADER] = kind;
    }
  }
  return request(`/temu${path}`, {
    ...options,
    headers,
    tokenType: 'funding',
  }).then((result) => {
    notifyTemuManualSyncQuota(path, options.method, true);
    return result;
  }).catch((err) => {
    notifyTemuManualSyncQuota(path, options.method, false, err);
    throw err;
  });
}

function isTemuManualSyncWrite(path, method) {
  if (String(method || 'GET').toUpperCase() !== 'POST') return false;
  const p = String(path || '');
  if (p.includes('/cancel')) return false;
  if (/\/shops\/\d+\/module-sync-jobs$/.test(p)) return true;
  return /\/shops\/\d+\/.+(?:\/sync|\/sync-jobs|\/sync-job)$/.test(p);
}

function notifyTemuManualSyncQuota(path, method, ok, err) {
  if (typeof window === 'undefined' || !isTemuManualSyncWrite(path, method)) return;
  if (ok || Number(err?.status) === 429 || err?.code === 'TEMU_MANUAL_SYNC_LIMIT') {
    window.dispatchEvent(new CustomEvent('ipe:temu-manual-sync-quota', {
      detail: {
        kind: currentTemuManualSyncKind() || resolveTemuManualSyncQuotaKindFromPath(path),
        shopId: parseTemuShopIdFromPath(path),
      },
    }));
  }
}

export function getTiktokWorkspaceToken() {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(TIKTOK_WORKSPACE_TOKEN_KEY);
    }
  } catch {
    return null;
  }
  return null;
}

export function persistTiktokWorkspaceToken(token) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (token) localStorage.setItem(TIKTOK_WORKSPACE_TOKEN_KEY, token);
    else localStorage.removeItem(TIKTOK_WORKSPACE_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function tiktokRequest(path, options = {}) {
  const token = getTiktokWorkspaceToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['X-Tiktok-Workspace-Token'] = token;
  return request(`/tiktok${path}`, { ...options, headers, tokenType: 'funding' });
}

function requireTemuShopId(shopId, action) {
  const id = String(shopId ?? '').trim();
  if (!id) {
    throw new Error(`${action || '操作'}需要先选择 TEMU 店铺`);
  }
  return id;
}

/**
 * 拉取 bg.glo.product.search：
 * 1) 优先用发布记录里的 productSkuId
 * 2) 没有 SKU 但有 SPU 时，先从本地货品缓存解析 SKU
 * 3) 仍没有则按 SPU/SKC 分页拉生命周期并本地过滤（不传无关货品）
 * 无发布记录 → 不打平台
 */
async function fetchTemuPublishLifecycleItems(shopId) {
  const id = requireTemuShopId(shopId, '查询货品生命周期');
  const queryIds = collectPublishRecordLifecycleQueryIds(id);
  if (!queryIds.recordCount) {
    return { items: [], skipped: true, reason: 'empty-records' };
  }

  let skuIds = [...queryIds.productSkuIds];
  // 有 SPU 无 SKU：从本地货品 SKU 缓存补齐
  if (!skuIds.length && queryIds.productIds.length) {
    const resolved = new Set();
    for (const productId of queryIds.productIds.slice(0, 30)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await temuRequest(
          `/shops/${encodeURIComponent(id)}/products/${encodeURIComponent(productId)}/skus`,
        );
        const rows = Array.isArray(res?.items) ? res.items : [];
        rows.forEach((row) => {
          const n = Number(row?.platformSkuId ?? row?.productSkuId ?? row?.skuId);
          if (Number.isSafeInteger(n) && n > 0) resolved.add(n);
        });
      } catch {
        /* 单品失败继续 */
      }
    }
    skuIds = [...resolved].slice(0, 100);
  }

  const productSet = new Set(queryIds.productIds.map(String));
  const skcSet = new Set(queryIds.skcIds.map(String));
  const skuSet = new Set(skuIds.map(String));

  const filterLifecycleRows = (rawItems) => (Array.isArray(rawItems) ? rawItems : []).filter((row) => {
    if (!row || typeof row !== 'object') return false;
    const productId = row.productId != null ? String(row.productId) : '';
    if (productId && productSet.has(productId)) return true;
    const skcList = Array.isArray(row.skcList) ? row.skcList : [];
    if (skcList.some((s) => s?.skcId != null && skcSet.has(String(s.skcId)))) return true;
    const skuList = Array.isArray(row.skuList) ? row.skuList : [];
    if (skuList.some((s) => s?.skuId != null && skuSet.has(String(s.skuId)))) return true;
    return false;
  });

  const postLifecycle = async (productSkuIdList, pageNum, pageSize) => {
    const life = await temuRequest(
      `/shops/${encodeURIComponent(id)}/publish-records/lifecycle/sync`,
      {
        method: 'POST',
        data: {
          productSkuIdList: productSkuIdList || [],
          pageNum,
          pageSize,
        },
      },
    );
    return Array.isArray(life?.items)
      ? life.items
      : (Array.isArray(life?.dataList) ? life.dataList : []);
  };

  if (skuIds.length) {
    const rawItems = await postLifecycle(skuIds, 1, Math.min(100, Math.max(1, skuIds.length)));
    return {
      items: filterLifecycleRows(rawItems),
      skipped: false,
      queriedSkuCount: skuIds.length,
      queryMode: 'sku',
    };
  }

  // 仅有 SPU/SKC：不传 productSkuIdList，分页拉并按本店记录过滤
  if (!productSet.size && !skcSet.size) {
    return { items: [], skipped: true, reason: 'empty-ids' };
  }
  const collected = [];
  const seen = new Set();
  for (let page = 1; page <= 5; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const pageItems = await postLifecycle([], page, 100);
    if (!pageItems.length) break;
    filterLifecycleRows(pageItems).forEach((row) => {
      const key = String(row?.productId ?? '');
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      collected.push(row);
    });
    if (collected.length >= productSet.size && productSet.size > 0) break;
    if (pageItems.length < 100) break;
  }
  return {
    items: collected,
    skipped: false,
    queriedSkuCount: 0,
    queryMode: 'spu-skc',
    productCount: productSet.size,
    skcCount: skcSet.size,
  };
}

/** 半托售后动作：后端 ActionBody = { gatewayRegion, params } */
function temuSemiAftersalesAction(shopId, actionPath, data = {}) {
  const body = data && typeof data === 'object' ? data : {};
  const { gatewayRegion, params: nestedParams, ...rest } = body;
  return temuRequest(
    `/shops/${encodeURIComponent(shopId)}/semi-aftersales/actions/${encodeURIComponent(actionPath)}`,
    {
      method: 'POST',
      data: {
        gatewayRegion,
        params: nestedParams && typeof nestedParams === 'object' ? { ...rest, ...nestedParams } : rest,
      },
    },
  );
}

function requireTiktokShopId(shopId, action) {
  const id = String(shopId ?? '').trim();
  if (!id) {
    throw new Error(`${action || '操作'}需要先选择 TikTok 店铺`);
  }
  return id;
}

/** 拉取 Temu 最近一次同步收录的平台 JSON */
async function fetchTemuSyncPlatformDump(shopId, kind) {
  const id = requireTemuShopId(shopId, '读取平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  await waitForIpeAuthReady();
  const path = `/temu/shops/${encodeURIComponent(id)}/sync-platform-dump?kind=${encodeURIComponent(dumpKind)}`;
  const { token, ipeCall } = pickAuthForRequest('funding', path);
  if (!token) {
    throw new Error('请先登录后再读取');
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (ipeCall) {
    headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
  }
  const workspaceToken = getTemuWorkspaceToken();
  if (workspaceToken) {
    headers['X-Temu-Workspace-Token'] = workspaceToken;
  }
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    headers,
    cache: 'no-store',
  });
  if (!response.ok) {
    let msg = '读取平台同步 JSON 失败';
    try {
      const errBody = await response.json();
      if (errBody?.message) msg = String(errBody.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { _raw: text };
  }
}

async function downloadTemuSyncPlatformDump(shopId, kind) {
  const id = requireTemuShopId(shopId, '下载平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  const data = await fetchTemuSyncPlatformDump(id, dumpKind);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `temu-sync-${dumpKind}-shop-${id}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

async function fetchTiktokSyncPlatformDump(shopId, kind) {
  const id = requireTiktokShopId(shopId, '读取平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  await waitForIpeAuthReady();
  const path = `/tiktok/shops/${encodeURIComponent(id)}/sync-platform-dump?kind=${encodeURIComponent(dumpKind)}`;
  const { token, ipeCall } = pickAuthForRequest('funding', path);
  if (!token) {
    throw new Error('请先登录后再读取');
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (ipeCall) {
    headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
  }
  const workspaceToken = getTiktokWorkspaceToken();
  if (workspaceToken) {
    headers['X-Tiktok-Workspace-Token'] = workspaceToken;
  }
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    headers,
    cache: 'no-store',
  });
  if (!response.ok) {
    let msg = '读取平台同步 JSON 失败';
    try {
      const errBody = await response.json();
      if (errBody?.message) msg = String(errBody.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { _raw: text };
  }
}

async function downloadTiktokSyncPlatformDump(shopId, kind) {
  const id = requireTiktokShopId(shopId, '下载平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  const data = await fetchTiktokSyncPlatformDump(id, dumpKind);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tiktok-sync-${dumpKind}-shop-${id}.json`;
  a.click();
  URL.revokeObjectURL(url);
}


/** TTS epoch（秒）→ ISO；已是字符串则原样返回。 */
function tiktokEpochToIso(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string' && !/^\d+$/.test(raw.trim())) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '';
  const ms = n < 1e12 ? n * 1000 : n;
  try {
    return new Date(ms).toISOString();
  } catch {
    return '';
  }
}

/** 将 TTS Get Order / 本地缓存 payload 投影为订单列表/详情可读字段。 */
function normalizeTiktokOrderDetail(order, fallbackNo = '') {
  if (!order || typeof order !== 'object') return null;
  const orderId = String(
    order.id || order.order_id || order.orderNo || order.orderId || fallbackNo || '',
  ).trim();
  if (!orderId) return null;
  const payment = order.payment && typeof order.payment === 'object' ? order.payment : {};
  const addr = order.recipient_address && typeof order.recipient_address === 'object'
    ? order.recipient_address
    : (order.recipientAddress && typeof order.recipientAddress === 'object' ? order.recipientAddress : {});
  const status = order.status || order.order_status || order.orderStatus || '';
  const currency = payment.currency || order.currency || order.saleCurrency || order.orderCurrency || '';
  const amount = payment.total_amount || payment.amount || order.orderAmount || order.amount || '';

  let orderGoodsInfoList = Array.isArray(order.orderGoodsInfoList) ? order.orderGoodsInfoList : null;
  if (!orderGoodsInfoList) {
    const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
    orderGoodsInfoList = lineItems.map((li) => ({
      goodsTitle: li?.product_name || li?.sku_name || li?.goodsTitle || '',
      productId: li?.product_id || li?.goodsId || '',
      skuId: li?.sku_id || li?.skuCode || '',
      skuCode: li?.sku_id || li?.skuCode || '',
      skuName: li?.sku_name || '',
      sellerSku: li?.seller_sku || li?.sellerSku || '',
      merchantSku: li?.seller_sku || li?.sellerSku || '',
      imageUrl: li?.sku_image || li?.imageUrl || '',
      spuPicURL: li?.sku_image || li?.imageUrl || '',
      goodsId: li?.id || li?.product_id || li?.goodsId || '',
      salePrice: li?.sale_price || '',
      originalPrice: li?.original_price || '',
      currency: li?.currency || currency,
      platformDiscount: li?.platform_discount || '',
      sellerDiscount: li?.seller_discount || '',
      displayStatus: li?.display_status || '',
      packageId: li?.package_id || '',
      trackingNumber: li?.tracking_number || '',
      shippingProviderName: li?.shipping_provider_name || '',
      cancelReason: li?.cancel_reason || '',
    }));
  }

  let packageWaybillList = Array.isArray(order.packageWaybillList) ? order.packageWaybillList : null;
  if (!packageWaybillList) {
    const packages = Array.isArray(order.packages) ? order.packages : [];
    packageWaybillList = packages.map((p) => ({
      packageNo: p?.id || p?.package_id || p?.packageNo || '',
      waybillNo: p?.tracking_number || p?.waybillNo || '',
      carrier: p?.shipping_provider_name || p?.carrier_name || p?.carrier || '',
    }));
  }

  return {
    ...order,
    id: orderId,
    orderNo: orderId,
    orderId,
    status,
    orderStatus: status,
    currency,
    saleCurrency: currency,
    orderCurrency: currency,
    amount,
    orderAmount: amount,
    estimatedGrossIncome: amount,
    orderTime: order.orderTime || tiktokEpochToIso(order.create_time) || '',
    paymentTime: order.paymentTime || tiktokEpochToIso(payment.paid_time || order.paid_time) || '',
    orderMsgUpdateTime: order.orderMsgUpdateTime || tiktokEpochToIso(order.update_time) || '',
    orderGoodsInfoList,
    packageWaybillList,
    receiveMsg: order.receiveMsg && typeof order.receiveMsg === 'object'
      ? order.receiveMsg
      : {
        country: addr.region || addr.region_code || addr.country || '',
        province: addr.state || addr.province || '',
        city: addr.city || '',
        postCode: addr.postal_code || addr.postCode || '',
      },
  };
}

/** 适配 Get Logistics Tracking → 列表/详情轨迹面板（trackInfo / ipeLogisticsTracks）。 */
function adaptTiktokLogisticsTracking(raw, orderNo) {
  const data = raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data : raw;
  const root = data && typeof data === 'object' ? data : {};
  const details = Array.isArray(root.logistics_details)
    ? root.logistics_details
    : (Array.isArray(root.tracking)
      ? root.tracking
      : (Array.isArray(root.packages)
        ? root.packages
        : (Array.isArray(root.package_list) ? root.package_list : [])));

  const trackInfo = details.map((d) => {
    const events = Array.isArray(d?.track_list)
      ? d.track_list
      : (Array.isArray(d?.tracking_details)
        ? d.tracking_details
        : (Array.isArray(d?.details) ? d.details : []));
    const waybillNo = d?.newest_tracking_no || d?.tracking_number || d?.tracking_no || d?.waybillNo || '';
    const carrier = d?.carrier_name || d?.shipping_provider_name || d?.carrier || '';
    return {
      waybillNo,
      carrier,
      waybillType: d?.shipping_type || d?.waybillType || '',
      packageNo: d?.package_id || d?.id || d?.packageNo || '',
      tracking: events.map((ev) => {
        const millisRaw = ev?.update_time_millis ?? ev?.update_time ?? ev?.time;
        let updateTimeMillis = Number(millisRaw);
        if (Number.isFinite(updateTimeMillis) && updateTimeMillis > 0 && updateTimeMillis < 1e12) {
          updateTimeMillis *= 1000;
        }
        return {
          description: ev?.description || ev?.detail_description || '',
          trackingNo: ev?.tracking_no || waybillNo || '',
          updateTimeMillis: Number.isFinite(updateTimeMillis) ? updateTimeMillis : 0,
          nodeCode: ev?.action_code || ev?.code || '',
          nodeCodeName: ev?.action_code_name || ev?.title || ev?.description || '节点',
        };
      }),
    };
  }).filter((t) => t.waybillNo || (t.tracking && t.tracking.length));

  return {
    ...root,
    orderNo: root.order_id || root.orderNo || orderNo,
    trackInfo,
    ipeLogisticsTracks: trackInfo.map((t) => ({
      waybillNo: t.waybillNo,
      packageNo: t.packageNo,
      carrier: t.carrier,
      track: { trackInfo: [t] },
    })),
    packageWaybillList: trackInfo.map((t) => ({
      waybillNo: t.waybillNo,
      packageNo: t.packageNo,
      carrier: t.carrier,
    })),
  };
}

/**
 * EU 合规实体回填（TK-PUB-28）：
 * 编辑商品时本地草稿未必带 manufacturer_ids / responsible_person_ids，
 * 以平台 Get Product 已绑定实体为准补齐，保证「合规认证」区能展示当前绑定。
 * 平台调用失败只记录警告，不阻断编辑回填。
 */
/**
 * 补齐制造商 / 责任人 ID。
 * 默认只吃本地 payload（发布记录 platformProductRaw / 商品缓存），不打平台 Get Product；
 * allowPlatform=true 时才回落实时接口。
 */
async function hydrateTiktokComplianceEntities(shopId, productId, form, options = {}) {
  const pid = String(productId || '').trim();
  if (!pid || !form || typeof form !== 'object') return { form, warning: '' };
  const allowPlatform = Boolean(options.allowPlatform);
  const localRaw = options.localProductRaw && typeof options.localProductRaw === 'object'
    ? options.localProductRaw
    : null;
  let nextForm = form;
  let hasManufacturer = Array.isArray(nextForm.manufacturerIds) && nextForm.manufacturerIds.length > 0;
  let hasResponsible = Array.isArray(nextForm.responsiblePersonIds) && nextForm.responsiblePersonIds.length > 0;
  if (hasManufacturer && hasResponsible) return { form: nextForm, warning: '' };

  const pickIds = (raw) => (Array.isArray(raw) ? raw.map((v) => String(v)).filter(Boolean) : []);
  const applyFromPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
    const manufacturerIds = pickIds(data?.manufacturer_ids ?? data?.manufacturerIds);
    const responsiblePersonIds = pickIds(data?.responsible_person_ids ?? data?.responsiblePersonIds);
    if (!manufacturerIds.length && !responsiblePersonIds.length) return null;
    return {
      ...nextForm,
      manufacturerIds: hasManufacturer ? nextForm.manufacturerIds : manufacturerIds,
      responsiblePersonIds: hasResponsible ? nextForm.responsiblePersonIds : responsiblePersonIds,
    };
  };

  if (localRaw) {
    const patched = applyFromPayload(localRaw);
    if (patched) {
      nextForm = patched;
      hasManufacturer = Array.isArray(nextForm.manufacturerIds) && nextForm.manufacturerIds.length > 0;
      hasResponsible = Array.isArray(nextForm.responsiblePersonIds) && nextForm.responsiblePersonIds.length > 0;
      if (hasManufacturer && hasResponsible) return { form: nextForm, warning: '' };
    }
  }

  // 本地商品缓存（DB payload），不打 TTS 实时接口
  try {
    const cached = await tiktokRequest(
      `/shops/${encodeURIComponent(shopId)}/product-cache/${encodeURIComponent(pid)}`,
    );
    if (cached?.found && cached?.item?.payload) {
      const patched = applyFromPayload(cached.item.payload);
      if (patched) {
        nextForm = patched;
        hasManufacturer = Array.isArray(nextForm.manufacturerIds) && nextForm.manufacturerIds.length > 0;
        hasResponsible = Array.isArray(nextForm.responsiblePersonIds) && nextForm.responsiblePersonIds.length > 0;
        if (hasManufacturer && hasResponsible) return { form: nextForm, warning: '' };
      }
    }
  } catch {
    /* ignore cache miss */
  }

  if (!allowPlatform) return { form: nextForm, warning: '' };

  try {
    const res = await tiktokRequest(`/shops/${encodeURIComponent(shopId)}/products/${encodeURIComponent(pid)}`);
    const patched = applyFromPayload(res);
    if (!patched) return { form: nextForm, warning: '' };
    return { form: patched, warning: '' };
  } catch (e) {
    return { form: nextForm, warning: `合规实体回填失败（可手动重选）：${e?.message || e}` };
  }
}

/** 将日期/毫秒/秒转为 TikTok OpenAPI 常用的 epoch 秒。 */
function toTiktokEpochSeconds(value) {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const s = String(value).trim();
  if (!s) return undefined;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return undefined;
    return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
  }
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return undefined;
  return Math.floor(t / 1000);
}

function toTiktokZeroBasedPage(page) {
  if (page == null || page === '') return 0;
  const p = Number(page);
  if (!Number.isFinite(p) || p <= 0) return 0;
  // 前端常传 1-based；0 视为已是 0-based
  return p >= 1 ? Math.max(0, Math.floor(p) - 1) : 0;
}

/** TikTok 平台工作区 API（由 TikTok 门面整包复制；请求走 /api/tiktok） */
export const tiktokPlatformAPI = {
  unlockSession: (password, shopType) => tiktokRequest('/session/unlock', {
    method: 'POST',
    data: { password, shopType },
  }),
  getSessionStatus: () => tiktokRequest('/session/status'),
  lockSession: () => tiktokRequest('/session/lock', { method: 'POST' }),
  listShops: () => tiktokRequest('/shops'),
  getShopContext: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/context`),
  syncShopProfile: (shopId, options = {}) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/shop-profile/sync`, {
    method: 'POST',
    signal: options?.signal,
  }),
  listGoods: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.track) q.set('track', String(params.track));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/goods${qs ? `?${qs}` : ''}`);
  },
  syncGoods: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.track) q.set('track', String(params.track));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/goods/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  listPriceReviews: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/pricing/reviews${qs ? `?${qs}` : ''}`);
  },
  syncPriceReviews: (shopId) => {
    const id = encodeURIComponent(shopId);
    return tiktokRequest(`/shops/${id}/pricing/reviews/sync`, { method: 'POST' });
  },
  listPriceAdjusts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.status != null && params.status !== '') q.set('status', String(params.status));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/pricing/adjusts${qs ? `?${qs}` : ''}`);
  },
  syncPriceAdjusts: (shopId) => {
    const id = encodeURIComponent(shopId);
    return tiktokRequest(`/shops/${id}/pricing/adjusts/sync`, { method: 'POST' });
  },
  listStock: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/stock${qs ? `?${qs}` : ''}`);
  },
  syncStock: (shopId) => {
    const id = encodeURIComponent(shopId);
    return tiktokRequest(`/shops/${id}/stock/sync`, { method: 'POST' });
  },
  listFulfillment: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询备货发货');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/fulfillment${qs ? `?${qs}` : ''}`);
  },
  syncFulfillment: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/fulfillment/sync`, { method: 'POST' }),
  listPrint: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询打印任务');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/print${qs ? `?${qs}` : ''}`);
  },
  syncPrint: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/print/sync`, { method: 'POST' }),
  listAftersale: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询寄样售后');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/aftersale${qs ? `?${qs}` : ''}`);
  },
  syncAftersale: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/aftersale/sync`, { method: 'POST' }),
  listPacking: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询包装清单');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/packing${qs ? `?${qs}` : ''}`);
  },
  syncPacking: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/packing/sync`, { method: 'POST' }),
  listMarketing: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.status) q.set('status', String(params.status));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing${qs ? `?${qs}` : ''}`);
  },
  listMarketingEnrolls: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.sessionStatus != null && params.sessionStatus !== '') {
      q.set('sessionStatus', String(params.sessionStatus));
    }
    if (params.enrollStatus != null && params.enrollStatus !== '') {
      q.set('enrollStatus', String(params.enrollStatus));
    }
    if (params.enrollTimeBegin != null && params.enrollTimeBegin !== '') {
      q.set('enrollTimeBegin', String(params.enrollTimeBegin));
    }
    if (params.enrollTimeEnd != null && params.enrollTimeEnd !== '') {
      q.set('enrollTimeEnd', String(params.enrollTimeEnd));
    }
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/enrolls${qs ? `?${qs}` : ''}`);
  },
  listMarketingProducts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.thematicId != null && params.thematicId !== '') {
      q.set('thematicId', String(params.thematicId));
    }
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/products${qs ? `?${qs}` : ''}`);
  },
  syncMarketingProducts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.thematicId != null && params.thematicId !== '') {
      q.set('thematicId', String(params.thematicId));
    }
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/products/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  syncMarketing: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/marketing/sync`, { method: 'POST' }),
  getMarketingActivityDetail: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/activities/${tid}/detail${qs ? `?${qs}` : ''}`);
  },
  getMarketingActivityProducts: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    if (params.searchScrollContext) q.set('searchScrollContext', String(params.searchScrollContext));
    if (params.rowCount != null) q.set('rowCount', String(params.rowCount));
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/activities/${tid}/products${qs ? `?${qs}` : ''}`);
  },
  getMarketingActivitySessions: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    const productIds = Array.isArray(params.productIds)
      ? params.productIds.join(',')
      : String(params.productIds || '');
    q.set('productIds', productIds);
    const qs = q.toString();
    return tiktokRequest(`/shops/${id}/marketing/activities/${tid}/sessions?${qs}`);
  },
  submitMarketingEnroll: (shopId, thematicId, data = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    return tiktokRequest(`/shops/${id}/marketing/activities/${tid}/enroll`, {
      method: 'POST',
      data,
      timeoutMs: 120000,
    });
  },
  listAds: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询商品广告');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/ads${qs ? `?${qs}` : ''}`);
  },
  syncAds: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/ads/sync`, { method: 'POST' }),
  listSales: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询销量数据');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/sales${qs ? `?${qs}` : ''}`);
  },
  syncSales: (shopId) => tiktokRequest(`/shops/${encodeURIComponent(shopId)}/sales/sync`, { method: 'POST' }),

  // --- stubs for Shein-parity UI (no Tiktok backend yet) ---
  addCustomAttributeValue: (..._args) => Promise.resolve({ ok: true, stub: true }),
  applyCertificateSkcBinds: (..._args) => Promise.resolve({ ok: true, stub: true }),
  autoSubmitRecommendRetailPrice: (..._args) => Promise.resolve({ ok: true, stub: true }),
  batchCopyProductAttributeTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopySizeChartTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchDeleteCertificateLibrary: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteCertificates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteComplianceSkc: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteComplianceSkcAgency: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeletePhotoTemplates: (shopId, ids = []) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProductAttributeTemplates: (shopId, ids = []) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProductTemplates: (shopId, ids = []) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProducts: (shopId, productIdsOrPayload = []) => {
    const id = requireTiktokShopId(shopId, '批量删除本地商品缓存');
    const raw = productIdsOrPayload && typeof productIdsOrPayload === 'object' && !Array.isArray(productIdsOrPayload)
      ? productIdsOrPayload
      : { spuNames: Array.isArray(productIdsOrPayload) ? productIdsOrPayload : [] };
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/batch-delete`, {
      method: 'POST',
      data: {
        spuNames: Array.isArray(raw.spuNames) ? raw.spuNames : undefined,
        productIds: Array.isArray(raw.productIds) ? raw.productIds : undefined,
        ids: Array.isArray(raw.ids) ? raw.ids : undefined,
      },
    });
  },
  modifySkcShelf: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '上架/下架商品');
    const raw = data && typeof data === 'object' ? data : {};
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/modify-shelf`, {
      method: 'POST',
      data: {
        shelfState: raw.shelfState,
        action: raw.action,
        spuNames: Array.isArray(raw.spuNames) ? raw.spuNames : undefined,
        productIds: Array.isArray(raw.productIds) ? raw.productIds : undefined,
      },
      timeoutMs: 120000,
    });
  },
  deleteProductsOnPlatform: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '删除平台商品');
    const raw = data && typeof data === 'object' ? data : {};
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/platform-delete`, {
      method: 'POST',
      data: {
        spuNames: Array.isArray(raw.spuNames) ? raw.spuNames : undefined,
        productIds: Array.isArray(raw.productIds) ? raw.productIds : undefined,
      },
      timeoutMs: 120000,
    });
  },
  recoverProductsOnPlatform: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '回收平台商品');
    const raw = data && typeof data === 'object' ? data : {};
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/recover`, {
      method: 'POST',
      data: {
        spuNames: Array.isArray(raw.spuNames) ? raw.spuNames : undefined,
        productIds: Array.isArray(raw.productIds) ? raw.productIds : undefined,
      },
      timeoutMs: 120000,
    });
  },
  batchDeletePublishDrafts: async (shopId, ids = []) => {
    const id = requireTiktokShopId(shopId, '删除草稿');
    const list = Array.isArray(ids) ? ids.map((item) => String(item)) : [];
    const localIds = list.filter((item) => item.startsWith('local-') || !/^\d+$/.test(item));
    const serverIds = list.filter((item) => /^\d+$/.test(item));
    if (localIds.length) await batchDeleteTiktokLocalPublishDrafts(id, localIds);
    if (serverIds.length) {
      await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents/batch-delete`, {
        method: 'POST',
        data: { ids: serverIds },
      });
    }
    return { ok: true, deleted: list.length };
  },
  batchDeletePublishRecords: (shopId, ids = []) => {
    const id = requireTiktokShopId(shopId, '删除发布记录');
    return batchDeleteTiktokLocalPublishRecords(id, ids);
  },
  batchDeleteReturnOrders: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteRrpTemplates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteSizeChartTemplates: (shopId, ids = []) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteTiktokOrders: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '批量删除订单缓存');
    const raw = data && typeof data === 'object' ? data : {};
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/orders/batch-delete`, {
      method: 'POST',
      data: {
        orderNos: Array.isArray(raw.orderNos) ? raw.orderNos : undefined,
        ids: Array.isArray(raw.ids) ? raw.ids : undefined,
      },
    });
  },
  batchSaveRecommendRetailPrice: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '提交调价单');
    const raw = data && typeof data === 'object' ? data : {};
    // Tiktok：待卖家确认 → 批量确认/拒绝；Shein 风格 skc_rrp_list 时尽量映射 priceOrderSn
    const submitOrders = [];
    const adjustList = [];
    if (Array.isArray(raw.submitOrders)) {
      raw.submitOrders.forEach((sn) => {
        if (sn != null && String(sn).trim()) submitOrders.push(String(sn).trim());
      });
    }
    if (Array.isArray(raw.adjustList)) {
      raw.adjustList.forEach((row) => {
        const sn = row?.priceOrderSn || row?.orderSn;
        if (sn) adjustList.push({ priceOrderSn: String(sn), result: Number(row?.result) === 2 ? 2 : 1 });
      });
    }
    if (Array.isArray(raw.skc_rrp_list)) {
      raw.skc_rrp_list.forEach((row) => {
        const sn = row?.priceOrderSn || row?.orderSn || row?.skc_name || row?.skcName;
        if (sn) {
          submitOrders.push(String(sn));
          adjustList.push({ priceOrderSn: String(sn), result: 1 });
        }
      });
    }
    if (raw.priceOrderSn) {
      submitOrders.push(String(raw.priceOrderSn));
      adjustList.push({ priceOrderSn: String(raw.priceOrderSn), result: 1 });
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts/batch-review`, {
      method: 'POST',
      data: {
        batchResult: raw.batchResult != null ? Number(raw.batchResult) : 1,
        submitOrders: [...new Set(submitOrders)],
        adjustList,
        rejectReasons: raw.rejectReasons || undefined,
        action: raw.action || 'confirm',
      },
    }).then((res) => {
      const ok = Number(res?.successCount) > 0 || res?.ok === true;
      const snList = [...new Set(submitOrders)];
      return {
        ...res,
        success_list: ok ? snList.map((skc_name) => ({ skc_name })) : [],
        failed_list: ok ? [] : [{ reason: res?.msg || '提交失败' }],
        msg: res?.msg,
      };
    });
  },
  batchSyncProductPrice: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  bindGoodsCertificates: (..._args) => Promise.resolve({ ok: true, stub: true }),
  bindSkcLabels: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelAgencyAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertificateLibrarySyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertificateSyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelComplianceSyncJob: (..._args) => Promise.resolve({
    ok: true,
    cancelled: false,
    message: '合规同步任务不支持取消',
  }),
  cancelModuleSyncJob: (..._args) => Promise.resolve({
    ok: true,
    cancelled: false,
    message: '模块同步任务不支持取消',
  }),
  cancelPhotoAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelProductSyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelPublishRecordSyncJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '终止发布记录同步');
    return cancelTiktokPublishRecordSyncJob(id, jobId);
  },
  checkEditPermission: (..._args) => Promise.resolve({ ok: true, stub: true }),
  checkSupplierSkuRepeated: (..._args) => Promise.resolve({ ok: true, stub: true }),
  convertPhotoTemplateOriginal: (shopId, originalUrl) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/convert-original`,
    { method: 'POST', data: { originalUrl } },
  ),
  copyOfficialCertificateLibrary: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '复制官方证书');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official`,
      { method: 'POST', data, timeoutMs: 180000 },
    );
  },
  copyPhotoTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  copyProductAttributeTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/copy`,
    { method: 'POST', data },
  ),
  copyProductTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyProductTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyPhotoTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  checkMaterialTemplateVersions: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/version-check`,
    { method: 'POST', data },
  ),
  copyPublishRecordToDraft: async (shopId, recordId, data = {}) => {
    const id = requireTiktokShopId(shopId, '复制到草稿');
    if (data?.form && typeof data.form === 'object') {
      return createTiktokLocalPublishDraft(id, { form: data.form });
    }
    const record = await getTiktokLocalPublishRecord(id, recordId);
    const form = record?.formSnapshot && typeof record.formSnapshot === 'object'
      ? record.formSnapshot
      : {
        title: data?.title || record?.title || '',
        supplierCode: data?.supplierCode || record?.supplierCode || '',
        spuName: data?.spuName || data?.platformSpuName || record?.platformSpuName || '',
        skcList: Array.isArray(data?.skcList) ? data.skcList : [],
      };
    return createTiktokLocalPublishDraft(id, { form });
  },
  copySizeChartTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/copy`,
    { method: 'POST', data },
  ),
  createPhotoTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { method: 'POST', data },
  ),
  createProductAttributeTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { method: 'POST', data },
  ),
  createProductTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { method: 'POST', data },
  ),
  createPublishDraft: async (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '保存草稿');
    try {
      return await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents`, {
        method: 'POST',
        data: { ...(data && typeof data === 'object' ? data : {}), kind: 'DRAFT' },
      });
    } catch {
      return createTiktokLocalPublishDraft(id, data);
    }
  },
  createRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  createSizeChartTemplate: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { method: 'POST', data },
  ),
  deletePhotoTemplate: (shopId, templateId) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deleteProductAttributeTemplate: (shopId, templateId) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deleteProductTemplate: (shopId, templateId) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deletePublishDraft: async (shopId, draftId) => {
    const id = requireTiktokShopId(shopId, '删除草稿');
    const draft = String(draftId || '');
    if (draft.startsWith('local-') || !/^\d+$/.test(draft)) {
      return deleteTiktokLocalPublishDraft(id, draft);
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents/${encodeURIComponent(draft)}`, {
      method: 'DELETE',
    });
  },
  deleteRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  deleteSizeChartTemplate: (shopId, templateId) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  dismissAlert: (..._args) => Promise.resolve({ ok: true, stub: true }),
  downloadCertificateSchemaFileModel: (..._args) => Promise.resolve({ ok: true, stub: true }),
  downloadSyncPlatformDump: (shopId, kind) => downloadTiktokSyncPlatformDump(shopId, kind),
  fetchSyncPlatformDump: (shopId, kind) => fetchTiktokSyncPlatformDump(shopId, kind),
  fetchTiktokOrderDetails: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询订单详情');
    const list = data?.orderNoList || data?.orderIds || data?.ids;
    const first = Array.isArray(list) && list.length
      ? String(list[0] || '').trim()
      : String(data?.orderNo || data?.orderId || '').trim();
    if (!first) throw new Error('缺少订单号');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/orders/${encodeURIComponent(first)}`).then((res) => {
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const orders = Array.isArray(payload?.orders) ? payload.orders : null;
      const rawOrder = orders?.length
        ? (orders.find((o) => String(o?.id || o?.order_id || '') === first) || orders[0])
        : payload;
      const normalized = normalizeTiktokOrderDetail(rawOrder, first);
      if (!normalized) return res;
      return { ...res, data: { ...(payload || {}), orders: [normalized] }, order: normalized, ...normalized };
    });
  },
  getAgencyAutoBindJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getAlerts: (..._args) => Promise.resolve(null),
  getAssociatedAttributeRules: (..._args) => Promise.resolve(null),
  getAttributeTemplate: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询属性模板');
    const catId = data.catId ?? data.category_id ?? data.categoryId
      ?? (Array.isArray(data.product_type_id_list) ? data.product_type_id_list[0] : null)
      ?? data.product_type_id
      ?? data.productTypeId;
    if (!catId) throw new Error('缺少叶子类目 catId');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/attrs`, {
      query: { catId },
    }).then((raw) => normalizeTiktokAttrsToEditorShape(raw, { catId }));
  },
  /**
   * Get Attributes 原件：不做任何归一，直接返回后端代理响应。
   * 结构：{ catId, productAttrs, requiredProduct, optionalProduct, skcSaleAttrs, skuSaleAttrs,
   *        code, message, data }，其中 data.attributes 即平台原始属性数组。
   * 仅用于「平台原始 JSON」查看。
   */
  getPublishAttrsRaw: (shopId, catId) => {
    const id = requireTiktokShopId(shopId, '查询类目属性');
    const cat = String(catId ?? '').trim();
    if (!cat) throw new Error('缺少叶子类目 catId');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/attrs`, {
      query: { catId: cat },
    });
  },
  getPublishMeta: (shopId) => {
    const id = requireTiktokShopId(shopId, '查询发品元信息');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/meta`);
  },
  getPublishCategories: (shopId, parentCatId) => {
    const id = requireTiktokShopId(shopId, '查询发品类目');
    const query = {};
    if (parentCatId != null && Number(parentCatId) > 0) {
      query.parentCatId = Number(parentCatId);
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/categories`, { query });
  },
  /** Get Category Rules：尺码表 measurement_and_options */
  getPublishCategoryRules: (shopId, categoryId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询类目规则');
    const catId = String(categoryId || params.categoryId || params.catId || '').trim();
    if (!catId) throw new Error('缺少类目 ID');
    const query = {};
    if (params.locale) query.locale = params.locale;
    if (params.categoryVersion) query.categoryVersion = params.categoryVersion;
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/categories/${encodeURIComponent(catId)}/rules`,
      { query },
    );
  },
  matchPublishCategories: (shopId, keyword) => {
    const id = requireTiktokShopId(shopId, '搜索发品类目');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/categories/match`, {
      method: 'POST',
      data: { keyword },
    });
  },
  addTiktokGoods: (shopId, payload = {}) => {
    const id = requireTiktokShopId(shopId, '发布货品');
    const raw = payload && typeof payload === 'object' ? { ...payload } : {};
    if (raw.activate == null) raw.activate = true;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/goods`, {
      method: 'POST',
      data: raw,
      timeoutMs: 180000,
    });
  },
  /** Check Product Listing：发品前平台规范核验（body 与 Create Product 对齐，或含 ttsProduct） */
  checkProductListing: (shopId, payload = {}, opts = {}) => {
    const id = requireTiktokShopId(shopId, '发品前核验');
    const query = {};
    if (opts.diagnosisRequired === true) query.diagnosisRequired = true;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/listing-check`, {
      method: 'POST',
      data: payload && typeof payload === 'object' ? payload : {},
      query,
      timeoutMs: 120000,
    });
  },
  /** Check Listing Prerequisites：店铺发品前置条件 */
  checkListingPrerequisites: (shopId) => {
    const id = requireTiktokShopId(shopId, '发品前置条件核验');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/listing-prerequisites`);
  },
  getPublishParentSpecs: (shopId) => {
    const id = requireTiktokShopId(shopId, '查询父规格');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/parent-specs`);
  },
  createPublishSpec: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '创建自定义规格');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/specs`, {
      method: 'POST',
      data,
    });
  },
  getPublishSizeChartClass: async (shopId, catId) => {
    const id = requireTiktokShopId(shopId, '查询尺码分类');
    const res = await tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/categories/${encodeURIComponent(catId)}/rules`,
    );
    const sizeChart = res?.sizeChart || res?.data?.size_chart || null;
    const supportedFlag = sizeChart?.is_supported;
    const isSupported = sizeChart != null
      && supportedFlag !== false
      && supportedFlag !== 'false'
      && supportedFlag !== 0
      && supportedFlag !== '0';
    const measures = Array.isArray(sizeChart?.measurement_and_options)
      ? sizeChart.measurement_and_options
      : [];
    const reqFlag = sizeChart?.is_required;
    const explicitRequired = reqFlag === true || reqFlag === 'true' || reqFlag === 1 || reqFlag === '1';
    // 平台支持且下发测量项（服装/童装等）按必填；无 size_chart 或不支持则不要求
    const isRequired = Boolean(isSupported && (explicitRequired || measures.length > 0));
    return {
      sizeSpecClassCat: {
        classId: isSupported ? (Number(catId) || 1) : 0,
        classType: 0,
        isRequired,
        isSupported,
      },
      sizeChart,
      raw: res,
    };
  },
  getPublishSizeChartMeta: async (shopId, catId, classId) => {
    const id = requireTiktokShopId(shopId, '查询尺码元信息');
    const res = await tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/categories/${encodeURIComponent(catId)}/rules`,
    );
    const sizeChart = res?.sizeChart || res?.data?.size_chart || null;
    const groups = Array.isArray(sizeChart?.measurement_and_options)
      ? sizeChart.measurement_and_options
      : [];
    // 平台 options[].id 是「字符串测量 ID」（如 size_chart_option_bust），
    // 原样透传作为 size_chart_data.table.headers[].id，禁止 Number() 化（会被丢列）。
    const withOptions = groups.filter((g) => Array.isArray(g?.options) && g.options.length);
    const group = withOptions.find(
      (g) => g.options.some((o) => String(o?.role || '').toUpperCase() === 'KEY'),
    ) || withOptions[0] || null;
    const options = Array.isArray(group?.options) ? group.options : [];
    const elementList = options.map((opt, idx) => {
      const role = String(opt?.role || '').toUpperCase();
      const optId = String(opt?.id || '').trim();
      return {
        id: optId || `size_chart_option_${idx + 1}`,
        name: String(opt?.name || optId || `测量${idx + 1}`),
        ...(role ? { role } : {}),
        // role=KEY 是平台「关键测量」（商家后台显示 * 的必填列），RECOMMEND 为推荐列（选填）
        necessary: role === 'KEY',
        ...(opt?.unit ? { unit: opt.unit } : {}),
        ...(opt?.metric_value_range ? { metricValueRange: opt.metric_value_range } : {}),
        ...(opt?.imperial_value_range ? { imperialValueRange: opt.imperial_value_range } : {}),
      };
    });
    return {
      sizeSpecMeta: {
        groupList: [{ id: 1, name: '尺码' }],
        elementList,
      },
      measurementType: group?.measurement_type || group?.measurementType || '',
      classId,
      raw: res,
    };
  },
  getPublishSizeChartSettings: async (shopId, catId, classId) => {
    const meta = await tiktokPlatformAPI.getPublishSizeChartMeta(shopId, catId, classId);
    return {
      mappingContent: { meta: meta?.sizeSpecMeta || {} },
      classId,
    };
  },
  listPublishSizeChartTemplates: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询尺码模板');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/material-templates/size-charts`, {
      query: { page: 1, pageSize: params.pageSize || 50 },
    }).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      return {
        items: items.map((row) => {
          // 平台 size_chart_id 超大，保持字符串，禁止 Number()
          const businessId = String(
            row.platformBusinessId || row.businessId || row.id || '',
          ).trim();
          return {
            ...row,
            businessId,
            name: row.name,
          };
        }),
        sizeSpecDataList: items,
      };
    });
  },
  createPublishSizeChart: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '创建尺码表');
    const mode = String(data.sizeChartMode || data.size_chart_mode || 'REUSABLE_TEMPLATE')
      .trim()
      .toUpperCase() || 'REUSABLE_TEMPLATE';
    const productScoped = mode === 'PRODUCT_SCOPED';
    const {
      sizeChartMode: _modeIgnored,
      size_chart_mode: _modeIgnored2,
      name: nameIn,
      template_name: templateNameIn,
      styleNo,
      style_no: styleNoAlt,
      catId,
      categoryId,
      category_id: categoryIdAlt,
      sizeChartData,
      size_chart_data: sizeChartDataAlt,
      publishToPlatform: _pubIgnored,
      ...rest
    } = data;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/material-templates/size-charts`, {
      method: 'POST',
      data: {
        ...rest,
        // 单品尺码无需名称；可复用模板才落 name
        ...(productScoped
          ? {}
          : { name: nameIn || templateNameIn || '尺码表' }),
        styleNo: styleNo || styleNoAlt || 'SIZE',
        categoryId: catId || categoryId || categoryIdAlt,
        publishToPlatform: true,
        sizeChartMode: mode,
        sizeChartData: sizeChartData || sizeChartDataAlt,
      },
      timeoutMs: 120000,
    });
  },
  listPublishAccessories: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询包装清单物品');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/accessories`, {
      query: params,
    });
  },
  uploadPublishInstructions: (shopId, file) => {
    const id = requireTiktokShopId(shopId, '上传说明书');
    const formData = new FormData();
    formData.append('file', file);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/instructions`, {
      method: 'POST',
      data: formData,
      timeoutMs: 180000,
    });
  },
  getPublishInstructionsLanguages: (shopId, pdfId) => {
    const id = requireTiktokShopId(shopId, '查询说明书语种');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/${encodeURIComponent(pdfId)}/languages`,
    );
  },
  startPublishInstructionsTranslation: (shopId, pdfId, data = {}) => {
    const id = requireTiktokShopId(shopId, '翻译说明书');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/${encodeURIComponent(pdfId)}/translate`,
      {
        method: 'POST',
        data: {
          selectedSourceLanguageCode: data.selectedSourceLanguageCode,
          targetLanguageCodeList: Array.isArray(data.targetLanguageCodeList)
            ? data.targetLanguageCodeList
            : [],
        },
        timeoutMs: 120000,
      },
    );
  },
  getPublishInstructionsTranslationResult: (shopId, translateId) => {
    const id = requireTiktokShopId(shopId, '查询说明书翻译结果');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/translations/${encodeURIComponent(translateId)}`,
    );
  },
  uploadPublishGoodsFile: (shopId, file) => {
    const id = requireTiktokShopId(shopId, '上传货品文件');
    const formData = new FormData();
    formData.append('file', file);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/files`, {
      method: 'POST',
      data: formData,
      timeoutMs: 120000,
    });
  },
  uploadProductVideo: (shopId, file, options = {}) => {
    const id = requireTiktokShopId(shopId, '上传货品视频');
    const formData = new FormData();
    formData.append('file', file);
    const query = {};
    // 从视频空间选用：只转 Tiktok，不重复写入视频空间
    if (options.fromVideoSpace === true) {
      query.fromVideoSpace = true;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/videos`, {
      method: 'POST',
      data: formData,
      query,
      timeoutMs: 180000,
    });
  },
  getPublishVideoResult: (shopId, vid) => {
    const id = requireTiktokShopId(shopId, '查询视频转码');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/videos/result`, {
      query: { vid },
    });
  },
  getCachedReturnOrderDetail: (shopId, returnOrderNo) => {
    const id = requireTiktokShopId(shopId, '读取退货详情');
    const no = String(returnOrderNo || '').trim();
    if (!no) throw new Error('缺少退货单号');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/return-orders/${encodeURIComponent(no)}`);
  },
  getCachedTiktokOrder: (shopId, orderNo) => {
    const id = requireTiktokShopId(shopId, '读取订单缓存');
    const no = String(orderNo || '').trim();
    if (!no) throw new Error('缺少订单号');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/orders/${encodeURIComponent(no)}`).then((res) => {
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      if (!data) return null;
      const order = Array.isArray(data.orders) ? data.orders[0] : data;
      return normalizeTiktokOrderDetail(order, no);
    }).catch(() => null);
  },
  getCertAutoBindJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getCertificateLibrarySyncJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getCertificateSchemasDetail: (..._args) => Promise.resolve(null),
  getCertificateSyncJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getComplianceSkcDetail: (..._args) => Promise.resolve(null),
  getComplianceSyncJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '查询合规同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/sync-jobs/${encodeURIComponent(jid)}`);
  },
  getComplianceTodoSummary: (..._args) => Promise.resolve({}),
  getCustomAttributePermission: (..._args) => Promise.resolve(null),
  getGoodsSubmitJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '查询发品任务');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少发品任务 ID');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/publish/goods/submit-jobs/${encodeURIComponent(jid)}`,
    );
  },
  getMarketingEnrollJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '查询报名任务');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少报名任务 ID');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enroll-jobs/${encodeURIComponent(jid)}`,
    );
  },
  getLabelTemplates: (..._args) => Promise.resolve(null),
  getManualSyncQuota: (..._args) => Promise.resolve(null),
  getModuleSyncJob: (shopId, jobId, _module) => {
    const id = requireTiktokShopId(shopId, '查询模块同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/sync-jobs/${encodeURIComponent(jid)}`);
  },
  getPhotoAutoBindJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPhotoTemplate: (shopId, templateId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  getPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPreferences: (..._args) => Promise.resolve(null),
  getProductAttributeTemplate: (shopId, templateId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  /**
   * 编辑页回填优先级：
   * 1) 完整本地草稿 / 发布记录 → 直接回填（本站发品，不打平台）
   * 2) 无完整本地表单时：始终拉平台 Get Product（同步商品的缓存往往偏瘦）
   * 3) 本地商品缓存作补空 / 平台失败时的兜底
   * 站内私有字段（印花等）从草稿/记录合并补齐。
   */
  getProductForEdit: async (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '编辑商品');
    const spuName = String(data.spuName || data.spu_name || data.productId || data.product_id || '').trim();
    const draftId = data.draftId != null && data.draftId !== '' ? String(data.draftId).trim() : '';
    const recordId = data.recordId != null && data.recordId !== '' ? String(data.recordId).trim() : '';
    const forceRefresh = Boolean(data.forceRefresh);

    const {
      buildTiktokPublishFormFromProduct,
      isTiktokEditFormComplete,
      mergeTiktokEditForm,
      tiktokEditFormHasSizeChartMeasurements,
    } = await import('@/tiktok-platform/utils/buildTiktokPublishFormFromProduct');
    const {
      applyOfficialSizeTemplate,
      recordsFromTiktokSizeChartData,
    } = await import('@/tiktok-platform/utils/tiktokOfficialSizeChart');

    const pack = (form, extra = {}) => ({
      form,
      draftId: extra.draftId || draftId || '',
      recordId: extra.recordId || recordId || '',
      spuName: String(form?.spuName || spuName || '').trim(),
      title: form?.title || '',
      titleEn: form?.titleEn || '',
      supplierCode: form?.supplierCode || '',
      brandCode: form?.brandCode || '',
      categoryId: form?.categoryId || '',
      productTypeId: form?.productTypeId || '',
      categoryPathLabel: form?.categoryPathLabel || '',
      description: form?.description || '',
      originCountry: form?.originCountry || '',
      skcItems: Array.isArray(extra.skcItems) ? extra.skcItems : [],
      platformLoadWarning: extra.platformLoadWarning || '',
      source: extra.source || '',
    });

    const localSkcItems = (draft, record) => (
      Array.isArray(draft?.skcItems)
        ? draft.skcItems
        : (Array.isArray(record?.skcItems) ? record.skcItems : [])
    );

    /** 编辑回填：本地草稿无测量值时，用 size_chart.template.id / 站内尺码库补 officialSizeChart */
    const hydrateEditSizeChartDraft = async (form) => {
      if (!form || typeof form !== 'object') return form;
      if (tiktokEditFormHasSizeChartMeasurements(form)) return form;
      const ids = (Array.isArray(form.tiktokSizeTemplateIds) ? form.tiktokSizeTemplateIds : [])
        .map((v) => String(v || '').trim())
        .filter(Boolean);
      if (!ids.length || !id) return form;
      for (const tid of ids) {
        try {
          const res = await tiktokPlatformAPI.getSizeChartTemplate(id, tid);
          const item = res?.item || res || {};
          const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
          const draft = payload.officialSizeChart && typeof payload.officialSizeChart === 'object'
            ? payload.officialSizeChart
            : null;
          if (Array.isArray(draft?.records) && draft.records.length) {
            return {
              ...form,
              officialSizeChart: {
                ...draft,
                mode: draft.mode || 'create',
                selectedBusinessIds: (Array.isArray(draft.selectedBusinessIds) && draft.selectedBusinessIds.length)
                  ? draft.selectedBusinessIds
                  : ids.map((x) => Number(x)).filter((n) => n > 0),
              },
            };
          }
          const sizeChartData = payload.sizeChartData
            || payload.size_chart_data
            || (Array.isArray(payload.sizeChartDatas)
              ? (payload.sizeChartDatas[0]?.size_chart_data || payload.sizeChartDatas[0]?.sizeChartData)
              : null);
          const converted = recordsFromTiktokSizeChartData(sizeChartData, {
            groupList: Array.isArray(draft?.groupList) ? draft.groupList : [],
            elementList: Array.isArray(draft?.elementList) ? draft.elementList : [],
          });
          if (converted?.records?.length) {
            return {
              ...form,
              officialSizeChart: {
                mode: 'create',
                records: converted.records,
                selectedBusinessIds: ids.map((x) => Number(x)).filter((n) => n > 0),
                selectedLibraryId: '',
                groupList: [],
                elementList: [],
              },
            };
          }
          const applied = applyOfficialSizeTemplate(
            { ...item, ...payload, businessId: tid, sizeChartData },
            {},
          );
          if (applied?.records?.length) {
            return {
              ...form,
              officialSizeChart: {
                mode: 'create',
                records: applied.records,
                selectedBusinessIds: ids.map((x) => Number(x)).filter((n) => n > 0),
                selectedLibraryId: '',
                groupList: [],
                elementList: [],
              },
            };
          }
        } catch {
          /* 单品 PRODUCT_SCOPED 尺码可能不在站内库，忽略 */
        }
      }
      return form;
    };

    let draft = null;
    if (draftId) {
      try {
        draft = await getTiktokLocalPublishDraft(id, draftId);
      } catch {
        draft = null;
      }
    }
    if ((!draft || !draft.form) && spuName) {
      try {
        const listed = await listTiktokLocalPublishDrafts(id, { page: 1, pageSize: 100 });
        const items = listed?.items || listed?.content || [];
        const hit = items.find((row) => {
          const rowSpu = String(row.platformSpuName || row.spuName || row.productId || '').trim();
          return rowSpu && rowSpu === spuName;
        });
        if (hit?.id) {
          draft = await getTiktokLocalPublishDraft(id, hit.id);
        }
      } catch {
        /* ignore list fallback */
      }
    }

    let record = null;
    if (recordId) {
      try {
        record = await getTiktokLocalPublishRecord(id, recordId);
      } catch {
        record = null;
      }
    }
    if ((!record || !record.formSnapshot) && spuName) {
      try {
        record = await findTiktokLocalPublishRecordByProductId(id, spuName);
      } catch {
        record = null;
      }
    }

    const localForm = (draft?.form && typeof draft.form === 'object')
      ? draft.form
      : (record?.formSnapshot && typeof record.formSnapshot === 'object' ? record.formSnapshot : null);
    const localProductRaw = record?.platformProductRaw
      || record?.platformRaw
      || null;

    // 1/2：完整本地草稿或发布记录 — 本站发品，不打平台
    if (localForm && !forceRefresh && isTiktokEditFormComplete(localForm)) {
      const withSize = await hydrateEditSizeChartDraft(localForm);
      const hydrated = await hydrateTiktokComplianceEntities(
        id,
        withSize.spuName || spuName,
        withSize,
        { allowPlatform: false, localProductRaw },
      );
      return pack(hydrated.form, {
        draftId: draft?.id || draftId || '',
        recordId: record?.id || recordId || '',
        skcItems: localSkcItems(draft, record),
        platformLoadWarning: hydrated.warning || '',
        source: draft?.form ? 'draft' : 'record',
      });
    }

    // 本地商品缓存（列表同步入库；Search 摘要往往偏瘦，不能单独当完整详情）
    let cacheForm = null;
    let cachePayload = null;
    if (spuName) {
      try {
        const cached = await tiktokRequest(
          `/shops/${encodeURIComponent(id)}/product-cache/${encodeURIComponent(spuName)}`,
        );
        if (cached?.found && cached?.item?.payload) {
          cachePayload = cached.item.payload;
          cacheForm = buildTiktokPublishFormFromProduct(cachePayload);
        }
      } catch {
        /* ignore */
      }
    }

    // 无完整本地草稿/记录：始终拉平台 Get Product（forceRefresh 时也走这里）
    let platformForm = null;
    let platformRaw = null;
    let platformWarning = '';
    if (spuName) {
      try {
        const res = await tiktokRequest(
          `/shops/${encodeURIComponent(id)}/products/${encodeURIComponent(spuName)}`,
        );
        platformRaw = res;
        platformForm = buildTiktokPublishFormFromProduct(res);
        if (!platformForm) {
          platformWarning = '平台商品详情结构无法解析';
        }
      } catch (e) {
        platformWarning = `拉取平台商品详情失败：${e?.message || e}`;
      }
    }

    // 平台为主，缓存补空，再叠本地私有字段
    let mergedForm = platformForm || cacheForm || null;
    if (platformForm && cacheForm) {
      mergedForm = mergeTiktokEditForm(platformForm, cacheForm);
    }
    if (mergedForm && localForm) {
      mergedForm = mergeTiktokEditForm(mergedForm, localForm);
    }

    if (mergedForm && isTiktokEditFormComplete(mergedForm)) {
      const withSize = await hydrateEditSizeChartDraft(mergedForm);
      const hydrated = await hydrateTiktokComplianceEntities(id, spuName, withSize, {
        allowPlatform: false,
        localProductRaw: platformRaw || cachePayload || localProductRaw,
      });
      const source = platformForm
        ? (cacheForm || localForm ? 'platform+local' : 'platform')
        : (localForm ? 'cache+local' : 'cache');
      return pack(hydrated.form, {
        draftId: draft?.id || draftId || '',
        recordId: record?.id || recordId || '',
        skcItems: localSkcItems(draft, record),
        platformLoadWarning: [
          platformForm ? '' : (platformWarning || '已用本地商品缓存回填（平台详情暂不可用）'),
          hydrated.warning,
        ].filter(Boolean).join('；'),
        source,
      });
    }

    if (localForm) {
      const withSize = await hydrateEditSizeChartDraft(localForm);
      const hydrated = await hydrateTiktokComplianceEntities(
        id,
        withSize.spuName || spuName,
        withSize,
        { allowPlatform: false, localProductRaw: platformRaw || cachePayload || localProductRaw },
      );
      return pack(hydrated.form, {
        draftId: draft?.id || draftId || '',
        recordId: record?.id || recordId || '',
        skcItems: localSkcItems(draft, record),
        platformLoadWarning: [
          '本地草稿/发布记录字段不完整',
          platformWarning || (cacheForm ? '本地缓存亦不完整' : '且本地缓存/平台均未能补全详情'),
          hydrated.warning,
        ].filter(Boolean).join('；'),
        source: draft?.form ? 'draft-partial' : 'record-partial',
      });
    }

    if (cacheForm) {
      const withSize = await hydrateEditSizeChartDraft(cacheForm);
      const hydrated = await hydrateTiktokComplianceEntities(
        id,
        spuName,
        withSize,
        { allowPlatform: false, localProductRaw: cachePayload },
      );
      return pack(hydrated.form, {
        draftId: draft?.id || draftId || '',
        recordId: record?.id || recordId || '',
        skcItems: localSkcItems(draft, record),
        platformLoadWarning: [
          '本地商品缓存字段不完整',
          platformWarning || '且未能从平台补全详情',
          hydrated.warning,
        ].filter(Boolean).join('；'),
        source: 'cache-partial',
      });
    }

    return {
      form: null,
      draftId: draftId || '',
      recordId: recordId || '',
      spuName,
      platformLoadWarning: platformWarning
        || (draftId
          ? '关联本地草稿不存在或已清理，且未能从本地缓存/平台回填商品详情'
          : '未能从本地或平台回填商品详情，请确认商品已同步且产品 ID 正确'),
      source: 'empty',
    };
  },
  getProductListingStats: (shopIdOrParams) => {
    if (shopIdOrParams && typeof shopIdOrParams === 'object' && !Array.isArray(shopIdOrParams)) {
      const q = { ...shopIdOrParams };
      const id = requireTiktokShopId(q.shopId, '查询统计');
      delete q.shopId;
      delete q.allOfType;
      return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/listing-stats`, { query: q });
    }
    const id = requireTiktokShopId(shopIdOrParams, '查询统计');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/listing-stats`);
  },
  getProductSyncJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '查询同步进度');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/sync-job/${encodeURIComponent(jid)}`);
  },
  getProductTemplate: (shopId, templateId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  getProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPublishDraft: async (shopId, draftId) => {
    const id = requireTiktokShopId(shopId, '读取草稿');
    const draft = String(draftId || '');
    if (draft.startsWith('local-') || !/^\d+$/.test(draft)) {
      return getTiktokLocalPublishDraft(id, draft);
    }
    try {
      return await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents/${encodeURIComponent(draft)}`);
    } catch {
      return getTiktokLocalPublishDraft(id, draft);
    }
  },
  /**
   * Tiktok 发品约束（bg.goods.add 币种等）。
   * 历史方法名沿用 getPublishFillInStandard，但绝不调用 Shein
   * /open-api/goods/query-publish-fill-in-standard。
   */
  getPublishFillInStandard: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询 Tiktok 发品规范');
    const catId = data.catId ?? data.category_id ?? data.categoryId ?? data.productTypeId;
    const query = {};
    if (catId != null && Number(catId) > 0) query.catId = Number(catId);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/standard`, { query });
  },
  getPublishRecordSyncJob: (shopId, jobId) => {
    const id = requireTiktokShopId(shopId, '查询发布记录同步进度');
    return getTiktokPublishRecordSyncJob(id, jobId);
  },
  getRecommendRetailPendingCount: (..._args) => Promise.resolve({ total: 0 }),
  getReturnOrderDetails: (..._args) => Promise.resolve(null),
  getRrpTemplate: (..._args) => Promise.resolve(null),
  getShopBrands: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询品牌');
    const query = {};
    if (params.pageSize != null) query.pageSize = params.pageSize;
    if (params.page != null) query.page = params.page;
    if (params.pageToken) query.pageToken = params.pageToken;
    if (params.brandName) query.brandName = params.brandName;
    if (params.categoryId) query.categoryId = params.categoryId;
    if (params.isAuthorized != null) query.isAuthorized = params.isAuthorized;
    if (params.categoryVersion) query.categoryVersion = params.categoryVersion;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/brands`, { query });
  },
  /** 从 TikTok 平台拉取品牌写入本地缓存 */
  syncShopBrands: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步品牌');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/brands/sync`, { method: 'POST' });
  },
  /** 从 TikTok 平台 Search Size Charts 增量同步到本地尺码库 */
  syncShopSizeCharts: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步尺码表');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/material-templates/size-charts/sync`,
      { method: 'POST' },
    );
  },
  createShopBrand: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '创建定制品牌');
    const name = String(data.name || data.brandName || '').trim();
    if (!name) throw new Error('请填写品牌名称');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/brands`, {
      method: 'POST',
      data: { name },
    });
  },
  listComplianceManufacturers: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询本地制造商');
    const raw = params && typeof params === 'object' ? params : {};
    const size = Number(raw.size ?? raw.pageSize ?? raw.page_size ?? 20);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/manufacturers`, {
      query: {
        page: toTiktokZeroBasedPage(raw.page),
        size: Number.isFinite(size) ? size : 20,
      },
    }).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
      const total = Number(res?.total ?? res?.totalElements ?? items.length) || items.length;
      return {
        ...res,
        items,
        data: { manufacturers: items, total_count: total },
        total,
        totalElements: total,
        lastSyncedAt: res?.lastSyncedAt || null,
        neverSynced: !!res?.neverSynced,
      };
    });
  },
  searchManufacturers: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询制造商');
    const raw = data && typeof data === 'object' ? data : {};
    if (raw.source === 'cached' || raw.cached === true || raw.cached === 'true') {
      return tiktokPlatformAPI.listComplianceManufacturers(id, raw);
    }
    const pageSize = Number(raw.pageSize ?? raw.page_size ?? 20);
    const pageToken = raw.pageToken ?? raw.page_token;
    const query = { pageSize: Number.isFinite(pageSize) ? pageSize : 20 };
    if (pageToken) query.pageToken = pageToken;
    const body = {};
    if (raw.keyword) body.keyword = String(raw.keyword).trim();
    if (Array.isArray(raw.manufacturer_ids)) body.manufacturer_ids = raw.manufacturer_ids;
    if (Array.isArray(raw.locales)) body.locales = raw.locales;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/manufacturers/search`, {
      method: 'POST',
      query,
      data: body,
    });
  },
  createManufacturer: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '创建制造商');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/manufacturers`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  listComplianceResponsiblePersons: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询本地负责人');
    const raw = params && typeof params === 'object' ? params : {};
    const size = Number(raw.size ?? raw.pageSize ?? raw.page_size ?? 20);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/responsible-persons`, {
      query: {
        page: toTiktokZeroBasedPage(raw.page),
        size: Number.isFinite(size) ? size : 20,
      },
    }).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
      const total = Number(res?.total ?? res?.totalElements ?? items.length) || items.length;
      return {
        ...res,
        items,
        data: { responsible_persons: items, total_count: total },
        total,
        totalElements: total,
        lastSyncedAt: res?.lastSyncedAt || null,
        neverSynced: !!res?.neverSynced,
      };
    });
  },
  searchResponsiblePersons: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询负责人');
    const raw = data && typeof data === 'object' ? data : {};
    if (raw.source === 'cached' || raw.cached === true || raw.cached === 'true') {
      return tiktokPlatformAPI.listComplianceResponsiblePersons(id, raw);
    }
    const pageSize = Number(raw.pageSize ?? raw.page_size ?? 20);
    const pageToken = raw.pageToken ?? raw.page_token;
    const query = { pageSize: Number.isFinite(pageSize) ? pageSize : 20 };
    if (pageToken) query.pageToken = pageToken;
    const body = {};
    if (raw.keyword) body.keyword = String(raw.keyword).trim();
    if (Array.isArray(raw.responsible_person_ids)) {
      body.responsible_person_ids = raw.responsible_person_ids;
    }
    if (Array.isArray(raw.locales)) body.locales = raw.locales;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/responsible-persons/search`, {
      method: 'POST',
      query,
      data: body,
    });
  },
  createResponsiblePerson: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '创建负责人');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/compliance/responsible-persons`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  getShopCategories: (shopId) => {
    const id = requireTiktokShopId(shopId, '查询类目');
    // 可发类目树：is_leaf=true 且 permission_statuses 含 AVAILABLE
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/categories`);
  },
  getShopIps: (..._args) => Promise.resolve({ ips: [] }),
  getShopPublishPermission: (..._args) => Promise.resolve(null),
  getShopSiteList: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询售卖站点');
    const query = {};
    if (params?.refresh === true || params?.refresh === 'true') {
      query.refresh = true;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/site-list`, { query });
  },
  getShopStoreInfo: (shopId) => {
    const id = requireTiktokShopId(shopId, '查询店铺基本信息');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/store-info`);
  },
  getShopSyncStatus: (shopId) => {
    const id = requireTiktokShopId(shopId, '查询同步状态');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/sync-status`);
  },
  getShopWarehouses: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询发货仓');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/warehouses`, { query: params });
  },
  /** 从 TikTok 平台拉取发货仓写入本地缓存 */
  syncShopWarehouses: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步发货仓');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/warehouses/sync`, { method: 'POST' });
  },
  listShopFreightTemplates: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询运费模板');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/freight-templates`, { query: params });
  },
  /** 从 TikTok 平台拉取运费模板写入本地缓存（未接入时返回空） */
  syncShopFreightTemplates: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步运费模板');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/freight-templates/sync`, { method: 'POST' });
  },
  getShopFreightTemplate: (shopId, templateId) => {
    const id = requireTiktokShopId(shopId, '查询运费模板详情');
    const tid = String(templateId || '').trim();
    if (!tid) throw new Error('缺少运费模板 ID');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/freight-templates/${encodeURIComponent(tid)}`,
    );
  },
  batchDeleteShopFreightTemplates: (shopId, ids = []) => {
    const id = requireTiktokShopId(shopId, '批量删除运费模板缓存');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/freight-templates/batch-delete`, {
      method: 'POST',
      data: { ids },
    });
  },
  listShopModels: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询店铺模特');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/models`, { query: params });
  },
  /** 从 TikTok 平台拉取模特写入本地缓存（未接入时返回占位） */
  syncShopModels: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步模特');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/models/sync`, { method: 'POST' });
  },
  createShopModel: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '新建模特');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/models`, {
      method: 'POST',
      data,
    });
  },
  updateShopModel: (shopId, modelId, data = {}) => {
    const id = requireTiktokShopId(shopId, '编辑模特');
    const mid = String(modelId || '').trim();
    if (!mid) throw new Error('缺少模特 ID');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/models/${encodeURIComponent(mid)}`, {
      method: 'PUT',
      data,
    });
  },
  batchDeleteShopModels: (shopId, ids = [], options = {}) => {
    const id = requireTiktokShopId(shopId, '批量删除模特');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/models/batch-delete`, {
      method: 'POST',
      data: {
        ids,
        // 默认仅删本地缓存（平台模特删除接口不可靠/未统一开放）
        platform: options.platform === true,
      },
    });
  },
  batchDeleteShopWarehouses: (shopId, ids = []) => {
    const id = requireTiktokShopId(shopId, '批量删除发货仓缓存');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/warehouses/batch-delete`, {
      method: 'POST',
      data: { ids },
    });
  },
  getSizeChartTemplate: (shopId, templateId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  getTiktokOrderLogisticsTrack: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询物流轨迹');
    const orderNo = String(params.orderNo || params.orderId || params.id || '').trim();
    if (!orderNo) throw new Error('缺少订单号');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/orders/${encodeURIComponent(orderNo)}/tracking`,
    ).then((res) => adaptTiktokLogisticsTracking(res, orderNo));
  },
  getWebhook: eventId => tiktokRequest(`/webhooks/${encodeURIComponent(eventId)}`),
  listCertificateLibrary: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询证书库');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library`,
      { query: params },
    );
  },
  listCertificateSkcBinds: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listCertificateTypes: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listCertificates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listComplianceAgencies: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listComplianceRequirements: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listComplianceSkc: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listDiscussOrders: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询核价单');
    const raw = data && typeof data === 'object' ? data : {};
    const pageNum = Number(raw.pageNum ?? raw.page ?? 1);
    const pageSize = Number(raw.pageSize ?? raw.size ?? 20);
    const page = Number.isFinite(pageNum) && pageNum > 0 ? Math.floor(pageNum) - 1 : 0;
    const size = Number.isFinite(pageSize) && pageSize > 0 ? Math.min(100, Math.floor(pageSize)) : 20;
    const statusFilter = raw.discussStatus ?? raw.status;
    const keyword = Array.isArray(raw.discussSnList) && raw.discussSnList.length
      ? String(raw.discussSnList[0])
      : (raw.keyword ? String(raw.keyword) : undefined);
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('size', String(size));
    if (keyword) q.set('keyword', keyword);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/pricing/reviews?${q.toString()}`).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      const mapped = items.map((row) => {
        const statusRaw = row.discussStatus ?? row.orderStatus ?? row.status;
        const statusNum = statusRaw === '' || statusRaw == null ? null : Number(statusRaw);
        return {
          ...row,
          discussSn: row.discussSn || row.orderSn || row.orderId || '',
          orderSn: row.orderSn || row.orderId || row.discussSn || '',
          orderId: row.orderId || row.orderSn || row.discussSn || '',
          discussStatus: Number.isFinite(statusNum) ? statusNum : statusRaw,
          orderStatus: Number.isFinite(statusNum) ? statusNum : statusRaw,
          status: Number.isFinite(statusNum) ? statusNum : (row.status ?? statusRaw),
          supplyPrice: row.supplyPrice ?? row.declarePrice,
          suggestSupplyPrice: row.suggestSupplyPrice ?? row.suggestPrice,
          canBargain: row.canBargain ?? row.negotiable,
          productName: row.productName || row.productTitle || row.product_title || '',
          productTitle: row.productTitle || row.productName || row.product_title || '',
          mainPicUrl: row.mainPicUrl || row.imageUrl || row.mainImageUrl || '',
          imageUrl: row.imageUrl || row.mainPicUrl || row.mainImageUrl || '',
          supplierCode: row.supplierCode || row.extCode || '',
          skcId: row.skcId || null,
          spuId: row.spuId || row.productId || null,
          productId: row.productId || row.spuId || null,
          rawJson: row.rawJson ?? null,
          platformItem: row.platformItem ?? null,
        };
      });
      const demos = mergeTiktokDiscussWithDemos(id, [], { statusFilter });
      const counts = buildDiscussStatusCounts(mergeTiktokDiscussWithDemos(id, mapped, {}));
      const pageItems = page === 0 ? [...demos, ...mapped] : mapped;
      const apiTotal = Number(res?.totalElements ?? res?.total ?? mapped.length) || 0;
      return {
        ...res,
        items: pageItems,
        content: pageItems,
        list: pageItems,
        total: apiTotal + demos.length,
        totalElements: apiTotal + demos.length,
        count: apiTotal + demos.length,
        statusCounts: counts,
      };
    }).catch(() => {
      const demos = mergeTiktokDiscussWithDemos(id, [], { statusFilter });
      const counts = buildDiscussStatusCounts(mergeTiktokDiscussWithDemos(id, [], {}));
      return {
        items: demos,
        content: demos,
        list: demos,
        total: demos.length,
        totalElements: demos.length,
        count: demos.length,
        statusCounts: counts,
      };
    });
  },
  seedDiscussPendingDemo: (shopId) => {
    const id = requireTiktokShopId(shopId, '加载核价单示例');
    return Promise.resolve(seedTiktokDiscussDemos(id));
  },
  seedAdjustPriceDemo: (shopId) => {
    const id = requireTiktokShopId(shopId, '加载调价单示例');
    return Promise.resolve(seedTiktokAdjustDemos(id));
  },
  listEnvironmentalMaterials: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listFinanceStatements: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询结算单');
    const raw = params && typeof params === 'object' ? params : {};
    const cached = raw.cached !== false && raw.cached !== 'false';
    const size = Number(raw.size ?? raw.pageSize ?? 20);
    const q = {
      cached,
      page: toTiktokZeroBasedPage(raw.page),
      size: Number.isFinite(size) ? size : 20,
    };
    if (!cached) {
      if (raw.pageSize != null) q.pageSize = raw.pageSize;
      if (raw.pageToken) q.pageToken = raw.pageToken;
      if (raw.statementTimeGe) q.statementTimeGe = raw.statementTimeGe;
      if (raw.statementTimeLt) q.statementTimeLt = raw.statementTimeLt;
      if (raw.paymentStatus) q.paymentStatus = raw.paymentStatus;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/finance/statements`, { query: q }).then((res) => {
      if (!cached) return res;
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
      return {
        ...res,
        items,
        data: { statements: items, total_count: Number(res?.total ?? items.length) || items.length },
      };
    });
  },
  listStatementTransactions: (shopId, statementId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询结算流水');
    const q = {};
    if (params.pageSize != null) q.pageSize = params.pageSize;
    if (params.pageToken) q.pageToken = params.pageToken;
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/finance/statements/${encodeURIComponent(statementId)}/transactions`,
      { query: q },
    );
  },
  getOrderStatementTransactions: (shopId, orderId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询订单结算'))}/finance/orders/${encodeURIComponent(orderId)}/transactions`,
  ),
  listUnsettledTransactions: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询未结算');
    const q = {};
    if (params.pageSize != null) q.pageSize = params.pageSize;
    if (params.pageToken) q.pageToken = params.pageToken;
    if (params.searchTimeGe != null) q.searchTimeGe = params.searchTimeGe;
    if (params.searchTimeLt != null) q.searchTimeLt = params.searchTimeLt;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/finance/unsettled`, { query: q });
  },
  listFinanceWithdrawals: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询提现');
    const raw = params && typeof params === 'object' ? params : {};
    const cached = raw.cached !== false && raw.cached !== 'false';
    const size = Number(raw.size ?? raw.pageSize ?? 20);
    const q = {
      cached,
      page: toTiktokZeroBasedPage(raw.page),
      size: Number.isFinite(size) ? size : 20,
    };
    if (!cached) {
      if (raw.pageSize != null) q.pageSize = raw.pageSize;
      if (raw.pageToken) q.pageToken = raw.pageToken;
      if (raw.createTimeGe) q.createTimeGe = raw.createTimeGe;
      if (raw.createTimeLt) q.createTimeLt = raw.createTimeLt;
      if (raw.types) q.types = raw.types;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/finance/withdrawals`, { query: q }).then((res) => {
      if (!cached) return res;
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
      return {
        ...res,
        items,
        data: { withdrawals: items, total_count: Number(res?.total ?? items.length) || items.length },
      };
    });
  },
  listFinancePayments: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询结算付款');
    const raw = params && typeof params === 'object' ? params : {};
    const cached = raw.cached !== false && raw.cached !== 'false';
    const size = Number(raw.size ?? raw.pageSize ?? 20);
    const q = {
      cached,
      page: toTiktokZeroBasedPage(raw.page),
      size: Number.isFinite(size) ? size : 20,
    };
    if (!cached) {
      if (raw.pageSize != null) q.pageSize = raw.pageSize;
      else if (raw.size != null) q.pageSize = raw.size;
      if (raw.pageToken) q.pageToken = raw.pageToken;
      if (raw.createTimeGe) q.createTimeGe = raw.createTimeGe;
      if (raw.createTimeLt) q.createTimeLt = raw.createTimeLt;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/finance/payments`, { query: q }).then((res) => {
      if (!cached) return res;
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
      return {
        ...res,
        items,
        data: { payments: items, total_count: Number(res?.total ?? items.length) || items.length },
      };
    });
  },
  listFinanceBills: (shopId, params = {}) => tiktokPlatformAPI.listFinancePayments(shopId, params),
  batchDeleteFinanceBills: (..._args) => Promise.resolve({ deleted: 0, ok: true }),
  getCachedFinanceBillDetail: (..._args) => Promise.resolve(null),
  getFinanceBillDetail: (..._args) => Promise.resolve(null),
  syncFinanceBills: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步财务');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/finance/sync`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  listPhotoTemplates: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { query: params },
  ),
  listProductAttributeTemplates: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { query: params },
  ),
  listProductMetaBySkc: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listProductTemplates: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { query: params },
  ),
  listProducts: (params = {}) => {
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    const id = requireTiktokShopId(query.shopId, '查询商品');
    delete query.shopId;
    delete query.allOfType;
    // 后端 0-based page；前端常传 1-based
    if (query.page != null) {
      const p = Number(query.page);
      if (Number.isFinite(p) && p > 0) query.page = Math.max(0, Math.floor(p) - 1);
    }
    if (query.pageSize != null && query.size == null) {
      query.size = query.pageSize;
      delete query.pageSize;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products`, { query });
  },
  listPublishDrafts: async (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询草稿');
    const local = await listTiktokLocalPublishDrafts(id, params);
    let serverItems = [];
    try {
      const server = await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents`, {
        query: { kind: 'DRAFT', page: params.page || 1, size: params.pageSize || params.size || 20, keyword: params.keyword || '' },
      });
      serverItems = Array.isArray(server?.items) ? server.items : [];
    } catch {
      serverItems = [];
    }
    const localOnly = (local.items || []).filter((row) => String(row?.id || '').startsWith('local-'));
    const items = [...serverItems, ...localOnly];
    return { items, content: items, total: items.length, totalElements: items.length, neverSynced: false };
  },
  /** 发布记录：读取本地保存的平台原始 JSON（发品响应 / 平台商品详情 / 表单快照） */
  getPublishRecordRaw: async (shopId, recordId) => {
    const id = requireTiktokShopId(shopId, '查看发布记录平台 JSON');
    const record = await getTiktokLocalPublishRecord(id, recordId);
    if (!record) return null;
    return {
      id: record.id,
      platformSpuName: record.platformSpuName || '',
      platformRaw: record.platformRaw ?? null,
      platformProductRaw: record.platformProductRaw ?? null,
      formSnapshot: record.formSnapshot ?? null,
    };
  },
  listPublishRecords: async (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询发布记录');
    const {
      mergeTiktokPublishRecordItems,
      countTiktokPublishRecordStatuses,
      filterTiktokPublishRecordItems,
    } = await import('@/tiktok-platform/utils/mergeTiktokPublishRecords');
    // 先取足量本地 + 服务端，再合并去重、按筛选项切页（避免瘦服务端行盖住本地完整行）
    const local = await listTiktokLocalPublishRecords(id, {
      page: 1,
      pageSize: 500,
    }, { reviewItems: [] });
    let serverItems = [];
    try {
      const server = await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents`, {
        query: { kind: 'RECORD', page: 1, size: 100 },
      });
      serverItems = Array.isArray(server?.items) ? server.items : [];
    } catch {
      serverItems = [];
    }
    const merged = mergeTiktokPublishRecordItems(local.items || local.content || [], serverItems);
    const statusCounts = countTiktokPublishRecordStatuses(merged);
    const filtered = filterTiktokPublishRecordItems(merged, params);
    const page = Math.max(1, Number(params.page) || 1);
    const pageSize = Math.min(100, Math.max(1, Number(params.pageSize || params.size) || 20));
    const start = (page - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);
    return {
      items,
      content: items,
      list: items,
      total: filtered.length,
      totalElements: filtered.length,
      page,
      pageSize,
      statusCounts,
    };
  },
  createPublishRecord: async (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '写入发布记录');
    const payload = data && typeof data === 'object' ? data : {};
    const { extractTiktokPublishedSpu } = await import('@/tiktok-platform/utils/tiktokPublishResult');
    const productId = String(
      payload.publishedSpu
      || payload.platformSpuName
      || payload.productId
      || payload.platformProductId
      || extractTiktokPublishedSpu(payload.platformRes || payload.platformRaw)
      || '',
    ).trim();
    const form = payload.form && typeof payload.form === 'object' ? payload.form : {};
    const title = String(payload.title || form.title || form.productName || productId || '').trim();
    // 本地记录始终写：带上 TTS product_id / skus 与已上架态；服务端文档仅作备份
    const local = await createTiktokLocalPublishRecord(id, {
      ...payload,
      form,
      publishedSpu: productId || payload.publishedSpu,
      listed: payload.listed !== false,
    });
    try {
      await tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents`, {
        method: 'POST',
        data: {
          ...payload,
          form,
          kind: 'RECORD',
          productId,
          publishedSpu: productId,
          platformSpuName: productId,
          title,
          status: local?.statusLabel || local?.status || '已上架',
          statusLabel: local?.statusLabel || local?.status || '已上架',
          skcItems: local?.skcItems || [],
          productSkuIds: local?.productSkuIds || [],
          listed: payload.listed !== false,
          platformProductStatus: 'ACTIVATE',
          typeLabel: local?.typeLabel || '新增',
          categoryLabel: local?.categoryLabel || form.categoryLabel || form.categoryPathLabel || '',
          supplierCode: local?.supplierCode || form.supplierCode || '',
          publishedAt: local?.publishedAt || new Date().toISOString(),
        },
      });
    } catch {
      /* 服务端备份失败不阻断本地发布记录 */
    }
    return local;
  },
  listReturnOrders: async (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询退货');
    const raw = params && typeof params === 'object' ? params : {};
    const size = Number(raw.size ?? raw.pageSize ?? 20);
    const query = {
      page: toTiktokZeroBasedPage(raw.page),
      size: Number.isFinite(size) ? size : 20,
    };
    const status = raw.returnStatus || raw.status;
    if (status != null && status !== '') query.returnStatus = String(status);
    const res = await tiktokRequest(`/shops/${encodeURIComponent(id)}/return-orders`, { query });
    const itemsRaw = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
    const items = itemsRaw.map((row) => ({
      ...row,
      returnOrderNo: row.returnOrderNo || row.return_id || row.returnId || row.id,
      returnNo: row.returnNo || row.return_id || row.returnId || row.id,
      orderNo: row.orderNo || row.order_id || row.orderId,
      status: row.status || row.return_status || row.returnStatus,
      createTime: row.createTime || row.create_time,
    }));
    const total = Number(res?.total ?? res?.totalElements ?? items.length) || items.length;
    return {
      ...res,
      items,
      content: items,
      list: items,
      total,
      totalElements: total,
      lastSyncedAt: res?.lastSyncedAt || null,
      neverSynced: !!res?.neverSynced,
    };
  },
  approveReturn: (shopId, returnId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同意退货');
    const returnNo = String(returnId || '').trim();
    if (!returnNo) throw new Error('缺少退货单号');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/returns/${encodeURIComponent(returnNo)}/approve`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  rejectReturn: (shopId, returnId, data = {}) => {
    const id = requireTiktokShopId(shopId, '拒绝退货');
    const returnNo = String(returnId || '').trim();
    if (!returnNo) throw new Error('缺少退货单号');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/returns/${encodeURIComponent(returnNo)}/reject`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  listCancellations: async (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询取消单');
    const raw = params && typeof params === 'object' ? params : {};
    const size = Number(raw.size ?? raw.pageSize ?? 20);
    const query = {
      page: toTiktokZeroBasedPage(raw.page),
      size: Number.isFinite(size) ? size : 20,
    };
    if (raw.cancelStatus) query.cancelStatus = raw.cancelStatus;
    if (raw.orderId) query.orderId = raw.orderId;
    if (raw.createTimeGe != null) query.createTimeGe = raw.createTimeGe;
    if (raw.createTimeLt != null) query.createTimeLt = raw.createTimeLt;
    const res = await tiktokRequest(`/shops/${encodeURIComponent(id)}/cancellations`, { query });
    const itemsRaw = Array.isArray(res?.items) ? res.items : (Array.isArray(res?.content) ? res.content : []);
    const items = itemsRaw.map((row) => ({
      ...row,
      cancelId: row.cancelId || row.cancel_id || row.id,
      orderNo: row.orderNo || row.order_id || row.orderId,
      status: row.status || row.cancel_status || row.cancelStatus,
      createTime: row.createTime || row.create_time,
    }));
    const total = Number(res?.total ?? res?.totalElements ?? items.length) || items.length;
    return {
      ...res,
      items,
      content: items,
      total,
      totalElements: total,
      lastSyncedAt: res?.lastSyncedAt || null,
      neverSynced: !!res?.neverSynced,
    };
  },
  syncCancellations: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步取消单');
    const raw = data && typeof data === 'object' ? data : {};
    const body = { ...raw };
    const ge = toTiktokEpochSeconds(raw.createTimeGe ?? raw.create_time_ge ?? raw.startTime ?? raw.start_time);
    const lt = toTiktokEpochSeconds(raw.createTimeLt ?? raw.create_time_lt ?? raw.endTime ?? raw.end_time);
    if (ge != null) body.createTimeGe = ge;
    if (lt != null) body.createTimeLt = lt;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/cancellations/sync`, {
      method: 'POST',
      data: body,
    });
  },
  approveCancellation: (shopId, cancelId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '同意取消'))}/cancellations/${encodeURIComponent(cancelId)}/approve`,
    { method: 'POST', data: {} },
  ),
  rejectCancellation: (shopId, cancelId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '拒绝取消'))}/cancellations/${encodeURIComponent(cancelId)}/reject`,
    { method: 'POST', data: data && typeof data === 'object' ? data : {} },
  ),
  shipTiktokPackage: (shopId, packageId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '发货'))}/packages/${encodeURIComponent(packageId)}/ship`,
    { method: 'POST', data: data && typeof data === 'object' ? data : {} },
  ),
  markTiktokOrderShipped: (shopId, orderId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '上传运单'))}/orders/${encodeURIComponent(orderId)}/packages`,
    { method: 'POST', data: data && typeof data === 'object' ? data : {} },
  ),
  getOrderPriceDetail: (shopId, orderId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询价格明细'))}/orders/${encodeURIComponent(orderId)}/price-detail`,
  ),
  listShippingProviders: (shopId, deliveryOptionId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询物流商'))}/delivery-options/${encodeURIComponent(deliveryOptionId)}/shipping-providers`,
  ),
  getTiktokShippingDocument: (shopId, packageId, documentType = 'SHIPPING_LABEL') => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '获取面单'))}/packages/${encodeURIComponent(packageId)}/shipping-documents`,
    { query: { documentType } },
  ),
  createPromotionActivity: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '创建活动'))}/marketing/activities`,
    { method: 'POST', data },
  ),
  deactivatePromotionActivity: (shopId, activityId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '停用活动'))}/marketing/activities/${encodeURIComponent(activityId)}/deactivate`,
    { method: 'POST', data: {} },
  ),
  updatePromotionActivity: (shopId, activityId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '更新活动'))}/marketing/activities/${encodeURIComponent(activityId)}`,
    { method: 'PUT', data },
  ),
  removePromotionActivityProducts: (shopId, activityId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '结束活动商品'))}/marketing/activities/${encodeURIComponent(activityId)}/products`,
    { method: 'DELETE', data },
  ),
  republishPromotionActivity: (shopId, activityId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '重新发布活动'))}/marketing/activities/${encodeURIComponent(activityId)}/republish`,
    { method: 'POST', data },
  ),
  searchPromotionCoupons: (shopId, params = {}, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询优惠券');
    const query = {};
    if (params.pageSize != null) query.pageSize = params.pageSize;
    if (params.pageToken) query.pageToken = params.pageToken;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/marketing/coupons/search`, {
      method: 'POST',
      query,
      data,
    });
  },
  getPromotionCoupon: (shopId, couponId) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询优惠券'))}/marketing/coupons/${encodeURIComponent(couponId)}`,
  ),
  updateProductInventory: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '更新库存'))}/publish/inventory`,
    { method: 'POST', data },
  ),
  listQualityInspections: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询质检单');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/quality-inspections`, { query });
  },
  getQualityInspectionDetail: (shopId, qcBillId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询质检详情');
    const billId = String(qcBillId ?? '').trim();
    if (!billId) throw new Error('缺少质检单 id');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/quality-inspections/${encodeURIComponent(billId)}`,
      { query },
    );
  },
  listReturnPackageLines: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询退供明细');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/return-package-lines`, { query });
  },
  listRrpTemplates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listSizeChartTemplates: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { query: params },
  ),
  listModelTemplates: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/models`,
    { query: params },
  ),
  getModelTemplate: (shopId, templateId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/models/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  listFreightTemplates: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询运费模板');
    // 兼容旧调用：店铺同步缓存（无官方库）
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/freight-templates`, { query: params });
  },
  getFreightTemplate: (..._args) => Promise.resolve(null),
  listWarehouseTemplates: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询发货仓');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/warehouses`, { query: params });
  },
  getWarehouseTemplate: (..._args) => Promise.resolve(null),
  listSkcLabels: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listTiktokOrders: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询订单列表');
    const q = {};
    const page = params.page != null ? Number(params.page) : 1;
    const pageSize = params.pageSize != null ? Number(params.pageSize) : (params.size != null ? Number(params.size) : 20);
    // 后端 0-based page
    q.page = Number.isFinite(page) && page > 0 ? page - 1 : 0;
    q.size = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 20;
    if (params.orderStatus != null && params.orderStatus !== '') {
      q.orderStatus = String(params.orderStatus);
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/orders`, { query: q });
  },
  listWebhooks: (params = {}) => tiktokRequest('/webhooks', { query: params }),
  markAlertsRead: (..._args) => Promise.resolve({ ok: true, stub: true }),
  markCertificateTemplateSynced: (..._args) => Promise.resolve({ ok: true, stub: true }),
  moveCertificateLibraryTemplates: (..._args) => Promise.resolve({ ok: true, stub: true }),
  movePhotoTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/move`,
    { method: 'POST', data },
  ),
  moveProductAttributeTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/move`,
    { method: 'POST', data },
  ),
  moveProductTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/move`,
    { method: 'POST', data },
  ),
  moveSizeChartTemplates: (shopId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/move`,
    { method: 'POST', data },
  ),
  partialEditProduct: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '编辑商品');
    const raw = data && typeof data === 'object' ? data : {};
    const productId = String(raw.productId || raw.product_id || raw.spuName || raw.spu_name || '').trim();
    const ttsProduct = raw.ttsProduct && typeof raw.ttsProduct === 'object' ? raw.ttsProduct : null;
    if (!productId) throw new Error('缺少商品 ID');
    if (!ttsProduct) throw new Error('缺少 TikTok 商品编辑内容');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/partial-edit`, {
      method: 'POST',
      data: { productId, ttsProduct },
      timeoutMs: 180000,
    });
  },
  patchShopAvatar: (..._args) => Promise.resolve({ ok: true, stub: true }),
  prepareCertificateTemplateSync: (..._args) => Promise.resolve({ ok: true, stub: true }),
  printLabels: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '打印标签');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/print/labels`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  processDiscussOrder: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '处理核价单');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/pricing/reviews/process`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  batchReviewAdjustPrice: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '确认调价单');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts/batch-review`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  editJitStock: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '编辑 JIT 库存');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/stock/jit/edit`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  updateSemiStock: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '更新半托库存');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/stock/quantity/update`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  addSemiRouteStock: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '新增半托路由库存');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/stock/route/add`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  publishOrEditProduct: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '发布货品');
    const raw = data && typeof data === 'object' ? { ...data } : {};
    // TTS Create Product：默认创建后尝试上架；前端可显式传 activate:false
    if (raw.activate == null) raw.activate = true;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/goods`, {
      method: 'POST',
      data: raw,
      timeoutMs: 180000,
    });
  },
  queryChangePriceReasons: (..._args) => Promise.resolve({ ok: true, stub: true }),
  queryRecommendRetailPriceRule: (..._args) => Promise.resolve({ ok: true, stub: true }),
  replayWebhook: eventId => tiktokRequest(`/webhooks/${encodeURIComponent(eventId)}/replay`, {
    method: 'POST',
  }),
  revokePublishRecord: async () => {
    throw new Error('TikTok 发布记录不再支持平台撤回；请到「价格处理 → 核价单」处理核价');
  },
  /** Get Product：供发布记录同步审核态（audit.status） */
  getOpenProduct: (shopId, productId) => {
    const id = requireTiktokShopId(shopId, '查询商品详情');
    const pid = String(productId || '').trim();
    if (!pid) throw new Error('缺少商品 ID');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/${encodeURIComponent(pid)}`);
  },
  startPublishRecordSyncJob: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步发布记录审核态');
    return startTiktokPublishRecordSyncJob(id, (sid, productId) => (
      tiktokRequest(`/shops/${encodeURIComponent(sid)}/products/${encodeURIComponent(productId)}`)
    ));
  },
  saveComplianceSkcAgency: (..._args) => Promise.resolve({ ok: true, stub: true }),
  saveGoodsCertificate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  saveProductSalePrice: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '更新售价');
    const raw = data && typeof data === 'object' ? data : {};
    const productId = String(raw.productId || raw.product_id || '').trim();
    if (!productId) throw new Error('缺少商品 ID（productId）');
    const skus = Array.isArray(raw.skus) ? raw.skus : [];
    if (!skus.length) throw new Error('缺少 SKU 价格列表');
    return tiktokRequest(
      `/shops/${encodeURIComponent(id)}/products/${encodeURIComponent(productId)}/prices`,
      { method: 'POST', data: { skus } },
    );
  },
  searchGoodsCertificates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  /**
   * 平台营销活动价格锁定（编辑/改价前置校验）。
   * 后端读 TTS Promotion：`activity_commands` 含 IMMUTABLE 的活动覆盖商品售价。
   */
  getPublishPriceLocks: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询活动价格锁定');
    const raw = data && typeof data === 'object' ? data : {};
    const productId = String(raw.productId || raw.product_id || raw.spuName || '').trim();
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/price-locks`, {
      method: 'POST',
      data: productId ? { productId } : {},
    });
  },
  /**
   * 商品级自定义配送方式（delivery_option_ids）。
   * 仅 ID/MX/MY/PH/SG/TH/VN 市场生效，留空则继承仓库默认；非白名单市场 supported=false。
   */
  getPublishDeliveryOptions: (shopId, warehouseId) => {
    const id = requireTiktokShopId(shopId, '查询配送方式');
    const wh = String(warehouseId || '').trim();
    if (!wh) return Promise.resolve({ ok: false, supported: false, items: [] });
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/delivery-options`, {
      method: 'GET',
      query: { warehouseId: wh },
    });
  },
  searchRecommendRetailPrice: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '查询调价单');
    const raw = data && typeof data === 'object' ? data : {};
    const pageNum = Number(raw.page_num ?? raw.pageNum ?? raw.page ?? 1);
    const pageSize = Number(raw.page_size ?? raw.pageSize ?? raw.size ?? 20);
    const page = Number.isFinite(pageNum) && pageNum > 0 ? Math.floor(pageNum) - 1 : 0;
    const size = Number.isFinite(pageSize) && pageSize > 0 ? Math.min(100, Math.floor(pageSize)) : 20;
    const statusFilter = raw.status;
    const joinTokens = (list) => (Array.isArray(list) ? list : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .slice(0, 50)
      .join(',');
    const orderSn = joinTokens(raw.priceOrderSnList)
      || (raw.priceOrderSn != null && String(raw.priceOrderSn).trim() ? String(raw.priceOrderSn).trim() : '');
    const skc = joinTokens(raw.skcNameList)
      || (raw.skc != null && String(raw.skc).trim() ? String(raw.skc).trim() : '');
    const supplierCode = joinTokens(raw.supplierCodeList)
      || (raw.supplierCode != null && String(raw.supplierCode).trim() ? String(raw.supplierCode).trim() : '');
    const keyword = (!orderSn && !skc && !supplierCode)
      ? (raw.keyword ? String(raw.keyword) : undefined)
      : undefined;
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('size', String(size));
    if (keyword) q.set('keyword', keyword);
    if (orderSn) q.set('orderSn', orderSn);
    if (skc) q.set('skc', skc);
    if (supplierCode) q.set('supplierCode', supplierCode);
    if (statusFilter !== undefined && statusFilter !== null && statusFilter !== '') {
      q.set('status', String(statusFilter));
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts?${q.toString()}`).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      const mapped = items.map((row) => {
        const statusRaw = row.status ?? row.orderStatus;
        const statusNum = statusRaw === '' || statusRaw == null ? null : Number(statusRaw);
        return {
          ...row,
          skc_name: String(row.skc_name || row.skcName || row.skcId || row.orderSn || row.priceOrderSn || ''),
          skcName: row.skcName || row.skc_name || row.skcId || '',
          skcId: row.skcId || null,
          priceOrderSn: row.priceOrderSn || row.orderSn || '',
          orderSn: row.orderSn || row.priceOrderSn || '',
          productName: row.productName || row.product_title || row.productTitle || '',
          product_title: row.product_title || row.productTitle || row.productName || '',
          productTitle: row.productTitle || row.product_title || row.productName || '',
          imageUrl: row.imageUrl || row.mainPicUrl || row.mainImageUrl || '',
          mainPicUrl: row.mainPicUrl || row.imageUrl || row.mainImageUrl || '',
          supplierCode: row.supplierCode || row.extCode || '',
          status: Number.isFinite(statusNum) ? statusNum : statusRaw,
          skuInfoList: Array.isArray(row.skuInfoList) ? row.skuInfoList : [],
          siteNameList: Array.isArray(row.siteNameList) ? row.siteNameList : [],
          rawJson: row.rawJson ?? null,
          platformItem: row.platformItem ?? null,
        };
      });
      const demos = mergeTiktokAdjustWithDemos(id, [], { statusFilter });
      const pageItems = page === 0 ? [...demos, ...mapped] : mapped;
      const apiTotal = Number(res?.totalElements ?? res?.total ?? mapped.length) || 0;
      return {
        ...res,
        items: pageItems,
        content: pageItems,
        list: pageItems,
        skc_rrp_list: pageItems,
        total: apiTotal + demos.length,
        totalElements: apiTotal + demos.length,
        count: apiTotal + demos.length,
      };
    }).catch(() => {
      const demos = mergeTiktokAdjustWithDemos(id, [], { statusFilter });
      return {
        items: demos,
        content: demos,
        list: demos,
        skc_rrp_list: demos,
        total: demos.length,
        totalElements: demos.length,
        count: demos.length,
      };
    });
  },
  searchRecommendRetailPriceAudit: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  seedCertificateDemo: (..._args) => Promise.resolve({ ok: true, stub: true }),
  seedPhotoComplianceDemo: (..._args) => Promise.resolve({ ok: true, stub: true }),
  signReturnOrder: (..._args) => Promise.resolve({ ok: true, stub: true }),
  signReturnOrdersBatch: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startAgencyAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertificateLibrarySyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertificateSyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startComplianceSyncJob: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步合规');
    const raw = data && typeof data === 'object' ? data : {};
    return tiktokPlatformAPI.startModuleSyncJob(id, { ...raw, module: 'compliance' });
  },
  startModuleSyncJob: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '启动模块同步');
    const raw = data && typeof data === 'object' ? data : {};
    let module = String(raw.module || '').trim().toLowerCase();
    if (module === 'orders' || module === 'order') module = 'order';
    else if (module === 'products' || module === 'product') module = 'product';
    else if (module === 'returnorders' || module === 'return_orders' || module === 'return-orders' || module === 'returns') {
      module = 'return';
    } else if (module === 'cancellations' || module === 'cancellation') module = 'cancellation';
    else if (module === 'finance') module = 'finance';
    else if (module === 'compliance') module = 'compliance';
    else if (module === 'analytics') module = 'analytics';
    else if (module === 'marketing' || module === 'marketingproduct' || module === 'marketing_product' || module === 'marketing-product') {
      module = 'marketing';
    }
    else if (!module) module = 'order';
    const body = { ...raw, module };
    const ge = toTiktokEpochSeconds(raw.createTimeGe ?? raw.create_time_ge ?? raw.startTime ?? raw.start_time);
    const lt = toTiktokEpochSeconds(raw.createTimeLt ?? raw.create_time_lt ?? raw.endTime ?? raw.end_time);
    if (ge != null) body.createTimeGe = ge;
    if (lt != null) body.createTimeLt = lt;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/sync-jobs`, {
      method: 'POST',
      data: body,
    });
  },
  getShopPerformance: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询店铺表现');
    const raw = params && typeof params === 'object' ? { ...params } : {};
    if (!raw.start_date && !raw.startDate && !raw.range_start && !raw.rangeStart) {
      const start = raw.start_date_ge || raw.startDateGe;
      if (start) raw.start_date = start;
    }
    if (!raw.end_date && !raw.endDate && !raw.range_end && !raw.rangeEnd) {
      const end = raw.end_date_lt || raw.endDateLt || raw.end_date_ge;
      if (end) raw.end_date = end;
    }
    if (raw.force === true || raw.force === 'true') raw.force = true;
    else delete raw.force;
    delete raw.cached;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/analytics/shop-performance`, {
      query: raw,
    });
  },
  listProductPerformance: (shopId, params = {}) => {
    const id = requireTiktokShopId(shopId, '查询商品表现');
    const raw = params && typeof params === 'object' ? { ...params } : {};
    if (!raw.start_date && !raw.startDate && !raw.range_start && !raw.rangeStart) {
      const start = raw.start_date_ge || raw.startDateGe;
      if (start) raw.start_date = start;
    }
    if (!raw.end_date && !raw.endDate && !raw.range_end && !raw.rangeEnd) {
      const end = raw.end_date_lt || raw.endDateLt || raw.end_date_ge;
      if (end) raw.end_date = end;
    }
    if (raw.force === true || raw.force === 'true') raw.force = true;
    else delete raw.force;
    delete raw.cached;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/analytics/product-performance`, {
      query: raw,
    });
  },
  syncAnalytics: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步经营数据');
    const raw = data && typeof data === 'object' ? { ...data } : {};
    if (!raw.start_date && !raw.startDate) {
      const start = raw.start_date_ge || raw.range_start || raw.rangeStart;
      if (start) raw.start_date = start;
    }
    if (!raw.end_date && !raw.endDate) {
      const end = raw.end_date_lt || raw.range_end || raw.rangeEnd;
      if (end) raw.end_date = end;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/analytics/sync`, {
      method: 'POST',
      data: raw,
    });
  },
  listSkuPerformance: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询 SKU 表现'))}/analytics/sku-performance`,
    { query: params },
  ),
  getPerformancePerHour: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询分时表现'))}/analytics/performance-per-hour`,
    { query: params },
  ),
  getVideoOverview: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询短视频表现'))}/analytics/video-overview`,
    { query: params },
  ),
  getLiveOverview: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询直播表现'))}/analytics/live-overview`,
    { query: params },
  ),
  getSpsOverview: (shopId, params = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(requireTiktokShopId(shopId, '查询店铺体验分'))}/analytics/sps-overview`,
    { query: params },
  ),
  syncReturnOrders: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步退货包裹');
    const raw = data && typeof data === 'object' ? data : {};
    const body = { ...raw };
    const ge = toTiktokEpochSeconds(raw.createTimeGe ?? raw.create_time_ge ?? raw.startTime ?? raw.start_time);
    const lt = toTiktokEpochSeconds(raw.createTimeLt ?? raw.create_time_lt ?? raw.endTime ?? raw.end_time);
    if (ge != null) body.createTimeGe = ge;
    if (lt != null) body.createTimeLt = lt;
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/return-orders/sync`, {
      method: 'POST',
      data: body,
    });
  },
  syncQualityInspections: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步质检单');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/quality-inspections/sync`, { method: 'POST' });
  },
  startPhotoAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startProductSyncJob: (shopId, data = {}) => {
    const id = requireTiktokShopId(shopId, '同步商品');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/sync-job`, {
      method: 'POST',
      data: { module: 'product', ...(data && typeof data === 'object' ? data : {}) },
    });
  },
  syncShopProducts: (shopId) => {
    const id = requireTiktokShopId(shopId, '同步商品');
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/products/sync`, { method: 'POST' });
  },
  cancelProductSyncJob: (..._args) => Promise.resolve({ ok: true, cancelled: false, message: '商品同步任务不支持取消' }),
  startProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  storePhotoTemplateOriginal: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return tiktokRequest(
      `/shops/${encodeURIComponent(shopId)}/material-templates/photos/store-original`,
      { method: 'POST', data: formData },
    );
  },
  syncCertificateSchemas: (..._args) => Promise.resolve({ ok: true, stub: true }),
  syncCertificates: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updatePhotoTemplate: (shopId, templateId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updatePreferences: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updateProductAttributeTemplate: (shopId, templateId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updateProductCost: async () => {
    throw new Error('TikTok Shop 请使用 Update Price 按 SKU 改展示价，无 Shein 式 update-cost 接口');
  },
  updateProductTemplate: (shopId, templateId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updatePublishDraft: async (shopId, draftId, data = {}) => {
    const id = requireTiktokShopId(shopId, '更新草稿');
    const draft = String(draftId || '');
    if (draft.startsWith('local-') || !/^\d+$/.test(draft)) {
      return updateTiktokLocalPublishDraft(id, draft, data);
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish-documents/${encodeURIComponent(draft)}`, {
      method: 'PUT',
      data: { ...(data && typeof data === 'object' ? data : {}), kind: 'DRAFT' },
    });
  },
  updateRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updateSizeChartTemplate: (shopId, templateId, data = {}) => tiktokRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  uploadDiscussFile: (..._args) => Promise.resolve({ ok: true, stub: true }),
  uploadGoodsCertificateFile: (..._args) => Promise.resolve({ ok: true, stub: true }),
  uploadProductPic: (shopId, file, options = {}) => {
    const id = requireTiktokShopId(shopId, '上传货品图片');
    const formData = new FormData();
    formData.append('file', file);
    // Tiktok imageBizType：0=货品图（主图/细节/素材/色块），1=外包装图
    const bizType = options.imageBizType != null
      ? Number(options.imageBizType)
      : (options.forPackage === true ? 1 : 0);
    if (Number.isFinite(bizType)) {
      formData.append('imageBizType', String(bizType));
    }
    const query = {};
    // 从图片空间选用：只转 Tiktok，不重复写入图片空间
    if (options.fromImageSpace === true) {
      query.fromImageSpace = true;
    }
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/images`, {
      method: 'POST',
      data: formData,
      query: {
        ...query,
        // TTS Upload Image use_case：主图/细节=MAIN_IMAGE；SKU 变体图=ATTRIBUTE_IMAGE；
        // 尺码表=SIZE_CHART_IMAGE；认证=CERTIFICATION_IMAGE。外包装暂无独立枚举，沿用 MAIN_IMAGE。
        useCase: options.useCase || 'MAIN_IMAGE',
      },
      timeoutMs: 120000,
    });
  },
  /** 发品本地落图（印花图等）：写入用户图片空间，不调 Tiktok 转图 */
  storePublishImage: (shopId, file) => {
    const id = requireTiktokShopId(shopId, '上传站内图片');
    const formData = new FormData();
    formData.append('file', file);
    return tiktokRequest(`/shops/${encodeURIComponent(id)}/publish/images/store`, {
      method: 'POST',
      data: formData,
      timeoutMs: 120000,
    });
  },
  uploadSkcLabelPicture: (..._args) => Promise.resolve({ ok: true, stub: true }),
};

/** TEMU 平台工作区 API（须 workspace token，session 除外） */
export const temuPlatformAPI = {
  unlockSession: (password, shopType) => temuRequest('/session/unlock', {
    method: 'POST',
    data: { password, shopType },
  }),
  getSessionStatus: () => temuRequest('/session/status'),
  lockSession: () => temuRequest('/session/lock', { method: 'POST' }),
  listShops: () => temuRequest('/shops'),
  getShopContext: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/context`),
  syncShopProfile: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/shop-profile/sync`, {
    method: 'POST',
  }),
  listGoods: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.track) q.set('track', String(params.track));
    if (params.gatewayRegion) q.set('gatewayRegion', String(params.gatewayRegion));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/goods${qs ? `?${qs}` : ''}`);
  },
  syncGoods: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.track) q.set('track', String(params.track));
    if (params.gatewayRegion) q.set('gatewayRegion', String(params.gatewayRegion));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/goods/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  listPriceReviews: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/pricing/reviews${qs ? `?${qs}` : ''}`);
  },
  syncPriceReviews: (shopId) => {
    const id = encodeURIComponent(shopId);
    return temuRequest(`/shops/${id}/pricing/reviews/sync`, { method: 'POST' });
  },
  listPriceAdjusts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.status != null && params.status !== '') q.set('status', String(params.status));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/pricing/adjusts${qs ? `?${qs}` : ''}`);
  },
  syncPriceAdjusts: (shopId) => {
    const id = encodeURIComponent(shopId);
    return temuRequest(`/shops/${id}/pricing/adjusts/sync`, { method: 'POST' });
  },
  listStock: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.track) q.set('track', String(params.track));
    if (params.gatewayRegion) q.set('gatewayRegion', String(params.gatewayRegion));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/stock${qs ? `?${qs}` : ''}`);
  },
  syncStock: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params && typeof params === 'object') {
      if (params.track) q.set('track', String(params.track));
      if (params.gatewayRegion) q.set('gatewayRegion', String(params.gatewayRegion));
    }
    const qs = q.toString();
    return temuRequest(`/shops/${id}/stock/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  listFulfillment: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询备货发货');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/fulfillment${qs ? `?${qs}` : ''}`);
  },
  syncFulfillment: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/fulfillment/sync`, { method: 'POST' }),
  listPrint: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询打印任务');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/print${qs ? `?${qs}` : ''}`);
  },
  syncPrint: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/print/sync`, { method: 'POST' }),
  listAftersale: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询寄样售后');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/aftersale${qs ? `?${qs}` : ''}`);
  },
  syncAftersale: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/aftersale/sync`, { method: 'POST' }),
  listPacking: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询包装清单');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/packing${qs ? `?${qs}` : ''}`);
  },
  syncPacking: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/packing/sync`, { method: 'POST' }),
  listMarketing: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.enrollSource != null && params.enrollSource !== '') {
      q.set('enrollSource', String(params.enrollSource));
    }
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing${qs ? `?${qs}` : ''}`);
  },
  listMarketingEnrolls: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.sessionStatus != null && params.sessionStatus !== '') {
      q.set('sessionStatus', String(params.sessionStatus));
    }
    if (params.enrollStatus != null && params.enrollStatus !== '') {
      q.set('enrollStatus', String(params.enrollStatus));
    }
    if (params.enrollTimeBegin != null && params.enrollTimeBegin !== '') {
      q.set('enrollTimeBegin', String(params.enrollTimeBegin));
    }
    if (params.enrollTimeEnd != null && params.enrollTimeEnd !== '') {
      q.set('enrollTimeEnd', String(params.enrollTimeEnd));
    }
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/enrolls${qs ? `?${qs}` : ''}`);
  },
  listMarketingProducts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    if (params.activityType) q.set('activityType', String(params.activityType));
    if (params.thematicId != null && params.thematicId !== '') {
      q.set('thematicId', String(params.thematicId));
    }
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/products${qs ? `?${qs}` : ''}`);
  },
  syncMarketingProducts: (shopId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const q = new URLSearchParams();
    if (params.thematicId != null && params.thematicId !== '') {
      q.set('thematicId', String(params.thematicId));
    }
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/products/sync${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },
  syncMarketing: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/marketing/sync`, { method: 'POST' }),
  getMarketingActivityDetail: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/activities/${tid}/detail${qs ? `?${qs}` : ''}`);
  },
  getMarketingActivityProducts: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    if (params.searchScrollContext) q.set('searchScrollContext', String(params.searchScrollContext));
    if (params.rowCount != null) q.set('rowCount', String(params.rowCount));
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/activities/${tid}/products${qs ? `?${qs}` : ''}`);
  },
  getMarketingActivitySessions: (shopId, thematicId, params = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    const q = new URLSearchParams();
    if (params.activityType != null) q.set('activityType', String(params.activityType));
    const productIds = Array.isArray(params.productIds)
      ? params.productIds.join(',')
      : String(params.productIds || '');
    q.set('productIds', productIds);
    const qs = q.toString();
    return temuRequest(`/shops/${id}/marketing/activities/${tid}/sessions?${qs}`);
  },
  submitMarketingEnroll: (shopId, thematicId, data = {}) => {
    const id = encodeURIComponent(shopId);
    const tid = encodeURIComponent(thematicId);
    return temuRequest(`/shops/${id}/marketing/activities/${tid}/enroll`, {
      method: 'POST',
      data,
      timeoutMs: 120000,
    });
  },
  listAds: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询商品广告');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/ads${qs ? `?${qs}` : ''}`);
  },
  syncAds: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/ads/sync`, { method: 'POST' }),
  listSales: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询销量数据');
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.size != null) q.set('size', String(params.size));
    if (params.keyword) q.set('keyword', String(params.keyword));
    const qs = q.toString();
    return temuRequest(`/shops/${encodeURIComponent(id)}/sales${qs ? `?${qs}` : ''}`);
  },
  syncSales: (shopId) => temuRequest(`/shops/${encodeURIComponent(shopId)}/sales/sync`, { method: 'POST' }),

  // --- stubs for Shein-parity UI (no Temu backend yet) ---
  addCustomAttributeValue: (..._args) => Promise.resolve({ ok: true, stub: true }),
  applyCertificateSkcBinds: (..._args) => Promise.resolve({ ok: true, stub: true }),
  autoSubmitRecommendRetailPrice: (..._args) => Promise.resolve({ ok: true, stub: true }),
  batchCopyProductAttributeTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopySizeChartTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchDeleteCertificateLibrary: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteCertificates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteComplianceSkc: (shopId, { ids = [], productIds = [], skcNames = [] } = {}) => {
    const id = requireTemuShopId(shopId, '批量删除合规缓存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/products/batch-delete`, {
      method: 'POST',
      data: {
        productIds: (Array.isArray(productIds) && productIds.length ? productIds : ids)
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0),
        skcNames: (Array.isArray(skcNames) ? skcNames : [])
          .map((s) => String(s || '').trim())
          .filter(Boolean),
      },
    });
  },
  batchDeleteComplianceSkcAgency: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeletePhotoTemplates: (shopId, ids = []) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProductAttributeTemplates: (shopId, ids = []) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProductTemplates: (shopId, ids = []) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteProducts: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeletePublishDrafts: (shopId, ids = []) => batchDeleteTemuLocalPublishDrafts(shopId, ids),
  batchDeletePublishRecords: (shopId, ids = []) => {
    const id = requireTemuShopId(shopId, '删除发布记录');
    return batchDeleteTemuLocalPublishRecords(id, ids);
  },
  batchDeleteReturnOrders: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteRrpTemplates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchDeleteSizeChartTemplates: (shopId, ids = []) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  batchDeleteTemuOrders: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  batchSaveRecommendRetailPrice: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '提交调价单');
    const raw = data && typeof data === 'object' ? data : {};
    // Temu：待卖家确认 → 批量确认/拒绝；Shein 风格 skc_rrp_list 时尽量映射 priceOrderSn
    const submitOrders = [];
    const adjustList = [];
    if (Array.isArray(raw.submitOrders)) {
      raw.submitOrders.forEach((sn) => {
        if (sn != null && String(sn).trim()) submitOrders.push(String(sn).trim());
      });
    }
    if (Array.isArray(raw.adjustList)) {
      raw.adjustList.forEach((row) => {
        const sn = row?.priceOrderSn || row?.orderSn;
        if (sn) adjustList.push({ priceOrderSn: String(sn), result: Number(row?.result) === 2 ? 2 : 1 });
      });
    }
    if (Array.isArray(raw.skc_rrp_list)) {
      raw.skc_rrp_list.forEach((row) => {
        const sn = row?.priceOrderSn || row?.orderSn || row?.skc_name || row?.skcName;
        if (sn) {
          submitOrders.push(String(sn));
          adjustList.push({ priceOrderSn: String(sn), result: 1 });
        }
      });
    }
    if (raw.priceOrderSn) {
      submitOrders.push(String(raw.priceOrderSn));
      adjustList.push({ priceOrderSn: String(raw.priceOrderSn), result: 1 });
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts/batch-review`, {
      method: 'POST',
      data: {
        batchResult: raw.batchResult != null ? Number(raw.batchResult) : 1,
        submitOrders: [...new Set(submitOrders)],
        adjustList,
        rejectReasons: raw.rejectReasons || undefined,
        action: raw.action || 'confirm',
      },
    }).then((res) => {
      const ok = Number(res?.successCount) > 0 || res?.ok === true;
      const snList = [...new Set(submitOrders)];
      return {
        ...res,
        success_list: ok ? snList.map((skc_name) => ({ skc_name })) : [],
        failed_list: ok ? [] : [{ reason: res?.msg || '提交失败' }],
        msg: res?.msg,
      };
    });
  },
  batchSyncProductPrice: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  bindGoodsCertificates: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '绑定证书');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/compliance/certificates/bind`,
      { method: 'POST', data: data && typeof data === 'object' ? data : {}, timeoutMs: 180000 },
    );
  },
  /**
   * 编辑/上传弹窗：查询该商品 + 证书类型需要填写/上传的内容
   * bg.arbok.open.cert.queryNeedUploadItems
   */
  queryCertificateNeedUpload: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询证书待上传项');
    const productId = Number(data?.productId || 0);
    const certType = Number(data?.certType || data?.certificateTypeId || 0);
    if (!(productId > 0)) throw new Error('缺少 productId');
    if (!(certType > 0)) throw new Error('缺少 certType');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/certificates/need-upload`, {
      method: 'POST',
      data: { productId, certType },
    });
  },
  /** 证书文件转换：bg.arbok.open.upload.uploadFile（base64 → 平台 url） */
  uploadComplianceCertificateFile: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '转换证书文件');
    const base64File = data?.base64File || data?.base64 || '';
    if (!base64File) throw new Error('缺少 base64File');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/certificates/upload-file`, {
      method: 'POST',
      data: { base64File },
      timeoutMs: 180000,
    });
  },
  /** 提交商品证书：bg.arbok.open.cert.uploadProductCert */
  uploadProductCertificate: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '提交商品证书');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/certificates/upload`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
      timeoutMs: 180000,
    });
  },
  /** 实拍/图片转换：bg.flash.open.upload.real.image */
  uploadCompliancePhotoFile: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '转换实拍图片');
    const image = data?.image || data?.base64 || data?.base64File || '';
    if (!image) throw new Error('缺少 image');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/photos/upload`, {
      method: 'POST',
      data: { image, clientId: data?.clientId || undefined },
      timeoutMs: 180000,
    });
  },
  bindSkcLabels: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '绑定实拍图');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc-labels/bind`,
      { method: 'POST', data: data && typeof data === 'object' ? data : {}, timeoutMs: 180000 },
    );
  },
  cancelAgencyAutoBindJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '终止代理自动绑定');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/${encodeURIComponent(jid)}/cancel`, { method: 'POST' });
  },
  cancelCertAutoBindJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '终止证书自动绑定');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/${encodeURIComponent(jid)}/cancel`, { method: 'POST' });
  },
  cancelCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertificateLibrarySyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelCertificateSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '终止证书同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}/cancel`, { method: 'POST' });
  },
  cancelComplianceSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '终止合规同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}/cancel`, { method: 'POST' });
  },
  cancelModuleSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '取消模块同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/module-sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  cancelPhotoAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelProductSyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  cancelPublishRecordSyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  checkEditPermission: (..._args) => Promise.resolve({ ok: true, stub: true }),
  checkSupplierSkuRepeated: (..._args) => Promise.resolve({ ok: true, stub: true }),
  convertPhotoTemplateOriginal: (shopId, originalUrl) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/convert-original`,
    { method: 'POST', data: { originalUrl } },
  ),
  copyOfficialCertificateLibrary: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '复制官方证书');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official`,
      { method: 'POST', data, timeoutMs: 180000 },
    );
  },
  copyPhotoTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  copyProductAttributeTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/copy`,
    { method: 'POST', data },
  ),
  copyProductTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyProductTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyPhotoTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  checkMaterialTemplateVersions: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/version-check`,
    { method: 'POST', data },
  ),
  copyPublishRecordToDraft: async (shopId, recordId, data = {}) => {
    const id = requireTemuShopId(shopId, '复制到草稿');
    if (data?.form && typeof data.form === 'object') {
      return createTemuLocalPublishDraft(id, { form: data.form });
    }
    const record = await getTemuLocalPublishRecord(id, recordId);
    const form = record?.formSnapshot && typeof record.formSnapshot === 'object'
      ? record.formSnapshot
      : {
        title: data?.title || record?.title || '',
        supplierCode: data?.supplierCode || record?.supplierCode || '',
        spuName: data?.spuName || data?.platformSpuName || record?.platformSpuName || '',
        skcList: Array.isArray(data?.skcList) ? data.skcList : [],
      };
    return createTemuLocalPublishDraft(id, { form });
  },
  copySizeChartTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/copy`,
    { method: 'POST', data },
  ),
  createPhotoTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { method: 'POST', data },
  ),
  createProductAttributeTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { method: 'POST', data },
  ),
  createProductTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { method: 'POST', data },
  ),
  createPublishDraft: (shopId, data = {}) => createTemuLocalPublishDraft(shopId, data),
  /**
   * 产品信息「复制到草稿箱」：本地货品 / 发布记录 → 新发品草稿，并写入所选上架站点。
   */
  copyProductToPublishDraft: async (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '复制到草稿箱');
    const spu = String(data.spuName || data.spu_name || data.spuId || '').trim();
    if (!spu || spu === '—') {
      throw new Error('缺少 SPU，无法复制到草稿箱');
    }
    const selectedSites = Array.isArray(data.selectedSites)
      ? data.selectedSites.map(String).map((s) => s.trim()).filter(Boolean)
      : [];

    const {
      fetchTemuPublishFormFromLocalGoods,
      stripTemuPlatformIdentityForDraft,
    } = await import('@/temu-platform/utils/buildTemuPublishFormFromLocalGoods');

    let form = null;
    try {
      const record = await findTemuLocalPublishRecordBySpu(id, spu);
      if (record?.formSnapshot && typeof record.formSnapshot === 'object') {
        form = stripTemuPlatformIdentityForDraft({ ...record.formSnapshot });
      }
    } catch {
      form = null;
    }
    if (!form || !(Array.isArray(form.skcList) && form.skcList.length)) {
      const fromGoods = await fetchTemuPublishFormFromLocalGoods(id, spu, {
        stripPlatformIds: true,
        selectedSites,
      });
      if (fromGoods) form = fromGoods;
    }
    if (!form || typeof form !== 'object') {
      throw new Error('本地暂无该商品同步数据。请先在产品信息页同步后再复制。');
    }
    if (selectedSites.length) {
      form.selectedSites = selectedSites;
      form.siteIds = selectedSites;
    }
    form.spuName = '';
    return createTemuLocalPublishDraft(id, { form });
  },
  createRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  createSizeChartTemplate: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { method: 'POST', data },
  ),
  deletePhotoTemplate: (shopId, templateId) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deleteProductAttributeTemplate: (shopId, templateId) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deleteProductTemplate: (shopId, templateId) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  deletePublishDraft: (shopId, draftId) => deleteTemuLocalPublishDraft(shopId, draftId),
  deleteRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  deleteSizeChartTemplate: (shopId, templateId) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  dismissAlert: (..._args) => Promise.resolve({ ok: true, stub: true }),
  downloadCertificateSchemaFileModel: (..._args) => Promise.resolve({ ok: true, stub: true }),
  downloadSyncPlatformDump: (shopId, kind) => downloadTemuSyncPlatformDump(shopId, kind),
  fetchSyncPlatformDump: (shopId, kind) => fetchTemuSyncPlatformDump(shopId, kind),
  fetchTemuOrderDetails: (..._args) => Promise.resolve({ ok: true, stub: true }),
  getAgencyAutoBindJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询代理自动绑定');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/${encodeURIComponent(jid)}`);
  },
  getAlerts: (..._args) => Promise.resolve(null),
  getAssociatedAttributeRules: (..._args) => Promise.resolve(null),
  getAttributeTemplate: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询属性模板');
    const catId = data.catId ?? data.category_id ?? data.categoryId
      ?? (Array.isArray(data.product_type_id_list) ? data.product_type_id_list[0] : null)
      ?? data.product_type_id
      ?? data.productTypeId;
    if (!catId) throw new Error('缺少叶子类目 catId');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/attrs`, {
      query: { catId },
    }).then((raw) => normalizeTemuAttrsToEditorShape(raw, { catId }));
  },
  getPublishMeta: (shopId) => {
    const id = requireTemuShopId(shopId, '查询发品元信息');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/meta`);
  },
  getPublishCategories: (shopId, parentCatId) => {
    const id = requireTemuShopId(shopId, '查询发品类目');
    const query = {};
    if (parentCatId != null && Number(parentCatId) > 0) {
      query.parentCatId = Number(parentCatId);
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/categories`, { query });
  },
  matchPublishCategories: (shopId, keyword) => {
    const id = requireTemuShopId(shopId, '搜索发品类目');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/categories/match`, {
      method: 'POST',
      data: { keyword },
    });
  },
  addTemuGoods: (shopId, payload = {}) => {
    const id = requireTemuShopId(shopId, '发布货品');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/goods`, {
      method: 'POST',
      data: payload,
      timeoutMs: 180000,
    });
  },
  getPublishParentSpecs: (shopId) => {
    const id = requireTemuShopId(shopId, '查询父规格');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/parent-specs`);
  },
  createPublishSpec: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '创建自定义规格');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/specs`, {
      method: 'POST',
      data,
    });
  },
  getPublishSizeChartClass: (shopId, catId) => {
    const id = requireTemuShopId(shopId, '查询尺码分类');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/class`, {
      query: { catId },
    });
  },
  getPublishSizeChartMeta: (shopId, catId, classId) => {
    const id = requireTemuShopId(shopId, '查询尺码元信息');
    const query = { catId };
    if (classId != null) query.classId = classId;
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/meta`, { query });
  },
  getPublishSizeChartSettings: (shopId, catId, classId) => {
    const id = requireTemuShopId(shopId, '查询尺码规则');
    const query = { catId };
    if (classId != null) query.classId = classId;
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/settings`, { query });
  },
  getPublishSizeChartFoot: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询鞋类脚长码表');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/foot`, {
      query: params,
    });
  },
  listPublishSizeChartTemplates: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询尺码模板');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/templates`, {
      query: params,
    });
  },
  createPublishSizeChart: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '创建尺码表');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts`, {
      method: 'POST',
      data,
      timeoutMs: 120000,
    });
  },
  createPublishSizeChartTemp: (shopId, businessId) => {
    const id = requireTemuShopId(shopId, '生成尺码 tempBusinessId');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/sizecharts/temp`, {
      method: 'POST',
      data: { businessId },
    });
  },
  listPublishAccessories: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询包装清单物品');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/accessories`, {
      query: params,
    });
  },
  getPublishCatsMandatory: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询类目必填信息');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/cats-mandatory`, {
      method: 'POST',
      data: {
        leafCatId: data.leafCatId ?? data.catId ?? data.categoryId,
        configItems: Array.isArray(data.configItems) ? data.configItems : [1],
        productPropertyReqs: data.productPropertyReqs,
        bindSiteIds: data.bindSiteIds,
      },
    });
  },
  uploadPublishInstructions: (shopId, file) => {
    const id = requireTemuShopId(shopId, '上传说明书');
    const formData = new FormData();
    formData.append('file', file);
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/instructions`, {
      method: 'POST',
      data: formData,
      timeoutMs: 180000,
    });
  },
  getPublishInstructionsLanguages: (shopId, pdfId) => {
    const id = requireTemuShopId(shopId, '查询说明书语种');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/${encodeURIComponent(pdfId)}/languages`,
    );
  },
  startPublishInstructionsTranslation: (shopId, pdfId, data = {}) => {
    const id = requireTemuShopId(shopId, '翻译说明书');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/${encodeURIComponent(pdfId)}/translate`,
      {
        method: 'POST',
        data: {
          selectedSourceLanguageCode: data.selectedSourceLanguageCode,
          targetLanguageCodeList: Array.isArray(data.targetLanguageCodeList)
            ? data.targetLanguageCodeList
            : [],
        },
        timeoutMs: 120000,
      },
    );
  },
  getPublishInstructionsTranslationResult: (shopId, translateId) => {
    const id = requireTemuShopId(shopId, '查询说明书翻译结果');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/publish/instructions/translations/${encodeURIComponent(translateId)}`,
    );
  },
  uploadPublishGoodsFile: (shopId, file) => {
    const id = requireTemuShopId(shopId, '上传货品文件');
    const formData = new FormData();
    formData.append('file', file);
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/files`, {
      method: 'POST',
      data: formData,
      timeoutMs: 120000,
    });
  },
  uploadProductVideo: (shopId, file, options = {}) => {
    const id = requireTemuShopId(shopId, '上传货品视频');
    const formData = new FormData();
    formData.append('file', file);
    const query = {};
    // 从视频空间选用：只转 Temu，不重复写入视频空间
    if (options.fromVideoSpace === true) {
      query.fromVideoSpace = true;
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/videos`, {
      method: 'POST',
      data: formData,
      query,
      timeoutMs: 180000,
    });
  },
  getPublishVideoResult: (shopId, vid) => {
    const id = requireTemuShopId(shopId, '查询视频转码');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/videos/result`, {
      query: { vid },
    });
  },
  getCachedReturnOrderDetail: (shopId, returnOrderNo) => {
    const id = requireTemuShopId(shopId, '读取退货详情');
    const no = String(returnOrderNo || '').trim();
    if (!no) throw new Error('缺少退货单号');
    return temuRequest(`/shops/${encodeURIComponent(id)}/return-orders/${encodeURIComponent(no)}`);
  },
  getCachedTemuOrder: (..._args) => Promise.resolve(null),
  getCertAutoBindJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询证书自动绑定');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/${encodeURIComponent(jid)}`);
  },
  getCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getCertificateLibrarySyncJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  /** @deprecated 请用 listCertificateTypes；兼容旧调用 */
  getCertificateSchemasDetail: (shopId) => {
    const id = requireTemuShopId(shopId, '查询证书类型');
    return temuRequest(`/shops/${encodeURIComponent(id)}/certificate-library/certificate-types`);
  },
  getCertificateSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询证书同步进度');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}`);
  },
  getComplianceSkcDetail: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询合规代理详情');
    const raw = data && typeof data === 'object' ? data : {};
    let bindStatus = raw.bindStatus;
    if (!bindStatus && Array.isArray(raw.reviewStatusList)) {
      const rs = raw.reviewStatusList.map((n) => Number(n));
      if (rs.includes(1) || rs.includes(3)) bindStatus = 'unbound';
      else if (rs.includes(2) || rs.includes(4)) bindStatus = 'bound';
    }
    const q = {
      page: Math.max(0, Number(raw.page ?? ((Number(raw.pageNum) || 1) - 1)) || 0),
      pageSize: Math.min(100, Math.max(1, Number(raw.pageSize ?? raw.size ?? 20) || 20)),
    };
    if (raw.keyword) q.keyword = String(raw.keyword);
    if (raw.gatewayRegion && String(raw.gatewayRegion).toUpperCase() !== 'ALL') {
      q.gatewayRegion = String(raw.gatewayRegion).toUpperCase();
    }
    if (bindStatus && bindStatus !== 'all') q.bindStatus = String(bindStatus);
    if (Array.isArray(raw.skcList) && raw.skcList.length) {
      const first = String(raw.skcList[0] || '').trim();
      if (first) q.keyword = first;
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/products`, { query: q });
  },
  getComplianceSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询合规同步进度');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}`);
  },
  getComplianceTodoSummary: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询合规待办');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/todos/summary`, { query: params });
  },
  getCustomAttributePermission: (..._args) => Promise.resolve(null),
  getGoodsSubmitJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询发品任务');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少发品任务 ID');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/publish/goods/submit-jobs/${encodeURIComponent(jid)}`,
    );
  },
  getMarketingEnrollJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询报名任务');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少报名任务 ID');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enroll-jobs/${encodeURIComponent(jid)}`,
    );
  },
  getLabelTemplates: (..._args) => Promise.resolve(null),
  getManualSyncQuota: (shopId, kind) => {
    const id = requireTemuShopId(shopId, '查询同步次数');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/manual-sync-quota?kind=${encodeURIComponent(String(kind || '').trim())}`,
    );
  },
  getModuleSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询模块同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/module-sync-jobs/${encodeURIComponent(jid)}`);
  },
  getPhotoAutoBindJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPhotoTemplate: (shopId, templateId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  getPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPreferences: (..._args) => Promise.resolve(null),
  getProductAttributeTemplate: (shopId, templateId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  /**
   * 只走平台货品详情（bg.glo/local goods detail），不读本地草稿/缓存。
   * 复色等场景直接按 店铺 + SPU 调平台详情接口；失败会抛出平台原因，不做本地兜底。
   */
  getProductDetailFromPlatform: async (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询平台货品详情');
    const spuName = String(data.spuName || data.spu_name || data.productId || '').trim();
    if (!spuName) throw new Error('缺少平台货品 ID，无法查询平台详情');
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/platform-detail`, {
      method: 'POST',
      data: {
        spuName,
        productId: /^\d+$/.test(spuName) ? Number(spuName) : undefined,
      },
      timeoutMs: 120000,
    });
  },
  /**
   * 编辑页回填：优先本地草稿 form（发品前/提交前会落草稿）。
   * 其次用发布记录 formSnapshot（自动规则刊登无草稿时靠它回显）。
   * 再调后端 product-for-edit：本地货品缓存 + 缺详情时拉平台 goods.detail 入库后回填。
   */
  getProductForEdit: async (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '编辑商品');
    const spuName = String(data.spuName || data.spu_name || data.productId || '').trim();
    const draftId = data.draftId != null && data.draftId !== '' ? String(data.draftId).trim() : '';
    const recordId = data.recordId != null && data.recordId !== '' ? String(data.recordId).trim() : '';
    const forceRefresh = Boolean(data.forceRefresh);

    const isWeakEditForm = (form) => {
      if (!form || typeof form !== 'object') return true;
      const skcList = Array.isArray(form.skcList) ? form.skcList : [];
      if (!skcList.length) return true;
      const attrs = form.productAttributes && typeof form.productAttributes === 'object'
        ? form.productAttributes
        : {};
      const hasAttrs = Object.keys(attrs).length > 0;
      const hasSizes = Array.isArray(form.selectedSizeIds) && form.selectedSizeIds.length > 0;
      const hasMaterial = Boolean(String(form.materialImgUrl || form.materialImage?.temuUrl || '').trim());
      const hasDetailImg = skcList.some((skc) => Array.isArray(skc?.detailImages) && skc.detailImages.length > 0);
      const path = String(form.categoryPathLabel || '').trim();
      const pathLooksLeafOnly = Boolean(path) && !path.includes('/') && !path.includes('>');
      return !hasAttrs || !hasSizes || !hasMaterial || !hasDetailImg || pathLooksLeafOnly;
    };

    let draft = null;
    if (draftId) {
      try {
        draft = await getTemuLocalPublishDraft(id, draftId);
      } catch {
        draft = null;
      }
    }
    if ((!draft || !draft.form) && spuName) {
      try {
        const listed = await listTemuLocalPublishDrafts(id, { page: 1, pageSize: 100 });
        const items = listed?.items || listed?.content || [];
        const hit = items.find((row) => {
          const rowSpu = String(row.platformSpuName || row.spuName || '').trim();
          return rowSpu && rowSpu === spuName;
        });
        if (hit?.id) {
          draft = await getTemuLocalPublishDraft(id, hit.id);
        }
      } catch {
        /* ignore list fallback */
      }
    }

    if (draft?.form && typeof draft.form === 'object' && !forceRefresh && !isWeakEditForm(draft.form)) {
      const form = draft.form;
      return {
        form,
        draftId: draft.id || draftId || '',
        recordId,
        spuName: String(form.spuName || spuName || '').trim(),
        title: form.title || '',
        titleEn: form.titleEn || '',
        supplierCode: form.supplierCode || '',
        brandCode: form.brandCode || '',
        categoryId: form.categoryId || '',
        productTypeId: form.productTypeId || '',
        categoryPathLabel: form.categoryPathLabel || '',
        description: form.description || '',
        originCountry: form.originCountry || '',
        skcItems: Array.isArray(draft.skcItems) ? draft.skcItems : [],
        source: 'draft',
      };
    }

    let record = null;
    if (recordId) {
      try {
        record = await getTemuLocalPublishRecord(id, recordId);
      } catch {
        record = null;
      }
    }
    if ((!record || !record.formSnapshot) && spuName) {
      try {
        record = await findTemuLocalPublishRecordBySpu(id, spuName);
      } catch {
        /* ignore */
      }
    }
    if (record?.formSnapshot && typeof record.formSnapshot === 'object'
      && !forceRefresh && !isWeakEditForm(record.formSnapshot)) {
      const form = {
        ...record.formSnapshot,
        spuName: String(record.formSnapshot.spuName || record.platformSpuName || spuName || '').trim(),
      };
      return {
        form,
        draftId: record.draftId || draftId || '',
        recordId: record.id || recordId,
        spuName: String(form.spuName || spuName || '').trim(),
        title: form.title || record.title || '',
        titleEn: form.titleEn || '',
        supplierCode: form.supplierCode || record.supplierCode || '',
        brandCode: form.brandCode || '',
        categoryId: form.categoryId || '',
        productTypeId: form.productTypeId || '',
        categoryPathLabel: form.categoryPathLabel || record.categoryLabel || '',
        description: form.description || '',
        originCountry: form.originCountry || '',
        skcItems: Array.isArray(record.skcItems) ? record.skcItems : [],
        source: 'record',
      };
    }

    if (spuName) {
      try {
        const backend = await temuRequest(`/shops/${encodeURIComponent(id)}/products/product-for-edit`, {
          method: 'POST',
          data: {
            spuName,
            productId: /^\d+$/.test(spuName) ? Number(spuName) : undefined,
            forceRefresh: forceRefresh || undefined,
          },
        });
        if (backend?.form && typeof backend.form === 'object') {
          return {
            ...backend,
            draftId: draft?.id || draftId || backend.draftId || '',
            recordId: record?.id || recordId || backend.recordId || '',
            skcItems: Array.isArray(backend.skcItems) ? backend.skcItems : [],
          };
        }
      } catch {
        /* fall through */
      }
    }

    if (spuName) {
      try {
        const {
          fetchTemuPublishFormFromLocalGoods,
        } = await import('@/temu-platform/utils/buildTemuPublishFormFromLocalGoods');
        const goodsForm = await fetchTemuPublishFormFromLocalGoods(id, spuName, { stripPlatformIds: false });
        if (goodsForm && typeof goodsForm === 'object') {
          return {
            form: goodsForm,
            draftId: draft?.id || draftId || '',
            recordId: record?.id || recordId || '',
            spuName: String(goodsForm.spuName || spuName || '').trim(),
            title: goodsForm.title || '',
            titleEn: goodsForm.titleEn || '',
            supplierCode: goodsForm.supplierCode || '',
            brandCode: goodsForm.brandCode || '',
            categoryId: goodsForm.categoryId || '',
            productTypeId: goodsForm.productTypeId || '',
            categoryPathLabel: goodsForm.categoryPathLabel || '',
            description: goodsForm.description || '',
            originCountry: goodsForm.originCountry || '',
            skcItems: [],
            platformLoadWarning: '已从本地货品列表回填基础信息；详情接口不可用时类目属性/尺码/素材图等需核对补充',
            source: 'local-goods-fallback',
          };
        }
      } catch {
        /* ignore goods fallback */
      }
    }

    if (draft?.form && typeof draft.form === 'object') {
      return {
        form: draft.form,
        draftId: draft.id || draftId || '',
        recordId,
        spuName: String(draft.form.spuName || spuName || '').trim(),
        title: draft.form.title || '',
        platformLoadWarning: '本地草稿字段不完整，且未能从平台补全详情',
        source: 'draft-partial',
      };
    }
    if (record?.formSnapshot && typeof record.formSnapshot === 'object') {
      return {
        form: record.formSnapshot,
        draftId: record.draftId || draftId || '',
        recordId: record.id || recordId,
        spuName: String(record.formSnapshot.spuName || spuName || '').trim(),
        title: record.formSnapshot.title || record.title || '',
        platformLoadWarning: '本地发布记录字段不完整，且未能从平台补全详情',
        source: 'record-partial',
      };
    }

    return {
      form: null,
      draftId: draftId || '',
      recordId,
      spuName,
      platformLoadWarning: draftId || recordId
        ? '关联本地草稿/发布记录不存在或已清理，且未能拉取平台详情'
        : '未找到本地草稿/发布记录，且本地货品缓存与平台详情均不可用；请先同步产品信息后再编辑',
    };
  },
  getProductListingStats: (shopIdOrParams) => {
    if (shopIdOrParams && typeof shopIdOrParams === 'object' && !Array.isArray(shopIdOrParams)) {
      const q = { ...shopIdOrParams };
      const id = requireTemuShopId(q.shopId, '查询统计');
      delete q.shopId;
      delete q.allOfType;
      return temuRequest(`/shops/${encodeURIComponent(id)}/products/listing-stats`, { query: q });
    }
    const id = requireTemuShopId(shopIdOrParams, '查询统计');
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/listing-stats`);
  },
  getProductSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '查询同步进度');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/sync-job/${encodeURIComponent(jid)}`);
  },
  getProductTemplate: (shopId, templateId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  checkListingImagePsoriasis: (shopId, urls) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/psoriasis-check`,
    { method: 'POST', data: { urls: Array.isArray(urls) ? urls : [] }, timeoutMs: 120000 },
  ),
  getProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getPublishDraft: (shopId, draftId) => getTemuLocalPublishDraft(shopId, draftId),
  /**
   * Temu 发品约束（bg.goods.add 币种等）。
   * 历史方法名沿用 getPublishFillInStandard，但绝不调用 Shein
   * /open-api/goods/query-publish-fill-in-standard。
   */
  getPublishFillInStandard: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询 Temu 发品规范');
    const catId = data.catId ?? data.category_id ?? data.categoryId ?? data.productTypeId;
    const query = {};
    if (catId != null && Number(catId) > 0) query.catId = Number(catId);
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/standard`, { query });
  },
  getPublishRecordSyncJob: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getRecommendRetailPendingCount: (..._args) => Promise.resolve({ total: 0 }),
  getReturnOrderDetails: (..._args) => Promise.resolve(null),
  getRrpTemplate: (..._args) => Promise.resolve(null),
  getShopBrands: (shopId) => {
    const id = requireTemuShopId(shopId, '查询品牌');
    return temuRequest(`/shops/${encodeURIComponent(id)}/brands`);
  },
  getShopCategories: (shopId) => {
    const id = requireTemuShopId(shopId, '查询类目');
    return temuRequest(`/shops/${encodeURIComponent(id)}/categories`);
  },
  getShopIps: (..._args) => Promise.resolve({ ips: [] }),
  getShopPublishPermission: (..._args) => Promise.resolve(null),
  getShopSiteList: (shopId) => {
    const id = requireTemuShopId(shopId, '查询售卖站点');
    return temuRequest(`/shops/${encodeURIComponent(id)}/site-list`);
  },
  getShopStoreInfo: (shopId) => {
    const id = requireTemuShopId(shopId, '查询店铺基本信息');
    return temuRequest(`/shops/${encodeURIComponent(id)}/store-info`);
  },
  getShopSyncStatus: (..._args) => Promise.resolve({ status: 'idle', done: true }),
  getShopWarehouses: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询发货仓');
    return temuRequest(`/shops/${encodeURIComponent(id)}/warehouses`, { query: params });
  },
  listShopFreightTemplates: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询运费模板');
    return temuRequest(`/shops/${encodeURIComponent(id)}/freight-templates`, { query: params });
  },
  getShopFreightTemplate: (shopId, templateId) => {
    const id = requireTemuShopId(shopId, '查询运费模板详情');
    const tid = String(templateId || '').trim();
    if (!tid) throw new Error('缺少运费模板 ID');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/freight-templates/${encodeURIComponent(tid)}`,
    );
  },
  batchDeleteShopFreightTemplates: (shopId, ids = []) => {
    const id = requireTemuShopId(shopId, '批量删除运费模板缓存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/freight-templates/batch-delete`, {
      method: 'POST',
      data: { ids },
    });
  },
  listShopModels: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询店铺模特');
    return temuRequest(`/shops/${encodeURIComponent(id)}/models`, { query: params });
  },
  createShopModel: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '新建模特');
    return temuRequest(`/shops/${encodeURIComponent(id)}/models`, {
      method: 'POST',
      data,
    });
  },
  updateShopModel: (shopId, modelId, data = {}) => {
    const id = requireTemuShopId(shopId, '编辑模特');
    const mid = String(modelId || '').trim();
    if (!mid) throw new Error('缺少模特 ID');
    return temuRequest(`/shops/${encodeURIComponent(id)}/models/${encodeURIComponent(mid)}`, {
      method: 'PUT',
      data,
    });
  },
  batchDeleteShopModels: (shopId, ids = [], options = {}) => {
    const id = requireTemuShopId(shopId, '批量删除模特');
    return temuRequest(`/shops/${encodeURIComponent(id)}/models/batch-delete`, {
      method: 'POST',
      data: {
        ids,
        // 默认仅删本地缓存（平台模特删除接口不可靠/未统一开放）
        platform: options.platform === true,
      },
    });
  },
  batchDeleteShopWarehouses: (shopId, ids = []) => {
    const id = requireTemuShopId(shopId, '批量删除发货仓缓存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/warehouses/batch-delete`, {
      method: 'POST',
      data: { ids },
    });
  },
  getSizeChartTemplate: (shopId, templateId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  getTemuOrderLogisticsTrack: (..._args) => Promise.resolve(null),
  getWebhook: eventId => temuRequest(`/webhooks/${encodeURIComponent(eventId)}`),
  listCertificateLibrary: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询证书库');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library`,
      { query: params },
    );
  },
  listCertificateSkcBinds: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  /** 店铺证书类型库（本地缓存，含 needUploadItems） */
  listCertificateTypes: (shopId) => {
    const id = requireTemuShopId(shopId, '查询证书类型');
    return temuRequest(`/shops/${encodeURIComponent(id)}/certificate-library/certificate-types`);
  },
  /** 按 productId 同步证书类型入库 */
  syncCertificateTypes: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步证书类型');
    return temuRequest(`/shops/${encodeURIComponent(id)}/certificate-library/certificate-types/sync`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
      timeoutMs: 180000,
    });
  },
  getCertificateTypeDetail: (shopId, certType) => {
    const id = requireTemuShopId(shopId, '查询证书类型详情');
    const type = Number(certType);
    if (!(type > 0)) throw new Error('缺少 certType');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/certificate-types/${encodeURIComponent(type)}`,
    );
  },
  /** 店铺证书库新建/编辑：只落本地，不提交平台 */
  saveCertificateLocalTemplate: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '保存本地证书模板');
    return temuRequest(`/shops/${encodeURIComponent(id)}/certificate-library/local-templates`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
      timeoutMs: 60000,
    });
  },
  getCertificateLocalTemplate: (shopId, templateId) => {
    const id = requireTemuShopId(shopId, '查询本地证书模板');
    const tid = Number(templateId);
    if (!(tid > 0)) throw new Error('缺少 templateId');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/local-templates/${encodeURIComponent(tid)}`,
    );
  },
  listCertificates: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询合规证书');
    const raw = params && typeof params === 'object' ? params : {};
    const q = {
      page: Math.max(0, Number(raw.page ?? ((Number(raw.pageNum) || 1) - 1)) || 0),
      pageSize: Math.min(100, Math.max(1, Number(raw.pageSize ?? raw.size ?? 20) || 20)),
    };
    if (raw.keyword) q.keyword = String(raw.keyword);
    if (raw.productId) q.productId = raw.productId;
    if (raw.skcName) q.skcName = String(raw.skcName);
    if (raw.reviewState != null && raw.reviewState !== '' && raw.reviewState !== 'all') q.reviewState = raw.reviewState;
    if (raw.status != null && raw.status !== '' && raw.status !== 'all') q.status = raw.status;
    if (raw.gatewayRegion && raw.gatewayRegion !== 'ALL') q.gatewayRegion = String(raw.gatewayRegion);
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/certificates`, { query: q });
  },
  listComplianceAgencies: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询合规代理');
    const raw = params && typeof params === 'object' ? params : {};
    const q = {
      page: Math.max(0, Number(raw.page ?? ((Number(raw.pageNum) || 1) - 1)) || 0),
      pageSize: Math.min(100, Math.max(1, Number(raw.pageSize ?? raw.size ?? 20) || 20)),
    };
    if (raw.keyword) q.keyword = String(raw.keyword);
    if (raw.agencyName) q.agencyName = String(raw.agencyName);
    if (raw.gatewayRegion && raw.gatewayRegion !== 'ALL') q.gatewayRegion = String(raw.gatewayRegion);
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/agencies`, { query: q });
  },
  listComplianceRequirements: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询合规要求');
    const productId = data?.productId ?? data?.spuId;
    if (!productId) return Promise.resolve({ items: [], content: [], total: 0, neverSynced: false });
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/metadata`, { query: { productId } });
  },
  /** GLOBAL bg.compliance.edit：按 productId + editReqList 编辑合规信息 */
  editComplianceInfo: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '编辑合规信息');
    const productId = data?.productId ?? data?.spuId;
    if (!productId) throw new Error('缺少 productId');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/edit`, {
      method: 'POST',
      data: {
        productId: Number(productId),
        editReqList: Array.isArray(data?.editReqList) ? data.editReqList : [],
      },
    });
  },
  listComplianceSkc: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询合规商品');
    const raw = params && typeof params === 'object' ? params : {};
    const q = {
      page: Math.max(0, Number(raw.page ?? ((Number(raw.pageNum) || 1) - 1)) || 0),
      pageSize: Math.min(100, Math.max(1, Number(raw.pageSize ?? raw.size ?? 20) || 20)),
    };
    if (raw.keyword) q.keyword = String(raw.keyword);
    if (raw.skcName) q.skcName = String(raw.skcName);
    if (raw.productId) q.productId = raw.productId;
    if (raw.bindStatus) q.bindStatus = String(raw.bindStatus);
    if (raw.infoStatus) q.infoStatus = String(raw.infoStatus);
    if (raw.certStatus) q.certStatus = String(raw.certStatus);
    if (raw.photoStatus) q.photoStatus = String(raw.photoStatus);
    if (raw.complianceType != null && raw.complianceType !== '') {
      q.complianceType = Number(raw.complianceType) || String(raw.complianceType);
    }
    if (raw.complianceInfo) q.complianceInfo = String(raw.complianceInfo);
    if (raw.gatewayRegion && raw.gatewayRegion !== 'ALL') q.gatewayRegion = String(raw.gatewayRegion);
    // photo 维度：优先 photos；也可走 products+photoStatus（商品合规聚合态）
    if (raw.dim === 'photo' || raw.mode === 'photo') {
      if (raw.status || raw.filter || raw.photoStatus) {
        q.status = String(raw.status || raw.filter || raw.photoStatus);
        q.photoStatus = q.status;
      }
      if (raw.useProductAgg === true) {
        if (q.status) q.photoStatus = q.status;
        delete q.status;
        return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/products`, { query: q });
      }
      return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/photos`, { query: q });
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/products`, { query: q });
  },
  listDiscussOrders: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询核价单');
    const raw = data && typeof data === 'object' ? data : {};
    const pageNum = Number(raw.pageNum ?? raw.page ?? 1);
    const pageSize = Number(raw.pageSize ?? raw.size ?? 20);
    const page = Number.isFinite(pageNum) && pageNum > 0 ? Math.floor(pageNum) - 1 : 0;
    const size = Number.isFinite(pageSize) && pageSize > 0 ? Math.min(100, Math.floor(pageSize)) : 20;
    const statusFilter = raw.discussStatus ?? raw.status;
    const keyword = Array.isArray(raw.discussSnList) && raw.discussSnList.length
      ? String(raw.discussSnList[0])
      : (raw.keyword ? String(raw.keyword) : undefined);
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('size', String(size));
    if (keyword) q.set('keyword', keyword);
    return temuRequest(`/shops/${encodeURIComponent(id)}/pricing/reviews?${q.toString()}`).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      const mapped = items.map((row) => {
        const statusRaw = row.discussStatus ?? row.orderStatus ?? row.status;
        const statusNum = statusRaw === '' || statusRaw == null ? null : Number(statusRaw);
        return {
          ...row,
          discussSn: row.discussSn || row.orderSn || row.orderId || '',
          orderSn: row.orderSn || row.orderId || row.discussSn || '',
          orderId: row.orderId || row.orderSn || row.discussSn || '',
          discussStatus: Number.isFinite(statusNum) ? statusNum : statusRaw,
          orderStatus: Number.isFinite(statusNum) ? statusNum : statusRaw,
          status: Number.isFinite(statusNum) ? statusNum : (row.status ?? statusRaw),
          supplyPrice: row.supplyPrice ?? row.declarePrice,
          suggestSupplyPrice: row.suggestSupplyPrice ?? row.suggestPrice,
          canBargain: row.canBargain ?? row.negotiable,
          productName: row.productName || row.productTitle || row.product_title || '',
          productTitle: row.productTitle || row.productName || row.product_title || '',
          mainPicUrl: row.mainPicUrl || row.imageUrl || row.mainImageUrl || '',
          imageUrl: row.imageUrl || row.mainPicUrl || row.mainImageUrl || '',
          supplierCode: row.supplierCode || row.extCode || '',
          skcId: row.skcId || null,
          spuId: row.spuId || row.productId || null,
          productId: row.productId || row.spuId || null,
          rawJson: row.rawJson ?? null,
          platformItem: row.platformItem ?? null,
        };
      });
      const demos = mergeTemuDiscussWithDemos(id, [], { statusFilter });
      const counts = buildDiscussStatusCounts(mergeTemuDiscussWithDemos(id, mapped, {}));
      const pageItems = page === 0 ? [...demos, ...mapped] : mapped;
      const apiTotal = Number(res?.totalElements ?? res?.total ?? mapped.length) || 0;
      return {
        ...res,
        items: pageItems,
        content: pageItems,
        list: pageItems,
        total: apiTotal + demos.length,
        totalElements: apiTotal + demos.length,
        count: apiTotal + demos.length,
        statusCounts: counts,
      };
    }).catch(() => {
      const demos = mergeTemuDiscussWithDemos(id, [], { statusFilter });
      const counts = buildDiscussStatusCounts(mergeTemuDiscussWithDemos(id, [], {}));
      return {
        items: demos,
        content: demos,
        list: demos,
        total: demos.length,
        totalElements: demos.length,
        count: demos.length,
        statusCounts: counts,
      };
    });
  },
  seedDiscussPendingDemo: (shopId) => {
    const id = requireTemuShopId(shopId, '加载核价单示例');
    return Promise.resolve(seedTemuDiscussDemos(id));
  },
  seedAdjustPriceDemo: (shopId) => {
    const id = requireTemuShopId(shopId, '加载调价单示例');
    return Promise.resolve(seedTemuAdjustDemos(id));
  },
  listEnvironmentalMaterials: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listFinanceBills: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询财务账单');
    return temuRequest(`/shops/${encodeURIComponent(id)}/finance/bills`, { query: params });
  },
  batchDeleteFinanceBills: (shopId, ids = []) => {
    const id = requireTemuShopId(shopId, '删除财务账单缓存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/finance/bills/batch-delete`, {
      method: 'POST',
      data: { ids: Array.isArray(ids) ? ids : [] },
    });
  },
  getCachedFinanceBillDetail: (shopId, billType, billNo) => {
    const id = requireTemuShopId(shopId, '查询财务账单详情');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/finance/bills/${encodeURIComponent(billType)}/${encodeURIComponent(billNo)}`,
    );
  },
  getFinanceBillDetail: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询财务账单详情');
    return temuRequest(`/shops/${encodeURIComponent(id)}/finance/bills/detail`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  syncFinanceBills: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步财务账单');
    return temuRequest(`/shops/${encodeURIComponent(id)}/finance/bills/sync`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  listPhotoTemplates: (shopId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { query: params },
  ),
  listProductAttributeTemplates: (shopId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { query: params },
  ),
  listProductMetaBySkc: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listProductTemplates: (shopId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { query: params },
  ),
  listProducts: (params = {}) => {
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    const id = requireTemuShopId(query.shopId, '查询商品');
    delete query.shopId;
    delete query.allOfType;
    return temuRequest(`/shops/${encodeURIComponent(id)}/products`, { query });
  },
  // SKU 明细按需加载：列表默认每个 SKC 只回 1 条预览行，弹窗打开时才拉全量
  listProductSkus: (shopId, productId, productSkcId) => {
    const id = requireTemuShopId(shopId, '查询商品SKU明细');
    const query = productSkcId != null && productSkcId !== '' ? { productSkcId } : undefined;
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/products/${encodeURIComponent(productId)}/skus`,
      { query },
    );
  },
  listPublishDrafts: (shopId, params = {}) => listTemuLocalPublishDrafts(shopId, params),
  listPublishRecords: async (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询发布记录');
    // 刊登任务回写的旧记录可能缺 productSkuId：打开列表时用本地货品 SKU 缓存补一次
    try {
      const listed = await listTemuLocalPublishRecords(id, { page: 1, pageSize: 100 });
      const rows = Array.isArray(listed?.items) ? listed.items : [];
      const incomplete = rows.filter((row) => {
        if (String(row?.source || '') !== 'listing-job') return false;
        const skuIds = Array.isArray(row?.productSkuIds) ? row.productSkuIds : [];
        if (skuIds.length) return false;
        const skcList = Array.isArray(row?.skcItems) ? row.skcItems : [];
        return !skcList.length || skcList.some((s) => !(Array.isArray(s?.productSkuIds) && s.productSkuIds.length));
      }).slice(0, 20);
      await Promise.all(incomplete.map(async (row) => {
        const productId = String(row?.platformSpuName || '').trim();
        if (!productId) return;
        try {
          const skuRes = await temuPlatformAPI.listProductSkus(id, productId);
          const items = Array.isArray(skuRes?.items) ? skuRes.items : [];
          if (!items.length) return;
          const bySkc = new Map();
          items.forEach((skuRow) => {
            const skcId = String(skuRow?.platformSkcId || skuRow?.productSkcId || skuRow?.skcId || '').trim();
            const skuId = Number(skuRow?.productSkuId || skuRow?.platformSkuId || skuRow?.skuId);
            if (!(Number.isSafeInteger(skuId) && skuId > 0)) return;
            const key = skcId || '_';
            if (!bySkc.has(key)) bySkc.set(key, { productSkcId: skcId || undefined, productSkuIdList: [] });
            const bucket = bySkc.get(key);
            if (!bucket.productSkuIdList.includes(skuId)) bucket.productSkuIdList.push(skuId);
          });
          const productSkcList = [...bySkc.values()].filter((s) => s.productSkuIdList.length);
          if (!productSkcList.length) return;
          await enrichTemuLocalPublishRecord(id, row.id, {
            platformRes: {
              result: { productId, productSkcList },
              productId,
              productSkcList,
            },
          });
        } catch {
          /* ignore single-record repair */
        }
      }));
    } catch {
      /* ignore repair sweep */
    }
    const reviewItems = [];
    try {
      let page = 0;
      const size = 100;
      for (let i = 0; i < 20; i += 1) {
        const res = await temuRequest(
          `/shops/${encodeURIComponent(id)}/pricing/reviews?page=${page}&size=${size}`,
        );
        const chunk = Array.isArray(res?.items) ? res.items : [];
        reviewItems.push(...chunk);
        const total = Number(res?.totalElements ?? res?.total ?? chunk.length);
        if (chunk.length < size || reviewItems.length >= total) break;
        page += 1;
      }
    } catch {
      /* 核价缓存读失败时仍返回本地记录 */
    }
    // 不自动打平台：仅合并上次手动「拉取生命周期」的本地缓存
    const lifecycleItems = getTemuPublishLifecycleCache(id);
    return listTemuLocalPublishRecords(id, params, { reviewItems, lifecycleItems });
  },
  createPublishRecord: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '写入发布记录');
    return createTemuLocalPublishRecord(id, data);
  },
  /** 货品生命周期（bg.glo.product.search） */
  searchProductLifecycle: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询货品生命周期');
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/lifecycle/search`, {
      method: 'POST',
      data: {
        productSkuIdList: Array.isArray(data.productSkuIdList) ? data.productSkuIdList : [],
        pageNum: data.pageNum || 1,
        pageSize: data.pageSize || 50,
      },
    });
  },
  /**
   * 发布记录页手动「拉取生命周期」：只传本店发布记录里的 productSkuId；
   * 无记录 / 无 SKU 时不打平台，写空缓存。
   */
  syncPublishRecordLifecycle: async (shopId) => {
    const id = requireTemuShopId(shopId, '拉取发布记录生命周期');
    const result = await fetchTemuPublishLifecycleItems(id);
    const items = Array.isArray(result?.items) ? result.items : [];
    setTemuPublishLifecycleCache(id, items);
    setTemuPublishLifecycleSyncedAt(id);
    return {
      ok: true,
      items,
      dataList: items,
      total: items.length,
      skipped: Boolean(result?.skipped),
      reason: result?.reason || null,
      queriedSkuCount: result?.queriedSkuCount || 0,
      queryMode: result?.queryMode || null,
    };
  },
  listReturnOrders: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询退货包裹');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    delete query._nonce;
    return temuRequest(`/shops/${encodeURIComponent(id)}/return-orders`, { query });
  },
  listSemiAftersales: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询半托售后');
    const raw = { ...(params && typeof params === 'object' ? params : {}) };
    delete raw._nonce;
    const query = {
      page: Number.isFinite(Number(raw.page)) ? Number(raw.page) : 0,
      size: Number(raw.size ?? raw.pageSize) || 20,
      gatewayRegion: raw.gatewayRegion,
      parentAfterSalesSn: raw.parentAfterSalesSn,
      parentOrderSn: raw.parentOrderSn,
      status: raw.status ?? raw.afterSalesStatus,
    };
    return temuRequest(`/shops/${encodeURIComponent(id)}/semi-aftersales`, { query });
  },
  getSemiAftersalesDetail: (shopId, parentAfterSalesSn, params = {}) => {
    const id = requireTemuShopId(shopId, '查询半托售后详情');
    const sn = String(parentAfterSalesSn ?? '').trim();
    if (!sn) throw new Error('缺少父售后单号');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/semi-aftersales/${encodeURIComponent(sn)}`,
      { query },
    );
  },
  agreeSemiAftersalesCancel: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同意取消');
    return temuSemiAftersalesAction(id, 'cancel-agree', data);
  },
  issueSemiAftersalesRefund: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '发起全额退款');
    return temuSemiAftersalesAction(id, 'refund-issue', data);
  },
  prepareSemiReturnLabel: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '准备退货标签');
    return temuSemiAftersalesAction(id, 'return-label-prepare', data);
  },
  getSemiAftersalesCarrier: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询承运商');
    return temuSemiAftersalesAction(id, 'carrier', data);
  },
  getSemiReturnAddress: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询退货地址');
    return temuSemiAftersalesAction(id, 'return-address', data);
  },
  getSemiAftersalesSignature: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询签名');
    return temuSemiAftersalesAction(id, 'signature', data);
  },
  uploadSemiReturnLabel: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '上传退货标签');
    return temuSemiAftersalesAction(id, 'upload-return-label', data);
  },
  batchDeleteSemiAftersales: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '批量删除半托售后缓存');
    const sns = Array.isArray(data?.parentAfterSalesSns)
      ? data.parentAfterSalesSns
      : Array.isArray(data?.returnOrderNos)
        ? data.returnOrderNos
        : [];
    return temuRequest(`/shops/${encodeURIComponent(id)}/semi-aftersales/batch-delete`, {
      method: 'DELETE',
      data: { parentAfterSalesSns: sns },
    });
  },
  getSalesOrderRegions: (shopId) => {
    const id = requireTemuShopId(shopId, '查询订单可用区');
    return temuRequest(`/shops/${encodeURIComponent(id)}/sales-orders/regions`);
  },
  deleteTemuOrders: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '删除订单缓存');
    const sns = Array.isArray(data?.parentOrderSns)
      ? data.parentOrderSns
      : Array.isArray(data?.orderNos)
        ? data.orderNos
        : [];
    return temuRequest(`/shops/${encodeURIComponent(id)}/sales-orders/batch-delete`, {
      method: 'DELETE',
      data: { parentOrderSns: sns },
    });
  },
  batchDeleteTemuOrders: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '批量删除订单缓存');
    const sns = Array.isArray(data?.parentOrderSns)
      ? data.parentOrderSns
      : Array.isArray(data?.orderNos)
        ? data.orderNos
        : [];
    return temuRequest(`/shops/${encodeURIComponent(id)}/sales-orders/batch-delete`, {
      method: 'DELETE',
      data: { parentOrderSns: sns },
    });
  },
  listQualityInspections: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询质检单');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return temuRequest(`/shops/${encodeURIComponent(id)}/quality-inspections`, { query });
  },
  getQualityInspectionDetail: (shopId, qcBillId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询质检详情');
    const billId = String(qcBillId ?? '').trim();
    if (!billId) throw new Error('缺少质检单 id');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/quality-inspections/${encodeURIComponent(billId)}`,
      { query },
    );
  },
  listReturnPackageLines: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询退供明细');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return temuRequest(`/shops/${encodeURIComponent(id)}/return-package-lines`, { query });
  },
  listRrpTemplates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  listSizeChartTemplates: (shopId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { query: params },
  ),
  listModelTemplates: (shopId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/models`,
    { query: params },
  ),
  getModelTemplate: (shopId, templateId, params = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/models/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  listFreightTemplates: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询运费模板');
    // 兼容旧调用：店铺同步缓存（无官方库）
    return temuRequest(`/shops/${encodeURIComponent(id)}/freight-templates`, { query: params });
  },
  getFreightTemplate: (..._args) => Promise.resolve(null),
  listWarehouseTemplates: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询发货仓');
    return temuRequest(`/shops/${encodeURIComponent(id)}/warehouses`, { query: params });
  },
  getWarehouseTemplate: (..._args) => Promise.resolve(null),
  listSkcLabels: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询合规标签');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/skc-labels`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  listTemuOrders: (shopId, params = {}) => {
    const id = requireTemuShopId(shopId, '查询销售订单');
    const raw = { ...(params && typeof params === 'object' ? params : {}) };
    delete raw._nonce;
    const pageRaw = Number(raw.page);
    const query = {
      page: Number.isFinite(pageRaw) ? Math.max(0, pageRaw >= 1 && raw.source === 'cached' ? pageRaw - 1 : pageRaw) : 0,
      size: Number(raw.size ?? raw.pageSize) || 20,
      gatewayRegion: raw.gatewayRegion,
      parentOrderSn: raw.parentOrderSn || raw.orderNo,
      status: raw.status ?? raw.orderStatus,
    };
    return temuRequest(`/shops/${encodeURIComponent(id)}/sales-orders`, { query });
  },
  getTemuOrderDetail: (shopId, parentOrderSn, params = {}) => {
    const id = requireTemuShopId(shopId, '查询销售订单详情');
    const sn = String(parentOrderSn ?? '').trim();
    if (!sn) throw new Error('缺少父订单号');
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/sales-orders/${encodeURIComponent(sn)}`,
      { query },
    );
  },
  listWebhooks: (params = {}) => temuRequest('/webhooks', { query: params }),
  markAlertsRead: (..._args) => Promise.resolve({ ok: true, stub: true }),
  markCertificateTemplateSynced: (..._args) => Promise.resolve({ ok: true, stub: true }),
  moveCertificateLibraryTemplates: (..._args) => Promise.resolve({ ok: true, stub: true }),
  movePhotoTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/move`,
    { method: 'POST', data },
  ),
  moveProductAttributeTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/move`,
    { method: 'POST', data },
  ),
  moveProductTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/move`,
    { method: 'POST', data },
  ),
  moveSizeChartTemplates: (shopId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/move`,
    { method: 'POST', data },
  ),
  partialEditProduct: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '编辑货品');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/goods/edit`, {
      method: 'POST',
      data,
      timeoutMs: 180000,
    });
  },
  patchShopAvatar: (..._args) => Promise.resolve({ ok: true, stub: true }),
  prepareCertificateTemplateSync: (..._args) => Promise.resolve({ ok: true, stub: true }),
  printLabels: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '打印标签');
    return temuRequest(`/shops/${encodeURIComponent(id)}/print/labels`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  processDiscussOrder: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '处理核价单');
    return temuRequest(`/shops/${encodeURIComponent(id)}/pricing/reviews/process`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  batchReviewAdjustPrice: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '确认调价单');
    return temuRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts/batch-review`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  editJitStock: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '编辑 JIT 库存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/stock/jit/edit`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  updateSemiStock: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '更新半托库存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/stock/quantity/update`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  addSemiRouteStock: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '新增半托路由库存');
    return temuRequest(`/shops/${encodeURIComponent(id)}/stock/route/add`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  publishOrEditProduct: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '发布货品');
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/goods`, {
      method: 'POST',
      data,
      timeoutMs: 180000,
    });
  },
  queryChangePriceReasons: (..._args) => Promise.resolve({ ok: true, stub: true }),
  queryRecommendRetailPriceRule: (..._args) => Promise.resolve({ ok: true, stub: true }),
  replayWebhook: eventId => temuRequest(`/webhooks/${encodeURIComponent(eventId)}/replay`, {
    method: 'POST',
  }),
  revokePublishRecord: async () => {
    throw new Error('TEMU 发布记录不再支持平台撤回；请到「价格处理 → 核价单」处理核价');
  },
  startPublishRecordSyncJob: async () => {
    throw new Error('TEMU 发布记录无需同步；状态随发品与核价单自动更新');
  },
  saveComplianceSkcAgency: (shopId, data) => {
    const id = requireTemuShopId(shopId, '绑定合规代理');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/skc-agency`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  saveGoodsCertificate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  saveProductSalePrice: (..._args) => Promise.resolve({ ok: true, stub: true }),
  searchGoodsCertificates: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  searchProduct: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询货品生命周期');
    return temuPlatformAPI.searchProductLifecycle(id, data);
  },
  searchRecommendRetailPrice: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '查询调价单');
    const raw = data && typeof data === 'object' ? data : {};
    const pageNum = Number(raw.page_num ?? raw.pageNum ?? raw.page ?? 1);
    const pageSize = Number(raw.page_size ?? raw.pageSize ?? raw.size ?? 20);
    const page = Number.isFinite(pageNum) && pageNum > 0 ? Math.floor(pageNum) - 1 : 0;
    const size = Number.isFinite(pageSize) && pageSize > 0 ? Math.min(100, Math.floor(pageSize)) : 20;
    const statusFilter = raw.status;
    const joinTokens = (list) => (Array.isArray(list) ? list : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .slice(0, 50)
      .join(',');
    const orderSn = joinTokens(raw.priceOrderSnList)
      || (raw.priceOrderSn != null && String(raw.priceOrderSn).trim() ? String(raw.priceOrderSn).trim() : '');
    const skc = joinTokens(raw.skcNameList)
      || (raw.skc != null && String(raw.skc).trim() ? String(raw.skc).trim() : '');
    const supplierCode = joinTokens(raw.supplierCodeList)
      || (raw.supplierCode != null && String(raw.supplierCode).trim() ? String(raw.supplierCode).trim() : '');
    const keyword = (!orderSn && !skc && !supplierCode)
      ? (raw.keyword ? String(raw.keyword) : undefined)
      : undefined;
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('size', String(size));
    if (keyword) q.set('keyword', keyword);
    if (orderSn) q.set('orderSn', orderSn);
    if (skc) q.set('skc', skc);
    if (supplierCode) q.set('supplierCode', supplierCode);
    if (statusFilter !== undefined && statusFilter !== null && statusFilter !== '') {
      q.set('status', String(statusFilter));
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/pricing/adjusts?${q.toString()}`).then((res) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      const mapped = items.map((row) => {
        const statusRaw = row.status ?? row.orderStatus;
        const statusNum = statusRaw === '' || statusRaw == null ? null : Number(statusRaw);
        return {
          ...row,
          skc_name: String(row.skc_name || row.skcName || row.skcId || row.orderSn || row.priceOrderSn || ''),
          skcName: row.skcName || row.skc_name || row.skcId || '',
          skcId: row.skcId || null,
          priceOrderSn: row.priceOrderSn || row.orderSn || '',
          orderSn: row.orderSn || row.priceOrderSn || '',
          productName: row.productName || row.product_title || row.productTitle || '',
          product_title: row.product_title || row.productTitle || row.productName || '',
          productTitle: row.productTitle || row.product_title || row.productName || '',
          imageUrl: row.imageUrl || row.mainPicUrl || row.mainImageUrl || '',
          mainPicUrl: row.mainPicUrl || row.imageUrl || row.mainImageUrl || '',
          supplierCode: row.supplierCode || row.extCode || '',
          status: Number.isFinite(statusNum) ? statusNum : statusRaw,
          skuInfoList: Array.isArray(row.skuInfoList) ? row.skuInfoList : [],
          siteNameList: Array.isArray(row.siteNameList) ? row.siteNameList : [],
          rawJson: row.rawJson ?? null,
          platformItem: row.platformItem ?? null,
        };
      });
      const demos = mergeTemuAdjustWithDemos(id, [], { statusFilter });
      const pageItems = page === 0 ? [...demos, ...mapped] : mapped;
      const apiTotal = Number(res?.totalElements ?? res?.total ?? mapped.length) || 0;
      return {
        ...res,
        items: pageItems,
        content: pageItems,
        list: pageItems,
        skc_rrp_list: pageItems,
        total: apiTotal + demos.length,
        totalElements: apiTotal + demos.length,
        count: apiTotal + demos.length,
      };
    }).catch(() => {
      const demos = mergeTemuAdjustWithDemos(id, [], { statusFilter });
      return {
        items: demos,
        content: demos,
        list: demos,
        skc_rrp_list: demos,
        total: demos.length,
        totalElements: demos.length,
        count: demos.length,
      };
    });
  },
  searchRecommendRetailPriceAudit: (..._args) => Promise.resolve({ items: [], content: [], total: 0, totalElements: 0, neverSynced: true }),
  /** 证书绑定联调：写入本地示例（覆盖审核状态 0–3） */
  seedCertificateDemo: (shopId) => {
    const id = requireTemuShopId(shopId, '加载证书绑定示例');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/certificates/seed-demo`, {
      method: 'POST',
    });
  },
  /** 实拍图联调：写入本地示例（覆盖全部识别状态） */
  seedPhotoComplianceDemo: (shopId) => {
    const id = requireTemuShopId(shopId, '加载实拍图示例');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/photos/seed-demo`, {
      method: 'POST',
    });
  },
  /** 合规信息联调：写入本地示例（覆盖全部合规信息状态） */
  seedComplianceInfoDemo: (shopId) => {
    const id = requireTemuShopId(shopId, '加载合规信息示例');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/products/seed-demo`, {
      method: 'POST',
    });
  },
  signReturnOrder: (..._args) => Promise.resolve({ ok: true, stub: true }),
  signReturnOrdersBatch: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startAgencyAutoBindJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '启动代理自动绑定');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/agency`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  startCertAutoBindJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '启动证书自动绑定');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/auto-bind-jobs/cert`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  startCertificateLibraryBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertificateLibrarySyncJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startCertificateSyncJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步证书');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs`, {
      method: 'POST',
      data: { scope: 'certificate', ...(data && typeof data === 'object' ? data : {}) },
    });
  },
  startComplianceSyncJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步合规');
    return temuRequest(`/shops/${encodeURIComponent(id)}/compliance/sync-jobs`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  startModuleSyncJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '启动模块同步');
    return temuRequest(`/shops/${encodeURIComponent(id)}/module-sync-jobs`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  syncReturnOrders: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步退货包裹');
    return temuRequest(`/shops/${encodeURIComponent(id)}/return-orders/sync`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  syncQualityInspections: (shopId) => {
    const id = requireTemuShopId(shopId, '同步质检单');
    return temuRequest(`/shops/${encodeURIComponent(id)}/quality-inspections/sync`, { method: 'POST' });
  },
  startPhotoAutoBindJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startPhotoTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  startProductSyncJob: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步商品');
    const raw = data && typeof data === 'object' ? data : {};
    const body = {};
    if (raw.maxPages != null && Number.isFinite(Number(raw.maxPages))) {
      body.maxPages = Math.max(1, Math.floor(Number(raw.maxPages)));
    }
    if (Array.isArray(raw.productSkcIds)) {
      const ids = raw.productSkcIds
        .map((v) => Number(v))
        .filter((n) => Number.isSafeInteger(n) && n > 0)
        .slice(0, 100);
      if (ids.length > 0) body.productSkcIds = ids;
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/sync-job`, {
      method: 'POST',
      data: body,
    });
  },
  syncShopProducts: (shopId) => {
    const id = requireTemuShopId(shopId, '同步商品');
    return temuRequest(`/shops/${encodeURIComponent(id)}/products/sync`, { method: 'POST' });
  },
  cancelProductSyncJob: (shopId, jobId) => {
    const id = requireTemuShopId(shopId, '终止商品同步');
    const jid = String(jobId || '').trim();
    if (!jid) throw new Error('缺少同步任务 ID');
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/products/sync-job/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  startProductTemplateBatchCopyJob: (..._args) => Promise.resolve({ ok: true, stub: true }),
  storePhotoTemplateOriginal: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return temuRequest(
      `/shops/${encodeURIComponent(shopId)}/material-templates/photos/store-original`,
      { method: 'POST', data: formData },
    );
  },
  /** @deprecated 请用 syncCertificateTypes */
  syncCertificateSchemas: (shopId, data = {}) => {
    const id = requireTemuShopId(shopId, '同步证书类型');
    return temuRequest(`/shops/${encodeURIComponent(id)}/certificate-library/certificate-types/sync`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
      timeoutMs: 180000,
    });
  },
  syncCertificates: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updatePhotoTemplate: (shopId, templateId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updatePreferences: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updateProductAttributeTemplate: (shopId, templateId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updateProductCost: async () => {
    throw new Error('TEMU 改供货价请到「价格处理 → 核价单 / 调价单」操作，不支持直接改价接口');
  },
  updateProductTemplate: (shopId, templateId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  updatePublishDraft: (shopId, draftId, data = {}) => updateTemuLocalPublishDraft(shopId, draftId, data),
  updateRrpTemplate: (..._args) => Promise.resolve({ ok: true, stub: true }),
  updateSizeChartTemplate: (shopId, templateId, data = {}) => temuRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  uploadDiscussFile: (..._args) => Promise.resolve({ ok: true, stub: true }),
  uploadGoodsCertificateFile: (..._args) => Promise.resolve({ ok: true, stub: true }),
  uploadProductPic: (shopId, file, options = {}) => {
    const id = requireTemuShopId(shopId, '上传货品图片');
    const formData = new FormData();
    formData.append('file', file);
    // Temu imageBizType：0=货品图（主图/细节/素材/色块），1=外包装图
    const bizType = options.imageBizType != null
      ? Number(options.imageBizType)
      : (options.forPackage === true ? 1 : 0);
    if (Number.isFinite(bizType)) {
      formData.append('imageBizType', String(bizType));
    }
    const query = {};
    // 从图片空间选用：只转 Temu，不重复写入图片空间
    if (options.fromImageSpace === true) {
      query.fromImageSpace = true;
    }
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/images`, {
      method: 'POST',
      data: formData,
      query,
      timeoutMs: 120000,
    });
  },
  /** 发品本地落图（印花图等）：写入用户图片空间，不调 Temu 转图 */
  storePublishImage: (shopId, file) => {
    const id = requireTemuShopId(shopId, '上传站内图片');
    const formData = new FormData();
    formData.append('file', file);
    return temuRequest(`/shops/${encodeURIComponent(id)}/publish/images/store`, {
      method: 'POST',
      data: formData,
      timeoutMs: 120000,
    });
  },
  uploadSkcLabelPicture: (shopId, file) => {
    const id = requireTemuShopId(shopId, '上传实拍图');
    const formData = new FormData();
    formData.append('file', file);
    return temuRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc-labels/upload`,
      { method: 'POST', data: formData, timeoutMs: 180000 },
    );
  },
};

function sheinRequest(path, options = {}) {
  const token = getSheinWorkspaceToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['X-Shein-Workspace-Token'] = token;
  }
  if (isSheinManualSyncWrite(path, options.method)) {
    const clickId = currentSheinManualSyncClickId();
    if (clickId && !headers[SHEIN_MANUAL_SYNC_CLICK_HEADER]) {
      headers[SHEIN_MANUAL_SYNC_CLICK_HEADER] = clickId;
    }
  }
  return request(`/shein${path}`, {
    ...options,
    headers,
    tokenType: 'funding',
  }).then((result) => {
    notifySheinManualSyncQuota(path, options.method, true);
    return result;
  }).catch((err) => {
    notifySheinManualSyncQuota(path, options.method, false, err);
    throw err;
  });
}

function isSheinManualSyncWrite(path, method) {
  if (String(method || 'GET').toUpperCase() !== 'POST') return false;
  const p = String(path || '');
  if (p.includes('/site-list/sync') || p.includes('/schemas/sync')) return false;
  return /\/shops\/\d+\/.+(?:\/sync|\/sync-jobs)$/.test(p);
}

function notifySheinManualSyncQuota(path, method, ok, err) {
  if (typeof window === 'undefined' || !isSheinManualSyncWrite(path, method)) return;
  if (ok || Number(err?.status) === 429 || err?.code === 'SHEIN_MANUAL_SYNC_LIMIT') {
    window.dispatchEvent(new CustomEvent('ipe:shein-manual-sync-quota', {
      detail: {
        kind: resolveSheinManualSyncQuotaKindFromPath(path),
        shopId: parseSheinShopIdFromPath(path),
      },
    }));
  }
}

/** 校验店铺 ID，避免拼出 /shops//xxx */
function requireSheinShopId(shopId, actionLabel = '操作') {
  const id = shopId == null ? '' : String(shopId).trim();
  if (!id || id === 'undefined' || id === 'null' || Number.isNaN(Number(id))) {
    throw new Error(`请先选择 SHEIN 店铺后再${actionLabel}`);
  }
  return id;
}

/** 校验同步 jobId，避免拼出 /sync-jobs/null */
function requireSheinSyncJobId(jobId, actionLabel = '查询同步进度') {
  const jid = jobId == null ? '' : String(jobId).trim();
  if (!jid || jid === 'undefined' || jid === 'null') {
    throw new Error(`同步任务 ID 无效，无法${actionLabel}`);
  }
  return jid;
}

/** 模块同步 Job 路径前缀（与后端 / 手动配额 buttonKey 对齐） */
function sheinModuleSyncJobBasePath(module) {
  switch (String(module || '').trim()) {
    case 'finance':
      return '/finance/bills';
    case 'orders':
      return '/aftersales/orders';
    case 'returnOrders':
      return '/aftersales/returns';
    case 'discuss':
      return '/discuss';
    case 'rrpSkc':
      return '/rrp';
    case 'rrpAudit':
      return '/rrp/audit';
    case 'environmental':
      return '/compliance/environmental-materials';
    case 'skcAgency':
      return '/compliance/skc-agency';
    case 'agency':
      return '/compliance/agencies';
    case 'shopProfile':
      return '/shop-profile';
    case 'marketing':
      return '/marketing';
    case 'marketing-enrolls':
      return '/marketing/enrolls';
    default:
      throw new Error(`不支持的模块同步类型：${module || '(空)'}`);
  }
}

/** 拉取最近一次同步收录的平台 JSON（统一入口，返回解析后的对象） */
async function fetchSheinSyncPlatformDump(shopId, kind) {
  const id = requireSheinShopId(shopId, '读取平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  await waitForIpeAuthReady();
  const path = `/shein/shops/${encodeURIComponent(id)}/sync-platform-dump?kind=${encodeURIComponent(dumpKind)}`;
  const { token, ipeCall } = pickAuthForRequest('funding', path);
  if (!token) {
    throw new Error('请先登录后再读取');
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (ipeCall) {
    headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
  }
  const workspaceToken = getSheinWorkspaceToken();
  if (workspaceToken) {
    headers['X-Shein-Workspace-Token'] = workspaceToken;
  }
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    headers,
    cache: 'no-store',
  });
  if (!response.ok) {
    let msg = '读取平台同步 JSON 失败';
    try {
      const errBody = await response.json();
      if (errBody?.message) msg = String(errBody.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { _raw: text };
  }
}

/** 下载最近一次同步收录的平台 JSON（统一入口） */
async function downloadSheinSyncPlatformDump(shopId, kind) {
  const id = requireSheinShopId(shopId, '下载平台同步 JSON');
  const dumpKind = String(kind || '').trim();
  if (!dumpKind) {
    throw new Error('缺少同步 dump 类型');
  }
  const data = await fetchSheinSyncPlatformDump(id, dumpKind);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shein-sync-${dumpKind}-shop-${id}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** SHEIN 平台工作区 API（须 step-up token） */
export const sheinPlatformAPI = {
  unlockSession: (password, shopType) => sheinRequest('/session/unlock', {
    method: 'POST',
    data: { password, shopType: shopType || undefined },
  }),
  getSessionStatus: () => sheinRequest('/session/status'),
  lockSession: () => sheinRequest('/session/lock', { method: 'POST' }),
  listShops: () => sheinRequest('/shops'),
  patchShopAvatar: (shopId, avatarUrl) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/avatar`,
    { method: 'PATCH', data: { avatarUrl: avatarUrl == null ? '' : String(avatarUrl) } },
  ),
  getShopContext: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/context`),
  getShopStoreInfo: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/store-info`),
  getShopPublishPermission: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-permission`,
    { query: params },
  ),
  getShopSiteList: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/site-list`,
    { query: params },
  ),
  syncShopSiteList: shopId => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/site-list/sync`,
    { method: 'POST' },
  ),
  syncShopProfile: (shopId, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/shop-profile/sync`,
    { method: 'POST', signal: options?.signal },
  ),
  getShopBrands: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/brands`),
  getShopIps: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/ips`),
  getShopCategories: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/categories`),
  getShopWarehouses: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/warehouses`),
  getShopSyncStatus: shopId => sheinRequest(`/shops/${encodeURIComponent(shopId)}/sync-status`),
  getManualSyncQuota: (shopId, kind) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/manual-sync-quota?kind=${encodeURIComponent(String(kind || '').trim())}`,
  ),
  getAttributeTemplate: (shopId, data = {}, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/attribute-template`,
    {
      method: 'POST',
      data,
      query: options.forceRefresh ? { forceRefresh: true } : undefined,
      timeoutMs: 90000,
    },
  ),
  /** 查询分类下哪些属性支持自定义属性值 */
  getCustomAttributePermission: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/custom-attribute-permission`,
    { method: 'POST', data },
  ),
  /** 添加自定义属性值（须先确认 has_permission=1） */
  addCustomAttributeValue: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/custom-attribute-value`,
    { method: 'POST', data },
  ),
  getPublishFillInStandard: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/publish-fill-in-standard`,
    { method: 'POST', data },
  ),
  /** 发品前：关联属性填写规则 */
  getAssociatedAttributeRules: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/associated-attribute-rules`,
    { method: 'POST', data },
  ),
  /** 发品前：商家 SKU（supplier_sku）店内唯一性校验 */
  checkSupplierSkuRepeated: (shopId, supplierSkuList = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/check-supplier-sku-repeated`,
    { method: 'POST', data: { supplierSkuList } },
  ),
  getSpuInfo: (shopId, data = {}) => {
    const spuName = String(data.spuName || data.spu_name || '').trim();
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/goods/spu-info`,
      {
        method: 'POST',
        data: {
          ...data,
          spuName,
          spu_name: spuName,
          language: data.language || 'zh-cn',
        },
      },
    );
  },
  /** 商品综合查询 */
  searchProduct: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/search-product`,
    { method: 'POST', data },
  ),
  /**
   * 编辑页拉详情填表：本地优先（草稿 form / 发布记录 / 分表 / 商品库）。
   * platformFallback=true：本地详情不够用时允许直接调平台 spu-info 兜底（复色等按 SPU 跨模块调用）。
   * 与 checkEditPermission（只校验能否编辑）是两个不同接口。
   * 本地过期时由用户在产品信息页点「同步更新」刷新库后再编辑。
   */
  getProductForEdit: (shopId, data = {}) => {
    const spuName = String(data.spuName || data.spu_name || '').trim();
    const recordId = data.recordId != null && data.recordId !== '' ? Number(data.recordId) : undefined;
    const draftId = data.draftId != null && data.draftId !== '' ? Number(data.draftId) : undefined;
    const body = { spuName, spu_name: spuName };
    if (Number.isFinite(recordId) && recordId > 0) body.recordId = recordId;
    if (Number.isFinite(draftId) && draftId > 0) body.draftId = draftId;
    if (data.platformFallback || data.forcePlatform || data.forceRefresh) body.platformFallback = true;
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/goods/product-for-edit`,
      {
        method: 'POST',
        data: body,
      },
    );
  },
  /** 确认商品是否可编辑（平台 check-edit-permission） */
  checkEditPermission: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/check-edit-permission`,
    {
      method: 'POST',
      data: {
        spuName: data.spuName || data.spu_name || '',
        spu_name: data.spuName || data.spu_name || '',
      },
    },
  ),
  /** 新建发品 → 入队/同步；返回 job 视图（含 response） */
  publishOrEditProduct: (shopId, data = {}, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/publish-or-edit`,
    { method: 'POST', data, query: params, timeoutMs: 180000 },
  ),
  /** 已审核商品部分编辑 → SHEIN partialEdit */
  partialEditProduct: (shopId, data = {}, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/partial-edit`,
    { method: 'POST', data, query: params, timeoutMs: 180000 },
  ),
  /** 更新成本价/供货价（半托管/全托管）→ update-cost */
  updateProductCost: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/update-cost`,
    { method: 'POST', data, timeoutMs: 120000 },
  ),
  /** 更新售价（自运营）→ product/price/save */
  saveProductSalePrice: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/product-price-save`,
    { method: 'POST', data, timeoutMs: 120000 },
  ),
  getGoodsSubmitJob: (shopId, jobId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/goods/submit-jobs/${encodeURIComponent(jobId)}`,
  ),
  listPublishDrafts: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts`,
    { query: params },
  ),
  getPublishDraft: (shopId, draftId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts/${encodeURIComponent(draftId)}`,
  ),

  listSizeChartTemplates: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { query: params },
  ),
  copySizeChartTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/copy`,
    { method: 'POST', data },
  ),
  batchCopySizeChartTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  moveSizeChartTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/move`,
    { method: 'POST', data },
  ),
  getSizeChartTemplate: (shopId, templateId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  createSizeChartTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts`,
    { method: 'POST', data },
  ),
  updateSizeChartTemplate: (shopId, templateId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  deleteSizeChartTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  batchDeleteSizeChartTemplates: (shopId, ids = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/size-charts/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  listProductAttributeTemplates: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { query: params },
  ),
  copyProductAttributeTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/copy`,
    { method: 'POST', data },
  ),
  batchCopyProductAttributeTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  moveProductAttributeTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/move`,
    { method: 'POST', data },
  ),
  getProductAttributeTemplate: (shopId, templateId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  createProductAttributeTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes`,
    { method: 'POST', data },
  ),
  updateProductAttributeTemplate: (shopId, templateId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  deleteProductAttributeTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  batchDeleteProductAttributeTemplates: (shopId, ids = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/product-attributes/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  listProductTemplates: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { query: params },
  ),
  copyProductTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyProductTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  startProductTemplateBatchCopyJob: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy-jobs`,
    { method: 'POST', data },
  ),
  getProductTemplateBatchCopyJob: (shopId, jobId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy-jobs/${encodeURIComponent(jobId)}`,
  ),
  cancelProductTemplateBatchCopyJob: (shopId, jobId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-copy-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  ),
  startPhotoTemplateBatchCopyJob: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy-jobs`,
    { method: 'POST', data },
  ),
  getPhotoTemplateBatchCopyJob: (shopId, jobId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy-jobs/${encodeURIComponent(jobId)}`,
  ),
  cancelPhotoTemplateBatchCopyJob: (shopId, jobId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  ),
  moveProductTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/move`,
    { method: 'POST', data },
  ),
  getProductTemplate: (shopId, templateId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  createProductTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products`,
    { method: 'POST', data },
  ),
  updateProductTemplate: (shopId, templateId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  deleteProductTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  batchDeleteProductTemplates: (shopId, ids = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/products/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  /** 批量比对素材模板版本（发品/刊登确认前） */
  checkMaterialTemplateVersions: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/version-check`,
    { method: 'POST', data },
  ),
  listPhotoTemplates: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { query: params },
  ),
  copyPhotoTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  batchCopyPhotoTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-copy`,
    { method: 'POST', data, timeoutMs: 180000 },
  ),
  movePhotoTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/move`,
    { method: 'POST', data },
  ),
  /** 实拍图库：按类目带出可选 SPU（编码 + 名称） */
  listPhotoTemplateSpuOptions: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/spu-options`,
    { query: params },
  ),
  getPhotoTemplate: (shopId, templateId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { query: params },
  ),
  createPhotoTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos`,
    { method: 'POST', data },
  ),
  updatePhotoTemplate: (shopId, templateId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  deletePhotoTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  batchDeletePhotoTemplates: (shopId, ids = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  /** 实拍图库：仅存 IPE 原图，不转平台 URL */
  storePhotoTemplateOriginal: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/material-templates/photos/store-original`,
      { method: 'POST', data: formData },
    );
  },
  /** 套用实拍图库时：原图 → 平台 imageUrl/imageMd5 */
  convertPhotoTemplateOriginal: (shopId, originalUrl) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/photos/convert-original`,
    { method: 'POST', data: { originalUrl } },
  ),
  listRrpTemplates: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp`,
    { query: params },
  ),
  getRrpTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp/${encodeURIComponent(templateId)}`,
  ),
  createRrpTemplate: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp`,
    { method: 'POST', data },
  ),
  updateRrpTemplate: (shopId, templateId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp/${encodeURIComponent(templateId)}`,
    { method: 'PUT', data },
  ),
  deleteRrpTemplate: (shopId, templateId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp/${encodeURIComponent(templateId)}`,
    { method: 'DELETE' },
  ),
  batchDeleteRrpTemplates: (shopId, ids = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/material-templates/rrp/batch-delete`,
    { method: 'POST', data: { ids } },
  ),
  createPublishDraft: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts`,
    { method: 'POST', data },
  ),
  /** 从产品信息复制一份到草稿箱 */
  copyProductToPublishDraft: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts/copy-from-product`,
    {
      method: 'POST',
      data: {
        spuName: data.spuName || data.spu_name || data.spuId || '',
        spu_name: data.spuName || data.spu_name || data.spuId || '',
      },
      timeoutMs: 120000,
    },
  ),
  /** 从发布记录复制一份到草稿箱（新发品草稿） */
  copyPublishRecordToDraft: (shopId, recordId, data = {}) => {
    const rid = Number(recordId);
    const spu = String(data.spuName || data.spu_name || data.platformSpuName || '').trim();
    // 优先走已有 copy-from-product（兼容未热更到新路由的后端）；有 recordId 时一并传上
    if (spu || (Number.isFinite(rid) && rid > 0)) {
      return sheinRequest(
        `/shops/${encodeURIComponent(shopId)}/publish-drafts/copy-from-product`,
        {
          method: 'POST',
          data: {
            recordId: Number.isFinite(rid) && rid > 0 ? rid : undefined,
            record_id: Number.isFinite(rid) && rid > 0 ? rid : undefined,
            spuName: spu,
            spu_name: spu,
          },
          timeoutMs: 120000,
        },
      );
    }
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/publish-records/${encodeURIComponent(recordId)}/copy-to-draft`,
      { method: 'POST', timeoutMs: 120000 },
    );
  },
  updatePublishDraft: (shopId, draftId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts/${encodeURIComponent(draftId)}`,
    { method: 'PUT', data },
  ),
  deletePublishDraft: (shopId, draftId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-drafts/${encodeURIComponent(draftId)}`,
    { method: 'DELETE' },
  ),
  /** 批量软删本地草稿（单次请求；不调 SHEIN 平台） */
  batchDeletePublishDrafts: (shopId, ids = []) => {
    const id = requireSheinShopId(shopId, '批量删除草稿');
    const draftIds = (Array.isArray(ids) ? ids : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/publish-drafts/batch-delete`,
      { method: 'POST', data: { ids: draftIds }, timeoutMs: 120000 },
    );
  },
  listPublishRecords: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records`,
    {
      query: {
        status: params.status || undefined,
        idType: params.idType || undefined,
        keywords: params.keywords || undefined,
        title: params.title || undefined,
        supplierCode: params.supplierCode || undefined,
        keyword: params.keyword || undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
    },
  ),
  getPublishRecord: (shopId, recordId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records/${encodeURIComponent(recordId)}`,
  ),
  refreshPublishRecordStatus: (shopId, recordId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records/${encodeURIComponent(recordId)}/refresh-status`,
    { method: 'POST' },
  ),
  /** 批量刷新发布记录审核态（平台 query-document-state，每批≤10 SPU；不同步商品全量）— 同步阻塞，兼容保留 */
  refreshPublishRecordStatuses: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records/refresh-statuses`,
    { method: 'POST', timeoutMs: 180000 },
  ),
  /** 启动异步发布记录审核态同步，返回 jobId；轮询 getPublishRecordSyncJob */
  startPublishRecordSyncJob: (shopId) => {
    const id = requireSheinShopId(shopId, '同步发布记录审核态');
    return sheinRequest(`/shops/${encodeURIComponent(id)}/publish-records/sync-jobs`, {
      method: 'POST',
    });
  },
  getPublishRecordSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询发布记录同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询发布记录同步进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/publish-records/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelPublishRecordSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止发布记录同步');
    const jid = requireSheinSyncJobId(jobId, '终止发布记录同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/publish-records/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  /** 下载最近一次发布记录同步收录的平台 query-document-state 原始 JSON */
  downloadPublishRecordSyncPlatformDump: (shopId) => downloadSheinSyncPlatformDump(shopId, 'publish-record'),
  revokePublishRecord: (shopId, recordId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records/${encodeURIComponent(recordId)}/revoke`,
    { method: 'POST' },
  ),
  deletePublishRecord: (shopId, recordId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/publish-records/${encodeURIComponent(recordId)}`,
    { method: 'DELETE' },
  ),
  /** 批量软删本地发布记录（单次请求；不调 SHEIN 平台） */
  batchDeletePublishRecords: (shopId, ids = []) => {
    const id = requireSheinShopId(shopId, '批量删除发布记录');
    const recordIds = (Array.isArray(ids) ? ids : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/publish-records/batch-delete`,
      { method: 'POST', data: { ids: recordIds }, timeoutMs: 120000 },
    );
  },
  uploadProductPic: (shopId, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const imageType = Number(options.imageType) > 0 ? Number(options.imageType) : 1;
    formData.append('imageType', String(imageType));
    formData.append('image_type', String(imageType));
    const query = { imageType };
    // 刊登转图 / 图片空间再转：只上 SHEIN，不重复写 OSS/图片空间
    if (options.fromImageSpace === true) {
      query.fromImageSpace = true;
    }
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/goods/upload-pic`,
      { method: 'POST', data: formData, query, timeoutMs: 120000 },
    );
  },
  uploadProductVideo: (shopId, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const query = {};
    if (options.fromVideoSpace === true) query.fromVideoSpace = true;
    return sheinRequest(`/shops/${encodeURIComponent(shopId)}/goods/upload-video`, {
      method: 'POST', data: formData, query, timeoutMs: 180000,
    });
  },
  /** SHEIN transform-pic：只传公网图链 + image_type，不读本地字节 */
  transformProductPic: (shopId, options = {}) => {
    const originalUrl = String(options.originalUrl || options.url || '').trim();
    const imageType = Number(options.imageType) > 0 ? Number(options.imageType) : 1;
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/goods/transform-pic`,
      {
        method: 'POST',
        data: {
          original_url: originalUrl,
          originalUrl,
          image_type: imageType,
          imageType,
        },
        timeoutMs: 120000,
      },
    );
  },
  listProducts: (params = {}) => {
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    if (query.allOfType || String(query.shopId || '') === '__ALL__') {
      delete query.shopId;
      query.allOfType = true;
    }
    return sheinRequest('/products', { query });
  },
  /**
   * 合规等列表补全：按 SKC 查本地商品摘要（标题/主图/类目），不打 searchProduct。
   * 单次最多 500 个 SKC。
   */
  listProductMetaBySkc: (shopId, skcNames = []) => {
    const id = requireSheinShopId(shopId, '查询商品摘要');
    const names = (Array.isArray(skcNames) ? skcNames : [])
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/meta-by-skc`,
      { method: 'POST', data: { skcNames: names } },
    );
  },
  getProductListingStats: (shopIdOrParams, maybeParams) => {
    // 兼容：(shopId, params) 或 ({ allOfType, shopType, ...filters })
    if (shopIdOrParams && typeof shopIdOrParams === 'object' && !Array.isArray(shopIdOrParams)) {
      const q = { ...shopIdOrParams };
      if (q.allOfType || !q.shopId) {
        delete q.shopId;
        q.allOfType = true;
        return sheinRequest('/products/stats', { query: q });
      }
      const id = requireSheinShopId(q.shopId, '查询统计');
      delete q.shopId;
      delete q.allOfType;
      return sheinRequest(`/shops/${encodeURIComponent(id)}/products/stats`, { query: q });
    }
    const id = requireSheinShopId(shopIdOrParams, '查询统计');
    return sheinRequest(`/shops/${encodeURIComponent(id)}/products/stats`, {
      query: maybeParams && typeof maybeParams === 'object' ? maybeParams : {},
    });
  },
  syncShopProducts: shopId => {
    const id = requireSheinShopId(shopId, '同步商品');
    return sheinRequest(`/shops/${encodeURIComponent(id)}/products/sync`, {
      method: 'POST',
    });
  },
  startProductSyncJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '同步商品');
    return sheinRequest(`/shops/${encodeURIComponent(id)}/products/sync-jobs`, {
      method: 'POST',
      data: data && typeof data === 'object' ? data : {},
    });
  },
  getProductSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询同步进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelProductSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止商品同步');
    const jid = requireSheinSyncJobId(jobId, '终止商品同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  /** 下载最近一次商品同步收录的平台 searchProduct 原始 JSON */
  downloadProductSyncPlatformDump: (shopId) => downloadSheinSyncPlatformDump(shopId, 'product'),
  /**
   * 下载最近一次「同步更新」收录的平台 JSON（统一入口）。
   * kind：product / product-spu-info / publish-record / compliance-skc / certificate / certificate-library /
   * compliance-agency / environmental / finance / return-orders / shop-profile / discuss / rrp-skc …
   */
  downloadSyncPlatformDump: (shopId, kind) => downloadSheinSyncPlatformDump(shopId, kind),
  /** 读取最近一次同步 dump（不触发浏览器下载） */
  fetchSyncPlatformDump: (shopId, kind) => fetchSheinSyncPlatformDump(shopId, kind),
  syncProductPrice: productId => sheinRequest(`/products/${encodeURIComponent(productId)}/sync-price`, {
    method: 'POST',
  }),
  batchSyncProductPrice: (shopId, productIds = []) => {
    const id = requireSheinShopId(shopId, '批量核价');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/sync-price`,
      { method: 'POST', data: { productIds } },
    );
  },
  listCertificates: (shopId, params = {}) => {
    const id = requireSheinShopId(shopId, '查询证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates`,
      { query: params },
    );
  },
  batchDeleteCertificates: (shopId, certificateIds = []) => {
    const id = requireSheinShopId(shopId, '批量删除证书缓存');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/batch-delete`,
      { method: 'POST', data: { certificateIds } },
    );
  },
  batchDeleteProducts: (shopId, productIdsOrPayload = []) => {
    const id = requireSheinShopId(shopId, '批量删除商品缓存');
    let data;
    if (productIdsOrPayload && typeof productIdsOrPayload === 'object' && !Array.isArray(productIdsOrPayload)) {
      const spuNames = (Array.isArray(productIdsOrPayload.spuNames) ? productIdsOrPayload.spuNames : [])
        .map((s) => String(s || '').trim())
        .filter((s) => s && s !== '—');
      const productIds = (Array.isArray(productIdsOrPayload.productIds) ? productIdsOrPayload.productIds : [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0);
      data = { spuNames, productIds, ids: productIds };
    } else {
      const ids = (Array.isArray(productIdsOrPayload) ? productIdsOrPayload : [])
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && n > 0);
      data = { productIds: ids, ids };
    }
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/batch-delete`,
      { method: 'POST', data },
    );
  },
  /**
   * 商品上下架（平台 modify-skc-shelf）。
   * @param {{ shelfState?: 1|2, action?: 'on'|'off', spuNames: string[], siteList?: string[] }} data
   */
  modifySkcShelf: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '商品上下架');
    const spuNames = (Array.isArray(data.spuNames) ? data.spuNames : [])
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—');
    const siteList = (Array.isArray(data.siteList) ? data.siteList : [])
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    const payload = {
      spuNames,
      shelfState: data.shelfState,
      action: data.action,
    };
    if (siteList.length) payload.siteList = siteList;
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/products/modify-skc-shelf`,
      { method: 'POST', data: payload, timeoutMs: 180000 },
    );
  },
  clearCertificateCache: (shopId, { legacyOnly = true } = {}) => {
    const id = requireSheinShopId(shopId, '清理证书缓存');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/clear-cache`,
      { method: 'POST', data: { legacyOnly } },
    );
  },
  listCertificateLibrary: (shopId, params = {}) => {
    const id = requireSheinShopId(shopId, '查询证书库');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library`,
      { query: params },
    );
  },
  copyOfficialCertificateLibrary: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '复制官方证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official`,
      { method: 'POST', data, timeoutMs: 180000 },
    );
  },
  batchCopyOfficialCertificateLibrary: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '批量复制官方证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official/batch-copy`,
      { method: 'POST', data, timeoutMs: 180000 },
    );
  },
  startCertificateLibraryBatchCopyJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '批量复制官方证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official/batch-copy-jobs`,
      { method: 'POST', data },
    );
  },
  getCertificateLibraryBatchCopyJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询证书库复制进度');
    const jid = requireSheinSyncJobId(jobId, '查询证书库复制进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official/batch-copy-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelCertificateLibraryBatchCopyJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止证书库复制');
    const jid = requireSheinSyncJobId(jobId, '终止证书库复制');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/copy-official/batch-copy-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  moveCertificateLibraryTemplates: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '批量移动本地证书模板');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/local-templates/move`,
      { method: 'POST', data },
    );
  },
  prepareCertificateTemplateSync: (shopId, templateId) => {
    const id = requireSheinShopId(shopId, '同步证书到后台');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/local-templates/${encodeURIComponent(templateId)}/prepare-sync`,
      { method: 'POST', timeoutMs: 180000 },
    );
  },
  markCertificateTemplateSynced: (shopId, templateId, data = {}) => {
    const id = requireSheinShopId(shopId, '标记证书已同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/local-templates/${encodeURIComponent(templateId)}/synced`,
      { method: 'POST', data },
    );
  },
  /** 证书已绑定 SKC（弹窗左侧） */
  listCertificateSkcBinds: (shopId, params = {}) => {
    const id = requireSheinShopId(shopId, '查询证书绑定 SKC');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/skc-binds`,
      { query: params },
    );
  },
  /** 确认绑定/解绑：新增走平台 bind；取消勾选仅删本地一对多表 */
  applyCertificateSkcBinds: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '绑定证书 SKC');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/skc-binds`,
      { method: 'POST', data },
    );
  },
  startCertificateLibrarySyncJob: (shopId) => {
    const id = requireSheinShopId(shopId, '同步平台证书库');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/sync-jobs`,
      { method: 'POST' },
    );
  },
  getCertificateLibrarySyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询证书库同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询证书库同步进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelCertificateLibrarySyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止证书库同步');
    const jid = requireSheinSyncJobId(jobId, '终止证书库同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  uploadCertificateLibrary: (shopId, file, meta = {}) => {
    const id = requireSheinShopId(shopId, '上传证书库文件');
    const formData = new FormData();
    formData.append('file', file);
    if (meta.title) formData.append('title', meta.title);
    if (meta.certificateTypeId != null && meta.certificateTypeId !== '') {
      formData.append('certificateTypeId', String(meta.certificateTypeId));
    }
    if (meta.certificateTypeName) formData.append('certificateTypeName', meta.certificateTypeName);
    if (meta.remark) formData.append('remark', meta.remark);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/upload`,
      { method: 'POST', data: formData },
    );
  },
  updateCertificateLibrary: (shopId, libraryId, data = {}) => {
    const id = requireSheinShopId(shopId, '更新证书库');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/${encodeURIComponent(libraryId)}`,
      { method: 'PUT', data },
    );
  },
  deleteCertificateLibrary: (shopId, libraryId) => {
    const id = requireSheinShopId(shopId, '删除证书库');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/${encodeURIComponent(libraryId)}`,
      { method: 'DELETE' },
    );
  },
  batchDeleteCertificateLibrary: (shopId, ids = []) => {
    const id = requireSheinShopId(shopId, '批量删除证书库');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/batch-delete`,
      { method: 'POST', data: { ids } },
    );
  },
  pushCertificateLibraryToShein: (shopId, libraryId) => {
    const id = requireSheinShopId(shopId, '推送证书到平台');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificate-library/${encodeURIComponent(libraryId)}/push`,
      { method: 'POST' },
    );
  },
  downloadCertificateLibrary: async (shopId, libraryId, fileName = 'certificate') => {
    const id = requireSheinShopId(shopId, '下载证书');
    await waitForIpeAuthReady();
    const path = `/shein/shops/${encodeURIComponent(id)}/certificate-library/${encodeURIComponent(libraryId)}/download`;
    const { token, ipeCall } = pickAuthForRequest('funding', path);
    if (!token) {
      throw new Error('请先登录后再下载');
    }
    const headers = { Authorization: `Bearer ${token}` };
    if (ipeCall) {
      headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
    }
    const workspaceToken = getSheinWorkspaceToken();
    if (workspaceToken) {
      headers['X-Shein-Workspace-Token'] = workspaceToken;
    }
    const response = await fetch(buildUrl(path), {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error('下载证书失败');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'certificate';
    a.click();
    URL.revokeObjectURL(url);
  },
  syncCertificates: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '同步证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/sync`,
      { method: 'POST', data, timeoutMs: 300000 },
    );
  },
  /** 合规资质联调：写入本地示例证书缓存 */
  seedCertificateDemo: (shopId) => {
    const id = requireSheinShopId(shopId, '加载合规资质示例');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/seed-demo`,
      { method: 'POST' },
    );
  },
  startCertificateSyncJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '同步证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/sync-jobs`,
      { method: 'POST', data },
    );
  },
  getCertificateSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询证书同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询证书同步进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelCertificateSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止证书同步');
    const jid = requireSheinSyncJobId(jobId, '终止证书同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  listCertificateTypes: shopId => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/types`,
  ),
  queryCertificateRule: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/rules`,
    { method: 'POST', data },
  ),
  saveCertificate: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates`,
    { method: 'POST', data },
  ),
  saveProductCertificatePool: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/pools/product`,
    { method: 'POST', data },
  ),
  saveSupplierCertificatePool: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/pools/supplier`,
    { method: 'POST', data },
  ),
  bindCertificatePoolSkc: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/pools/skc-bind`,
    { method: 'POST', data },
  ),
  uploadCertificateFile: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/certificates/upload-file`,
      { method: 'POST', data: formData },
    );
  },
  uploadCertificateAttachment: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/certificates/upload-attachment`,
      { method: 'POST', data: formData },
    );
  },
  /** 资质证书填写规则（新方案） */
  getCertificateSchemasDetail: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/schemas/detail`,
    { method: 'POST', data },
  ),
  /** 从平台同步资质证书填写规则并写入本地 */
  syncCertificateSchemas: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/schemas/sync`,
    { method: 'POST' },
  ),
  /** 代下载证书填写模板（避免浏览器直开平台私有 OSS） */
  downloadCertificateSchemaFileModel: async (shopId, { certificateTypeId, certificateTypeCode, fileName } = {}) => {
    const id = requireSheinShopId(shopId, '下载填写模板');
    const params = new URLSearchParams();
    if (certificateTypeId != null && String(certificateTypeId).trim() !== '') {
      params.set('certificateTypeId', String(certificateTypeId).trim());
    }
    if (certificateTypeCode) {
      params.set('certificateTypeCode', String(certificateTypeCode).trim());
    }
    if (!params.has('certificateTypeId') && !params.has('certificateTypeCode')) {
      throw new Error('请先选择证书类型');
    }
    await waitForIpeAuthReady();
    const path = `/shein/shops/${encodeURIComponent(id)}/certificates/schemas/file-model?${params.toString()}`;
    const { token, ipeCall } = pickAuthForRequest('funding', path);
    if (!token) {
      throw new Error('请先登录后再下载');
    }
    const headers = { Authorization: `Bearer ${token}` };
    if (ipeCall) {
      headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
    }
    const workspaceToken = getSheinWorkspaceToken();
    if (workspaceToken) {
      headers['X-Shein-Workspace-Token'] = workspaceToken;
    }
    const response = await fetch(buildUrl(path), {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      let msg = '下载填写模板失败';
      try {
        const errBody = await response.json();
        if (errBody?.message) msg = String(errBody.message);
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || '证书填写模板.pdf';
    a.click();
    URL.revokeObjectURL(url);
  },
  /** 资质证书文件上传（新方案，含 MD5） */
  uploadGoodsCertificateFile: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/certificates/goods-files/upload`,
      { method: 'POST', data: formData },
    );
  },
  saveGoodsCertificate: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/goods-certificates`,
    { method: 'POST', data },
  ),
  bindGoodsCertificates: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/goods-certificates/bind`,
    { method: 'POST', data },
  ),
  searchGoodsCertificates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/certificates/goods-certificates/search`,
    { method: 'POST', data },
  ),
  listComplianceAgencies: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/agencies`,
    { query: { source: 'cached', ...params } },
  ),
  syncComplianceAgencies: (shopId, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/agencies/sync`,
    { method: 'POST', signal: options?.signal },
  ),
  listComplianceRequirements: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/requirements`,
    { method: 'POST', data },
  ),
  listComplianceSkc: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc`,
    { query: params },
  ),
  batchDeleteComplianceSkc: (shopId, { ids = [], skcNames = [] } = {}) => {
    const id = requireSheinShopId(shopId, '批量删除合规缓存');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/batch-delete`,
      {
        method: 'POST',
        data: {
          ids: (Array.isArray(ids) ? ids : []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0),
          skcNames: (Array.isArray(skcNames) ? skcNames : []).map((s) => String(s || '').trim()).filter(Boolean),
        },
      },
    );
  },
  syncComplianceSkc: shopId => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc/sync`,
    { method: 'POST' },
  ),
  /**
   * 立即自动补传（同步，兼容旧调用）。大批量请用 photo-auto-bind-jobs。
   */
  runPhotoAutoBind: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '自动补传实拍图');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/photo-auto-bind`,
      { method: 'POST', data, timeoutMs: 180000 },
    );
  },
  startPhotoAutoBindJob: (shopId) => {
    const id = requireSheinShopId(shopId, '自动绑定实拍图');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/photo-auto-bind-jobs`,
      { method: 'POST' },
    );
  },
  getPhotoAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询实拍图自动绑定进度');
    const jid = requireSheinSyncJobId(jobId, '查询实拍图自动绑定进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/photo-auto-bind-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelPhotoAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止实拍图自动绑定');
    const jid = requireSheinSyncJobId(jobId, '终止实拍图自动绑定');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/photo-auto-bind-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  startAgencyAutoBindJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '自动绑定代理');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc-agency/auto-bind-jobs`,
      { method: 'POST', data },
    );
  },
  getAgencyAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询代理自动绑定进度');
    const jid = requireSheinSyncJobId(jobId, '查询代理自动绑定进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc-agency/auto-bind-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelAgencyAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止代理自动绑定');
    const jid = requireSheinSyncJobId(jobId, '终止代理自动绑定');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc-agency/auto-bind-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  startCertAutoBindJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '自动绑定证书');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/cert-auto-bind-jobs`,
      { method: 'POST', data },
    );
  },
  getCertAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询证书自动绑定进度');
    const jid = requireSheinSyncJobId(jobId, '查询证书自动绑定进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/cert-auto-bind-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelCertAutoBindJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止证书自动绑定');
    const jid = requireSheinSyncJobId(jobId, '终止证书自动绑定');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/certificates/cert-auto-bind-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  /** 下载最近一次实拍图同步收录的平台 skc-label-list JSON */
  downloadComplianceSkcSyncPlatformDump: (shopId) => downloadSheinSyncPlatformDump(shopId, 'compliance-skc'),
  /** 实拍图联调：写入本地示例 SKC/商品缓存 */
  seedPhotoComplianceDemo: (shopId) => {
    const id = requireSheinShopId(shopId, '加载实拍图示例');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/skc/seed-photo-demo`,
      { method: 'POST' },
    );
  },
  startComplianceSyncJob: (shopId) => {
    const id = requireSheinShopId(shopId, '同步合规');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/sync-jobs`,
      { method: 'POST' },
    );
  },
  getComplianceSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询合规同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询合规同步进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelComplianceSyncJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '终止合规同步');
    const jid = requireSheinSyncJobId(jobId, '终止合规同步');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/compliance/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  getComplianceSkcDetail: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-detail`,
    { method: 'POST', data: { source: 'cached', ...data } },
  ),
  syncComplianceSkcAgency: (shopId, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-agency/sync`,
    { method: 'POST', data: {}, signal: options?.signal },
  ),
  batchDeleteComplianceSkcAgency: (shopId, { skcNames = [], ids = [] } = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-agency/batch-delete`,
    {
      method: 'POST',
      data: {
        skcNames: (Array.isArray(skcNames) ? skcNames : [])
          .map((s) => String(s || '').trim())
          .filter(Boolean),
        ids: (Array.isArray(ids) ? ids : []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0),
      },
    },
  ),
  saveComplianceSkcAgency: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-agency`,
    { method: 'POST', data },
  ),
  listComplianceWarningRules: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/warning-rules`,
  ),
  updateComplianceSkcWarning: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-warning`,
    { method: 'POST', data },
  ),
  getComplianceSkcWarningStatus: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-warning-status`,
    { method: 'POST', data },
  ),
  getComplianceTodoSummary: (shopId, params = {}) => {
    const source = params.source || 'local';
    const qs = new URLSearchParams({ source });
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/compliance/todos/summary?${qs.toString()}`,
    );
  },
  listSkcLabels: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-labels`,
    { method: 'POST', data },
  ),
  uploadSkcLabelPicture: (shopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/compliance/skc-labels/upload`,
      { method: 'POST', data: formData },
    );
  },
  bindSkcLabels: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/skc-labels/bind`,
    { method: 'POST', data },
  ),
  getLabelTemplates: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/label-templates`,
    { method: 'POST', data },
  ),
  printLabels: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/label-print`,
    { method: 'POST', data },
  ),
  listEnvironmentalMaterials: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/environmental-materials`,
    { query: { source: 'cached', ...params } },
  ),
  syncEnvironmentalMaterials: (shopId, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/compliance/environmental-materials/sync`,
    { method: 'POST', signal: options?.signal },
  ),
  listReturnOrders: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/returns`,
    { query: params },
  ),
  /** 营销活动：可报活动列表（live） */
  listMarketing: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '查询营销活动');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/list`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：报名记录 */
  listMarketingEnrolls: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '查询营销报名记录');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enrolls`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：拉取可报活动（工具栏「同步更新」，计入每日手动同步次数） */
  syncMarketing: (shopId, data = {}, options = {}) => {
    const id = requireSheinShopId(shopId, '同步营销活动');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/sync`,
      { method: 'POST', data: data || {}, signal: options?.signal },
    );
  },
  /** 营销活动：拉取报名记录（工具栏「同步更新」，计入每日手动同步次数） */
  syncMarketingEnrolls: (shopId, data = {}, options = {}) => {
    const id = requireSheinShopId(shopId, '同步营销报名记录');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enrolls/sync`,
      { method: 'POST', data: data || {}, signal: options?.signal },
    );
  },
  /** 营销活动：活动详情 */
  getMarketingActivityDetail: (shopId, activityId, data = {}) => {
    const id = requireSheinShopId(shopId, '查询营销活动详情');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/activities/${encodeURIComponent(activityId)}/detail`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：可报商品 */
  listMarketingActivityProducts: (shopId, activityId, data = {}) => {
    const id = requireSheinShopId(shopId, '查询可报商品');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/activities/${encodeURIComponent(activityId)}/products`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：提交报名（异步任务，立即返回 jobId；单个 / 批量共用） */
  submitMarketingEnroll: (shopId, activityId, data = {}) => {
    const id = requireSheinShopId(shopId, '提交营销报名');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/activities/${encodeURIComponent(activityId)}/enroll`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：报名任务进度（轮询） */
  getMarketingEnrollJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询报名进度');
    const jid = requireSheinSyncJobId(jobId, '查询报名进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enroll-jobs/${encodeURIComponent(jid)}`,
    );
  },
  /** 营销活动：取消报名（异步任务，立即返回 jobId；单个 / 批量共用） */
  cancelMarketingEnroll: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '取消营销报名');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enrolls/cancel`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 营销活动：取消报名任务进度（轮询） */
  getMarketingEnrollCancelJob: (shopId, jobId) => {
    const id = requireSheinShopId(shopId, '查询取消进度');
    const jid = requireSheinSyncJobId(jobId, '查询取消进度');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enroll-cancel-jobs/${encodeURIComponent(jid)}`,
    );
  },
  /** 营销活动：删除报名记录（仅本地缓存，不通知平台） */
  deleteMarketingEnrolls: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '删除营销报名记录');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/marketing/enrolls/delete`,
      { method: 'POST', data: data || {} },
    );
  },
  /** 成本议价单列表（默认本地缓存；source=live 打平台） */
  listDiscussOrders: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/discuss/list`,
    { method: 'POST', data: { source: 'cached', ...data } },
  ),
  syncDiscussOrders: (shopId, data = {}) => {
    const { signal, maxPages, ...rest } = data && typeof data === 'object' ? data : {};
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/discuss/sync`,
      {
        method: 'POST',
        data: {
          ...rest,
          ...(Number.isFinite(Number(maxPages)) && Number(maxPages) > 0
            ? { maxPages: Math.floor(Number(maxPages)) }
            : {}),
        },
        signal,
        // 多状态翻页可能较久
        timeoutMs: 10 * 60 * 1000,
      },
    );
  },
  batchDeleteDiscussOrders: (shopId, { discussSns = [], ids = [] } = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/discuss/batch-delete`,
    {
      method: 'POST',
      data: {
        discussSns: (Array.isArray(discussSns) ? discussSns : [])
          .map((s) => String(s || '').trim())
          .filter(Boolean),
        ids: (Array.isArray(ids) ? ids : []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0),
      },
    },
  ),
  getDiscussPendingCount: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/discuss/pending-count`,
    { method: 'POST', data: {} },
  ),
  /** 成本价涨价原因枚举（半托管/全托管） */
  queryChangePriceReasons: (shopId, skcNameList = []) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/discuss/change-price-reasons`,
    {
      method: 'POST',
      data: {
        skcNameList: (Array.isArray(skcNameList) ? skcNameList : [skcNameList])
          .map((s) => String(s || '').trim())
          .filter(Boolean),
      },
    },
  ),
  processDiscussOrder: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/discuss/process`,
    { method: 'POST', data },
  ),
  /** 写入一条待商家确认议价单示例（需 IPE_SHEIN_DEMO_SEED_ENABLED=true） */
  seedDiscussPendingDemo: (shopId) => {
    const id = requireSheinShopId(shopId, '加载议价单示例');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/discuss/seed-pending-demo`,
      { method: 'POST', data: {} },
    );
  },
  uploadDiscussFile: (shopId, file, type = 1) => {
    const form = new FormData();
    form.append('file', file);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/discuss/upload?type=${encodeURIComponent(type)}`,
      { method: 'POST', data: form },
    );
  },
  /**
   * 建议零售价当前价：
   * - 传数组 → 点查平台（编辑用）
   * - 传对象（如 source:'cached'）→ 列表读本地 / live 点查
   */
  searchRecommendRetailPrice: (shopId, skcNameListOrBody = []) => {
    const data = Array.isArray(skcNameListOrBody)
      ? {
        skc_name_list: skcNameListOrBody
          .map((s) => String(s || '').trim())
          .filter(Boolean),
      }
      : (skcNameListOrBody && typeof skcNameListOrBody === 'object' ? skcNameListOrBody : {});
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/rrp/search`,
      { method: 'POST', data },
    );
  },
  /** 立即按类目套用建议价资料库并提交审核 */
  autoSubmitRecommendRetailPrice: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/auto-submit`,
    { method: 'POST', data },
  ),
  /** 同步发品时提交过建议零售价的 SKC 当前价入库 */
  syncRecommendRetailPrice: (shopId, options = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/sync`,
    { method: 'POST', data: {}, signal: options?.signal, timeoutMs: 600000 },
  ),
  /** 写入建议零售价 UI 联调示例（审核中 + 生效中；需 IPE_SHEIN_DEMO_SEED_ENABLED=true） */
  seedRecommendRetailUiDemo: (shopId) => {
    const id = requireSheinShopId(shopId, '加载建议零售价示例');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/rrp/seed-ui-demo`,
      { method: 'POST', data: {} },
    );
  },
  queryRecommendRetailPriceRule: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/rule`,
    { method: 'POST', data },
  ),
  batchSaveRecommendRetailPrice: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/batch-save`,
    { method: 'POST', data },
  ),
  /** 建议零售价审核记录：默认 live（可指定 SKC 或不指定按页拉取） */
  searchRecommendRetailPriceAudit: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/audit/search`,
    { method: 'POST', data },
  ),
  /** @deprecated 审核记录不再作为全量同步主源；请用 syncRecommendRetailPrice */
  syncRecommendRetailPriceAudit: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/audit/sync`,
    { method: 'POST', data: {} },
  ),
  getRecommendRetailPendingCount: (shopId) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/rrp/pending-count`,
    { method: 'POST', data: {} },
  ),
  batchDeleteReturnOrders: (shopId, { returnOrderNos = [], ids = [] } = {}) => {
    const id = requireSheinShopId(shopId, '批量删除退货缓存');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/aftersales/returns/batch-delete`,
      {
        method: 'POST',
        data: {
          returnOrderNos: (Array.isArray(returnOrderNos) ? returnOrderNos : [])
            .map((s) => String(s || '').trim())
            .filter(Boolean),
          ids: (Array.isArray(ids) ? ids : []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0),
        },
      },
    );
  },
  getReturnOrderDetails: (shopId, data = {}) => {
    const nos = Array.isArray(data.returnOrderNoList)
      ? data.returnOrderNoList
      : (data.returnOrderNo || data.returnNo
        ? [data.returnOrderNo || data.returnNo]
        : []);
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/aftersales/returns/details`,
      {
        method: 'POST',
        data: {
          ...data,
          returnOrderNoList: nos.filter(Boolean).map((n) => String(n).trim()).filter(Boolean),
        },
      },
    );
  },
  getCachedReturnOrderDetail: (shopId, returnOrderNo) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/returns/${encodeURIComponent(returnOrderNo)}`,
  ),
  signReturnOrder: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/returns/sign`,
    { method: 'POST', data },
  ),
  /** 批量确认签收：后端同店串行 + 单店限流，避免浏览器并发打满平台 sign≈40/s */
  signReturnOrdersBatch: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/returns/sign-batch`,
    { method: 'POST', data },
  ),
  listSheinOrders: (shopId, params = {}) => {
    const query = { ...(params && typeof params === 'object' ? params : {}) };
    if (query.allOfType || String(shopId || '') === '__ALL__' || !shopId) {
      delete query.shopId;
      return sheinRequest('/aftersales/orders', {
        query: {
          ...query,
          shopType: query.shopType,
          shopId: query.filterShopId || undefined,
        },
      });
    }
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/aftersales/orders`,
      { query },
    );
  },
  syncSheinOrders: (shopId, data = {}) => {
    const { signal, ...body } = data && typeof data === 'object' ? data : {};
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/aftersales/orders/sync`,
      { method: 'POST', data: body, signal, timeoutMs: 600000 },
    );
  },
  batchDeleteSheinOrders: (shopId, { orderNos = [], ids = [] } = {}) => {
    const id = requireSheinShopId(shopId, '批量删除订单缓存');
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/aftersales/orders/batch-delete`,
      {
        method: 'POST',
        data: {
          orderNos: (Array.isArray(orderNos) ? orderNos : [])
            .map((s) => String(s || '').trim())
            .filter(Boolean),
          ids: (Array.isArray(ids) ? ids : []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0),
        },
      },
    );
  },
  getCachedSheinOrder: (shopId, orderNo) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/orders/${encodeURIComponent(orderNo)}`,
  ),
  /** 实时 order-detail + 并行 logistics-track，回写本地 */
  fetchSheinOrderDetails: (shopId, data = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/orders/details`,
    { method: 'POST', data },
  ),
  /** 客单物流轨迹：GET /open-api/gsp/logistics-track */
  getSheinOrderLogisticsTrack: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/orders/logistics-track`,
    { query: params },
  ),
  querySkuSales: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/aftersales/sku-sales`,
    { method: 'POST', data },
  ),
  listFinanceBills: (shopId, params = {}) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/finance/bills`,
    { query: params },
  ),
  batchDeleteFinanceBills: (shopId, ids = []) => {
    const id = requireSheinShopId(shopId, '批量删除账单缓存');
    const normalized = (Array.isArray(ids) ? ids : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}/finance/bills/batch-delete`,
      { method: 'POST', data: { ids: normalized } },
    );
  },
  /** 本地缓存详情（优先读库） */
  getCachedFinanceBillDetail: (shopId, billType, billNo) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/finance/bills/${encodeURIComponent(billType)}/${encodeURIComponent(billNo)}`,
  ),
  /** 实时拉平台详情并回写本地 */
  getFinanceBillDetail: (shopId, data) => sheinRequest(
    `/shops/${encodeURIComponent(shopId)}/finance/bills/detail`,
    { method: 'POST', data },
  ),
  getPreferences: () => sheinRequest('/preferences'),
  updatePreferences: data => sheinRequest('/preferences', {
    method: 'PUT',
    data,
  }),
  syncReturnOrders: (shopId, data = {}) => {
    const { signal, ...body } = data && typeof data === 'object' ? data : {};
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/aftersales/returns/sync`,
      { method: 'POST', data: body, signal, timeoutMs: 600000 },
    );
  },
  syncFinanceBills: (shopId, data = {}) => {
    const { signal, ...body } = data && typeof data === 'object' ? data : {};
    return sheinRequest(
      `/shops/${encodeURIComponent(shopId)}/finance/bills/sync`,
      // 多窗列表同步可能数分钟；详情已改为按需拉取，正常应更快完成
      { method: 'POST', data: body, signal, timeoutMs: 600000 },
    );
  },
  startModuleSyncJob: (shopId, data = {}) => {
    const id = requireSheinShopId(shopId, '启动模块同步');
    const { module: mod, signal, ...rest } = data && typeof data === 'object' ? data : {};
    const base = sheinModuleSyncJobBasePath(mod);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}${base}/sync-jobs`,
      { method: 'POST', data: rest, signal },
    );
  },
  getModuleSyncJob: (shopId, jobId, module) => {
    const id = requireSheinShopId(shopId, '查询模块同步进度');
    const jid = requireSheinSyncJobId(jobId, '查询模块同步进度');
    const base = sheinModuleSyncJobBasePath(module);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}${base}/sync-jobs/${encodeURIComponent(jid)}`,
    );
  },
  cancelModuleSyncJob: (shopId, jobId, module) => {
    const id = requireSheinShopId(shopId, '终止模块同步');
    const jid = requireSheinSyncJobId(jobId, '终止模块同步');
    const base = sheinModuleSyncJobBasePath(module);
    return sheinRequest(
      `/shops/${encodeURIComponent(id)}${base}/sync-jobs/${encodeURIComponent(jid)}/cancel`,
      { method: 'POST' },
    );
  },
  getAlerts: shopId => sheinRequest('/alerts', { query: { shopId } }),
  dismissAlert: (data) => sheinRequest('/alerts/dismiss', {
    method: 'POST',
    data,
  }),
  dismissAllAlerts: (data) => sheinRequest('/alerts/dismiss-all', {
    method: 'POST',
    data,
  }),
  markAlertsRead: (data) => sheinRequest('/alerts/mark-read', {
    method: 'POST',
    data,
  }),
  listWebhooks: (params = {}) => sheinRequest('/webhooks', { query: params }),
  getWebhook: eventId => sheinRequest(`/webhooks/${encodeURIComponent(eventId)}`),
  replayWebhook: eventId => sheinRequest(`/webhooks/${encodeURIComponent(eventId)}/replay`, {
    method: 'POST',
  }),
};

export function persistSheinWorkspaceToken(token) {
  try {
    if (typeof localStorage !== 'undefined') {
      if (token) {
        localStorage.setItem(SHEIN_WORKSPACE_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(SHEIN_WORKSPACE_TOKEN_KEY);
      }
    }
  } catch {
    /* ignore */
  }
  // 清理旧 sessionStorage，避免双份不一致
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(SHEIN_WORKSPACE_TOKEN_KEY);
    }
  } catch {
    /* ignore */
  }
}
export const infringementAPI = {
  createBatch: payload => request('/infringement/batches', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  listBatches: (params = {}) => request('/infringement/batches', {
    query: params,
    tokenType: 'funding'
  }),
  getBatch: id => request(`/infringement/batches/${encodeURIComponent(id)}`, {
    tokenType: 'funding'
  }),
  patchBatch: (id, payload) => request(`/infringement/batches/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  deleteBatch: id => request(`/infringement/batches/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  cancelBatch: id => request(`/infringement/batches/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    tokenType: 'funding'
  }),
  resumeBatch: id => request(`/infringement/batches/${encodeURIComponent(id)}/resume`, {
    method: 'POST',
    tokenType: 'funding'
  }),
};

/** 开放对接：IPE Key 管理（用户 JWT） */
export const openKeysAPI = {
  list: () => request('/open-keys', { tokenType: 'funding' }),
  create: (payload) => request('/open-keys', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  rename: (id, payload) => request(`/open-keys/${encodeURIComponent(id)}/rename`, {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  revoke: (id) => request(`/open-keys/${encodeURIComponent(id)}/revoke`, {
    method: 'POST',
    tokenType: 'funding',
  }),
};

/** 第三方任务 / 自动规则（用户 JWT） */
export const openReceiveAPI = {
  listTasks: (params = {}) => request('/open-receive/tasks', {
    query: params,
    tokenType: 'funding',
  }),
  getTask: (taskId) => request(`/open-receive/tasks/${encodeURIComponent(taskId)}`, {
    tokenType: 'funding',
  }),
  renameItemImage: (taskId, itemId, payload = {}) => request(
    `/open-receive/tasks/${encodeURIComponent(taskId)}/items/${encodeURIComponent(itemId)}/image-name`,
    {
      method: 'PATCH',
      data: payload,
      tokenType: 'funding',
    },
  ),
  updateItemTitle: (taskId, itemId, payload = {}) => request(
    `/open-receive/tasks/${encodeURIComponent(taskId)}/items/${encodeURIComponent(itemId)}/title`,
    {
      method: 'PATCH',
      data: payload,
      tokenType: 'funding',
    },
  ),
  updateItemColor: (taskId, itemId, payload = {}) => request(
    `/open-receive/tasks/${encodeURIComponent(taskId)}/items/${encodeURIComponent(itemId)}/color`,
    {
      method: 'PATCH',
      data: payload,
      tokenType: 'funding',
    },
  ),
  removeItemImage: (taskId, itemId, payload = {}) => request(
    `/open-receive/tasks/${encodeURIComponent(taskId)}/items/${encodeURIComponent(itemId)}/images`,
    {
      method: 'DELETE',
      query: payload,
      tokenType: 'funding',
    },
  ),
  addItemImage: (taskId, itemId, payload = {}) => request(
    `/open-receive/tasks/${encodeURIComponent(taskId)}/items/${encodeURIComponent(itemId)}/images`,
    {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
    },
  ),
  retryFailed: (taskId) => request(`/open-receive/tasks/${encodeURIComponent(taskId)}/retry-failed`, {
    method: 'POST',
    tokenType: 'funding',
  }),
  deleteTask: (taskId) => request(`/open-receive/tasks/${encodeURIComponent(taskId)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
  getAutoRule: () => request('/open-receive/auto-rule', { tokenType: 'funding' }),
  saveAutoRule: (payload) => request('/open-receive/auto-rule', {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }),
  listAutoRules: () => request('/open-receive/auto-rules', { tokenType: 'funding' }),
  syncAutoRulesFromTemplates: () => request('/open-receive/auto-rules/sync-from-templates', {
    method: 'POST',
    tokenType: 'funding',
  }),
  createAutoRule: (payload = {}) => request('/open-receive/auto-rules', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  getAutoRuleById: (ruleId) => request(`/open-receive/auto-rules/${encodeURIComponent(ruleId)}`, {
    tokenType: 'funding',
  }),
  updateAutoRule: (ruleId, payload) => request(`/open-receive/auto-rules/${encodeURIComponent(ruleId)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }),
  deleteAutoRule: (ruleId) => request(`/open-receive/auto-rules/${encodeURIComponent(ruleId)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};
export const imageFissionAPI = {
  listBatches: (params = {}) => request('/image-fission/batches', { query: params, tokenType: 'funding' }),
  getBatch: batchId => request(`/image-fission/batches/${encodeURIComponent(batchId)}`, { tokenType: 'funding' }),
  deleteBatch: batchId => request(`/image-fission/batches/${encodeURIComponent(batchId)}`, { method: 'DELETE', tokenType: 'funding' }),
  cancelBatch: batchId => request(`/image-fission/batches/${encodeURIComponent(batchId)}/cancel`, { method: 'POST', tokenType: 'funding' }),
  resumeBatch: batchId => request(`/image-fission/batches/${encodeURIComponent(batchId)}/resume`, { method: 'POST', tokenType: 'funding' }),
  createBatch: payload => request('/image-fission/batches', { method: 'POST', data: payload, tokenType: 'funding' }),
  /** IPE 内置引擎：单图同步裂变，返回 outputs[].url */
  processLocal: payload => request('/image-fission/process', { method: 'POST', data: payload, tokenType: 'funding' }),
};
export const materialHubAPI = {
  list: (params = {}) => request('/material-hub/items', {
    query: params,
    tokenType: 'funding',
  }).then((res) => {
    const raw = res?.items ?? res?.data;
    return {
      ...res,
      items: Array.isArray(raw) ? raw : [],
    };
  }),
  listCategories: (params = {}) => request('/material-hub/categories', {
    query: params,
    tokenType: 'funding',
  }),
  listContentTypes: () => request('/material-hub/content-types', { tokenType: 'funding' }),
};
export const systemMaterialAPI = {
  list: (params = {}) => request('/system/material-library', {
    query: params,
    tokenType: 'funding'
  }).then(res => {
    var _res$items;
    const raw = (_res$items = res === null || res === void 0 ? void 0 : res.items) !== null && _res$items !== void 0 ? _res$items : res === null || res === void 0 ? void 0 : res.data;
    return {
      ...res,
      items: Array.isArray(raw) ? raw : []
    };
  }),
  listBatches: () => request('/system/material-library/batches', {
    tokenType: 'funding'
  }),
  deleteItem: id => request(`/system/material-library/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};
export const systemModelsAPI = {
  list: (params = {}) => listSystemModelsWithCache(params, async (query) => {
    const res = await request('/system/models', {
      query,
      tokenType: 'funding',
    });
    const raw = res?.items ?? res?.data;
    return {
      ...res,
      items: Array.isArray(raw) ? raw : [],
    };
  }),
};
/** AI 处理产出写入 AI 灵感待发布（服务端内嵌 IPE 水印） */
export const aiInspirationOutputAPI = {
  saveProcessOutput: payload => request('/official/ai-inspiration/save-process-output', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  saveProcessOutputs: payload => request('/official/ai-inspiration/save-process-outputs', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
};
export const officialDataAPI = {
  listWorkflowTemplates: () => request('/official/workflow-templates', {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    templates: Array.isArray(res?.templates) ? res.templates : (Array.isArray(res?.data) ? res.data : [])
  })),
  listImageTemplates: () => request('/official/image-templates', {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    templates: Array.isArray(res?.templates) ? res.templates : (Array.isArray(res?.data) ? res.data : [])
  })),
  listTitleTemplates: () => request('/official/title-templates', {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    templates: Array.isArray(res?.templates) ? res.templates : (Array.isArray(res?.data) ? res.data : [])
  })),
  listTableTemplateRules: () => request('/official/table-template-rules', {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    rules: Array.isArray(res?.rules) ? res.rules : (Array.isArray(res?.data) ? res.data : [])
  })),
  /** 设计模板颜色库（颜色名 + 颜色编码） */
  listDesignColors: () => request('/official/design-colors', {
    tokenType: 'funding'
  }).then((res) => {
    const colors = Array.isArray(res?.colors)
      ? res.colors
      : (Array.isArray(res?.data) ? res.data : []);
    return { ...res, colors };
  }),
};

/** 电商平台使用教程（Admin 维护，与 IPE 使用手册独立） */
export const platformTutorialAPI = {
  list: (type) => request('/platform-tutorials', {
    query: type && type !== 'ALL' ? { type } : undefined,
    tokenType: 'funding',
  }).then((res) => {
    const items = Array.isArray(res?.items)
      ? res.items
      : (Array.isArray(res?.data) ? res.data : []);
    return { ...res, items };
  }),
};

/** 系统公告（Admin 维护，工作台「系统通知」展示） */
export const systemAnnouncementAPI = {
  list: () => request('/system-announcements', {
    tokenType: 'funding',
  }).then((res) => {
    const items = Array.isArray(res?.items)
      ? res.items
      : (Array.isArray(res?.data) ? res.data : []);
    return { ...res, items };
  }),
};
export const imageAccessAPI = {
  signBatch: (urls, options = {}) => request('/storage/images/sign-batch', {
    method: 'POST',
    data: {
      urls: Array.isArray(urls) ? urls : [],
      ...(options?.variant ? { variant: String(options.variant).trim() } : {}),
    },
    tokenType: isIpeAdminConsole() ? 'admin' : 'funding',
  }),
  /** 模板导出：换取 OSS 预签名直链，供平台外链导入 */
  signBatchExternal: (urls) => request('/storage/images/sign-batch-external', {
    method: 'POST',
    data: { urls: Array.isArray(urls) ? urls : [] },
    tokenType: isIpeAdminConsole() ? 'admin' : 'funding',
  }),
};

/** 去重跳过时只回传 URL / 内容哈希命中的已有记录，避免按文件名误复用另一张图。 */
function reusedImageSpaceDuplicates(skipped, normalizeUrl) {
  const out = [];
  const seen = new Set();
  const norm = typeof normalizeUrl === 'function' ? normalizeUrl : (value) => String(value || '');
  (skipped || []).forEach((row) => {
    if (row?.reason !== 'existing' || !row.duplicate || row.duplicate.id == null || row.duplicate.id === '') return;
    const item = row.item || {};
    const dup = row.duplicate;
    const sameHash = item.contentHash && dup.contentHash
      && String(item.contentHash) === String(dup.contentHash);
    const itemUrl = norm(item.url || item.src || item.imageUrl || '');
    const dupUrl = norm(dup.url || dup.src || dup.imageUrl || '');
    if (!sameHash && !(itemUrl && itemUrl === dupUrl)) return;
    const id = String(dup.id);
    if (seen.has(id)) return;
    seen.add(id);
    out.push(dup);
  });
  return out;
}

export const imageSpaceAPI = {
  getAll: (params = {}) => request('/admin/image-space', {
    query: params,
    tokenType: 'funding'
  }).then(res => {
    var _res$images;
    const raw = (_res$images = res === null || res === void 0 ? void 0 : res.images) !== null && _res$images !== void 0 ? _res$images : res === null || res === void 0 ? void 0 : res.data;
    let images = [];
    let nestedMeta = null;
    if (Array.isArray(raw)) {
      images = raw;
    } else if (raw && typeof raw === 'object' && Array.isArray(raw.images)) {
      images = raw.images;
      nestedMeta = raw;
    }
    return {
      ...res,
      images,
      total: res?.total ?? nestedMeta?.total,
      hasMore: res?.hasMore ?? nestedMeta?.hasMore,
      page: res?.page ?? nestedMeta?.page,
      pageSize: res?.pageSize ?? nestedMeta?.pageSize,
      totalPages: res?.totalPages ?? nestedMeta?.totalPages,
    };
  }),
  getById: (id) => {
    const key = String(id || '').trim();
    // 本地占位 / 落盘窗口期 / 近期已 404：不打服务端，避免工作流 Network 刷红
    if (!key || shouldSkipImageSpaceServerLookup(key)) {
      return Promise.resolve({ image: null, data: null });
    }
    return request(`/admin/image-space/${encodeURIComponent(key)}`, {
      tokenType: 'funding',
    }).then((res) => {
      const inner = (res === null || res === void 0 ? void 0 : res.image)
        || (res === null || res === void 0 ? void 0 : res.data)
        || (res && res.id && !res.image ? res : null);
      const innerObj = inner && typeof inner === 'object' ? inner : null;
      // 后端统一返回 { image, data }；展开 inner 以便旧代码直接访问 .url / .dataUrl
      return {
        ...res,
        ...(innerObj || {}),
        image: innerObj,
        data: innerObj !== null && innerObj !== void 0 ? innerObj : res === null || res === void 0 ? void 0 : res.data,
      };
    }).catch((err) => {
      const status = Number(err?.status || 0);
      const msg = String(err?.message || err?.userMessage || '');
      if (status === 404 || /not found|不存在|404/i.test(msg)) {
        rememberImageSpaceLookupMiss(key);
        return { image: null, data: null };
      }
      throw err;
    });
  },
  update: (id, payload) => request(`/admin/image-space/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  delete: id => request(`/admin/image-space/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  /** 智能工具生成结果入库（OSS + 元数据，不扣 IMAGE_SPACE 豆） */
  ingestGenerated: (payload) => request('/admin/image-space/ingest-generated', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
  batchSave: async (images, options = {}) => {
    const { ensureImageSpaceItemsServerUrl } = await import('./imageUrlUtils');
    const { enrichImageSpaceRecords } = await import('./imageSpaceCreatorMeta');
    const { filterNewImageSpaceItems, loadExistingImagesForDedupe, normalizeImageSpaceUrl } = await import('./imageSpaceDedupe');
    const ingestKind = options.ingestKind;
    const withIngest = options.skipIngestAspect
      ? (Array.isArray(images) ? images : [])
      : enrichImageSpaceRecordsForIngest(images, { ingestKind, fileMeta: options.fileMeta });

    let toUpload = withIngest;
    let skippedDup = 0;
    let skippedRows = [];
    if (options.dedupe !== false) {
      const existingForDedupe = options.existingImages
        ? options.existingImages
        : await loadExistingImagesForDedupe(options.localImages || []);
      const { accepted, skipped } = filterNewImageSpaceItems(withIngest, existingForDedupe);
      toUpload = accepted;
      skippedRows = skipped;
      skippedDup = skipped.length;
    }

    if (!toUpload || toUpload.length === 0) {
      return {
        message: 'ok',
        saved: 0,
        images: reusedImageSpaceDuplicates(skippedRows, normalizeImageSpaceUrl),
        skippedDup,
      };
    }

    const normalized = await ensureImageSpaceItemsServerUrl(toUpload, {
      skipCompress: options.skipCompress === true
        || toUpload.every((item) => item?._preCompressed === true || item?.uploadFile instanceof File),
    });
    const persisted = (normalized || []).filter((item) => {
      const u = String(item?.url || '').trim();
      return u && !u.startsWith('data:') && !u.startsWith('blob:') && !u.startsWith('indexeddb://');
    });
    if (persisted.length === 0) {
      return {
        message: 'ok',
        saved: 0,
        images: [],
        skippedDup,
      };
    }
    const enriched = enrichImageSpaceRecords(persisted, options.creatorPayload || null);
    const cleaned = (enriched || []).map((item) => {
      if (!item || typeof item !== 'object') return item;
      const {
        dataUrl,
        originalDataUrl,
        compressedDataUrl,
        uploadFile,
        _preCompressed,
        ...rest
      } = item;
      const persisted = String(rest.url || '').trim();
      ['imageUrl', 'src', 'originalUrl'].forEach((key) => {
        const v = String(rest[key] || '').trim();
        if (!v.startsWith('data:') && !v.startsWith('blob:') && !v.startsWith('indexeddb://')) return;
        if (persisted) rest[key] = persisted;
        else delete rest[key];
      });
      return rest;
    });
    const inlineUrls = cleaned.filter((item) => {
      const url = String(item?.url || '').trim();
      return url.startsWith('data:') || url.startsWith('blob:');
    });
    if (inlineUrls.length > 0) {
      throw new Error('图片上传失败，未能转为服务器地址，请重试');
    }
    const response = await request('/admin/image-space/batch', {
      method: 'POST',
      data: {
        images: cleaned,
      },
      tokenType: 'funding',
    });
    const serverSkipped = Number(response?.skippedDup) || 0;
    const savedImages = Array.isArray(response?.images) ? response.images : cleaned;
    // 上传接口会先 registerStoredUpload（存储文件名 id），batch 去重后回包 id 可能≠客户端临时 id
    const { correlateImageSpaceBatchSaveResults } = await import('./imageSpaceBatchCorrelate');
    const correlated = correlateImageSpaceBatchSaveResults(cleaned, savedImages);
    const { appendImageSpaceDedupeCache } = await import('./imageSpaceDedupe');
    appendImageSpaceDedupeCache(correlated);
    return {
      ...response,
      images: correlated,
      skippedDup: skippedDup + serverSkipped,
    };
  },
  batchDelete: ids => request('/admin/image-space/batch-delete', {
    method: 'POST',
    data: {
      ids
    },
    tokenType: 'funding'
  }),
  batchTrash: ({ ids, deletedAt }) => request('/admin/image-space/batch-trash', {
    method: 'POST',
    data: { ids, deletedAt },
    tokenType: 'funding',
  }),
  batchRestore: ({ ids }) => request('/admin/image-space/batch-restore', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding',
  }),
  copyToTeam: (payload) => request('/admin/image-space/copy-to-team', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
};

export const videoSpaceAPI = {
  getAll: (params = {}) => request('/admin/video-space', {
    query: params,
    tokenType: 'funding',
  }).then((res) => {
    const raw = res?.videos ?? res?.data;
    let videos = [];
    let nestedMeta = null;
    if (Array.isArray(raw)) {
      videos = raw;
    } else if (raw && typeof raw === 'object' && Array.isArray(raw.videos)) {
      videos = raw.videos;
      nestedMeta = raw;
    }
    return {
      ...res,
      videos,
      total: res?.total ?? nestedMeta?.total,
      hasMore: res?.hasMore ?? nestedMeta?.hasMore,
      page: res?.page ?? nestedMeta?.page,
      pageSize: res?.pageSize ?? nestedMeta?.pageSize,
      totalPages: res?.totalPages ?? nestedMeta?.totalPages,
    };
  }),
  getById: (id) => request(`/admin/video-space/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => {
    const inner = res?.video || res?.data || (res && res.id && !res.video ? res : null);
    const innerObj = inner && typeof inner === 'object' ? inner : null;
    return {
      ...res,
      ...(innerObj || {}),
      video: innerObj,
      data: innerObj ?? res?.data,
    };
  }),
  update: (id, payload) => request(`/admin/video-space/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding',
  }),
  delete: (id) => request(`/admin/video-space/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
  batchSave: (videos) => request('/admin/video-space/batch', {
    method: 'POST',
    data: { videos: Array.isArray(videos) ? videos : [] },
    tokenType: 'funding',
  }),
  batchDelete: (ids) => request('/admin/video-space/batch-delete', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding',
  }),
  batchTrash: ({ ids, deletedAt }) => request('/admin/video-space/batch-trash', {
    method: 'POST',
    data: { ids, deletedAt },
    tokenType: 'funding',
  }),
  batchRestore: ({ ids }) => request('/admin/video-space/batch-restore', {
    method: 'POST',
    data: { ids },
    tokenType: 'funding',
  }),
};

export const videoSpaceFolderAPI = {
  getAll: async (params = {}) => {
    const normalize = (res) => {
      const raw = res?.folders ?? res?.data;
      const folders = Array.isArray(raw) ? raw : [];
      return { ...res, folders };
    };
    // 显式 page：单页；否则按 hasMore 拉全量（文件夹树需要完整列表，单页 cap=200）
    if (params.page != null) {
      const res = await request('/admin/video-space-folders', {
        query: {
          page: Math.max(1, Number(params.page) || 1),
          pageSize: Math.min(200, Math.max(1, Number(params.pageSize) || 200)),
        },
        tokenType: 'funding',
      });
      return normalize(res);
    }
    const pageSize = Math.min(200, Math.max(1, Number(params.pageSize) || 200));
    const all = [];
    let page = 1;
    let hasMore = true;
    let lastRes = { folders: [], total: 0, hasMore: false };
    while (hasMore && page <= 25) {
      lastRes = normalize(await request('/admin/video-space-folders', {
        query: { page, pageSize },
        tokenType: 'funding',
      }));
      all.push(...(lastRes.folders || []));
      hasMore = Boolean(lastRes.hasMore) && (lastRes.folders || []).length > 0;
      page += 1;
    }
    return {
      ...lastRes,
      folders: all,
      data: all,
      total: all.length,
      page: 1,
      pageSize: all.length,
      hasMore: false,
    };
  },
  batchSave: (folders) => request('/admin/video-space-folders/batch', {
    method: 'POST',
    data: { folders },
    tokenType: 'funding',
  }),
  update: (id, payload) => request(`/admin/video-space-folders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding',
  }),
  delete: (id) => request(`/admin/video-space-folders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

export const imageSpaceFolderAPI = {
  getAll: async (params = {}) => {
    const normalize = (res) => {
      const raw = res?.folders ?? res?.data;
      const folders = Array.isArray(raw) ? raw : [];
      return { ...res, folders };
    };
    if (params.page != null) {
      const res = await request('/admin/image-space-folders', {
        query: {
          page: Math.max(1, Number(params.page) || 1),
          pageSize: Math.min(200, Math.max(1, Number(params.pageSize) || 200)),
        },
        tokenType: 'funding',
      });
      return normalize(res);
    }
    const pageSize = Math.min(200, Math.max(1, Number(params.pageSize) || 200));
    const all = [];
    let page = 1;
    let hasMore = true;
    let lastRes = { folders: [], total: 0, hasMore: false };
    while (hasMore && page <= 25) {
      lastRes = normalize(await request('/admin/image-space-folders', {
        query: { page, pageSize },
        tokenType: 'funding',
      }));
      all.push(...(lastRes.folders || []));
      hasMore = Boolean(lastRes.hasMore) && (lastRes.folders || []).length > 0;
      page += 1;
    }
    return {
      ...lastRes,
      folders: all,
      data: all,
      total: all.length,
      page: 1,
      pageSize: all.length,
      hasMore: false,
    };
  },
  batchSave: (folders) => request('/admin/image-space-folders/batch', {
    method: 'POST',
    data: { folders },
    tokenType: 'funding',
  }),
  update: (id, payload) => request(`/admin/image-space-folders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding',
  }),
  delete: (id) => request(`/admin/image-space-folders/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 浏览器插件采集上报（须 IPE 登录 Token，路径见 client/docs/plugin-capture-api.md）
 * T-A07：仍走 multipart 旁路（非 imageStorageAPI）；后端按张限流 PLUGIN_UPLOAD_RATE_LIMITED。
 * 结论见 docs/upload-bypass-inventory-20260728.md —— 后续改直传。
 */
export const pluginCaptureAPI = {
  getTransportConfig: () => request('/plugin/capture/transport-config', {
    tokenType: 'funding',
  }),
  ingestImageSpace: (space, payload) => request('/plugin/capture/image-space/ingest', {
    method: 'POST',
    query: { space },
    data: payload,
    tokenType: 'funding',
  }),
  uploadImageSpace: async (space, file, options = {}) => {
    const { runWithRetry } = await import('./uploadImageUtils');
    const formData = new FormData();
    formData.append('file', file);
    if (options.name) formData.append('name', options.name);
    if (options.pageUrl) formData.append('pageUrl', options.pageUrl);
    if (options.pageTitle) formData.append('pageTitle', options.pageTitle);
    if (options.siteName) formData.append('siteName', options.siteName);
    if (options.pluginVersion) formData.append('pluginVersion', options.pluginVersion);
    return runWithRetry(() => request('/plugin/capture/image-space/upload', {
      method: 'POST',
      query: { space },
      data: formData,
      tokenType: 'funding',
      timeout: UPLOAD_CONFIG.timeoutMs,
    }));
  },
  batchUploadImageSpace: async (space, files, options = {}) => {
    const { runWithRetry } = await import('./uploadImageUtils');
    const formData = new FormData();
    (files || []).forEach((file) => {
      if (file) formData.append('files', file);
    });
    if (options.pageUrl) formData.append('pageUrl', options.pageUrl);
    if (options.pageTitle) formData.append('pageTitle', options.pageTitle);
    if (options.siteName) formData.append('siteName', options.siteName);
    if (options.pluginVersion) formData.append('pluginVersion', options.pluginVersion);
    return runWithRetry(() => request('/plugin/capture/image-space/batch-upload', {
      method: 'POST',
      query: { space },
      data: formData,
      tokenType: 'funding',
      timeout: UPLOAD_CONFIG.batchTimeoutMs,
    }));
  },
  /** 插件商品采集：优先写入素材采集「抓取批次」（推荐入口，见 plugin-capture-api.md） */
  ingestSourcing: (payload) => request('/plugin/capture/sourcing/ingest', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }),
};

/**
 * 按槽位累加图片空间使用次数（同一 id 在数组中出现 N 次则 +N）。
 * @returns {Promise<Record<string, number>>} 各 id 本次增量，便于列表就地更新
 */
function shouldSkipImageSpaceUsageBump(id) {
  const s = String(id || '').trim();
  if (!s) return true;
  if (isImageSpaceClientLocalId(s)) return true;
  if (s.startsWith('from-official-')) return true;
  if (s.startsWith('cutout_')) return true;
  // 落盘窗口期 / 近期已知 404：勿再打 getById，避免工作流刷 ClientError 404
  if (shouldSkipImageSpaceServerLookup(s)) return true;
  return false;
}

function isExpectedImageSpaceUsageBumpError(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return msg.includes('404')
    || msg.includes('图片不存在')
    || msg.includes('not found')
    || msg.includes('not_found');
}

export async function bumpImageSpaceUsageCounts(slotIds) {
  const list = Array.isArray(slotIds) ? slotIds.filter(id => id !== null && id !== undefined && String(id).trim() !== '') : [];
  const deltaById = {};
  for (const id of list) {
    if (shouldSkipImageSpaceUsageBump(id)) continue;
    const s = String(id);
    deltaById[s] = (deltaById[s] || 0) + 1;
  }
  const entries = Object.entries(deltaById);
  if (entries.length === 0) {
    return {};
  }
  await Promise.all(entries.map(async ([id, delta]) => {
    if (delta <= 0) return;
    try {
      var _res$image, _ref, _ref2, _img$usageCount;
      const res = await imageSpaceAPI.getById(id);
      const img = (_res$image = res === null || res === void 0 ? void 0 : res.image) !== null && _res$image !== void 0 ? _res$image : res === null || res === void 0 ? void 0 : res.data;
      if (!img || typeof img !== 'object') return;
      const cur = Number((_ref = (_ref2 = (_img$usageCount = img.usageCount) !== null && _img$usageCount !== void 0 ? _img$usageCount : img.useCount) !== null && _ref2 !== void 0 ? _ref2 : img.referenceCount) !== null && _ref !== void 0 ? _ref : 0) || 0;
      const next = cur + delta;
      await imageSpaceAPI.update(id, {
        usageCount: next,
        useCount: next,
        referenceCount: next
      });
    } catch (e) {
      if (!isExpectedImageSpaceUsageBumpError(e)) {
        console.warn('[image-space] bump usage failed', id, e);
      }
    }
  }));
  return deltaById;
}
export const titlePresetAPI = {
  getAll: () => request('/title-presets', {
    tokenType: 'funding',
    query: {
      page: 1,
      pageSize: 200
    }
  }).then(async res => {
    var _ref3, _res$presets;
    const normalizePresetMap = rawValue => {
      const list = Array.isArray(rawValue) ? rawValue : Object.values(rawValue || {});
      const presetMap = {};
      list.forEach(item => {
        const id = item !== null && item !== void 0 && item.id ? String(item.id) : '';
        if (id) presetMap[id] = item;
      });
      return presetMap;
    };
    const directPresets = normalizePresetMap((_ref3 = (_res$presets = res === null || res === void 0 ? void 0 : res.presets) !== null && _res$presets !== void 0 ? _res$presets : res === null || res === void 0 ? void 0 : res.data) !== null && _ref3 !== void 0 ? _ref3 : []);
    if (Object.keys(directPresets).length > 0) {
      return {
        ...res,
        presets: directPresets
      };
    }

    // 兼容历史数据：旧版本会把预设快照写在 /title-presets/batch
    try {
      const batchRes = await request('/title-presets/batch', {
        tokenType: 'funding',
        query: {
          pageSize: 1000
        }
      });
      const rows = Array.isArray(batchRes === null || batchRes === void 0 ? void 0 : batchRes.data) ? batchRes.data : [];
      const mergedPresets = {};
      rows.forEach(row => {
        if (!row || typeof row !== 'object') return;
        const fromRow = normalizePresetMap(row !== null && row !== void 0 && row.presets && typeof row.presets === 'object' ? row.presets : {});
        Object.assign(mergedPresets, fromRow);
      });
      return {
        ...res,
        presets: mergedPresets
      };
    } catch (_) {
      return {
        ...res,
        presets: {}
      };
    }
  }),
  getById: id => request(`/title-presets/${encodeURIComponent(id)}`, {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    preset: (res === null || res === void 0 ? void 0 : res.preset) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  create: payload => request('/title-presets', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    preset: (res === null || res === void 0 ? void 0 : res.preset) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  update: (id, payload) => request(`/title-presets/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    preset: (res === null || res === void 0 ? void 0 : res.preset) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  delete: id => request(`/title-presets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  batchSave: presets => request('/title-presets/batch', {
    method: 'POST',
    data: {
      presets
    },
    tokenType: 'funding'
  })
};

/** 文生图画面描述案例（personal / team / official） */
export const aiText2ImgPromptCaseAPI = {
  list: (params = {}) => request('/ai-text2img-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-text2img-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-text2img-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-text2img-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-text2img-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 万能改图指令模板（personal / team / official），独立于文生图案例表 */
export const aiImg2ImgPromptCaseAPI = {
  list: (params = {}) => request('/ai-img2img-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-img2img-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-img2img-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-img2img-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-img2img-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 图生视频文本补充模板（personal / team / official），独立于文生视频模板 */
export const aiVideoI2vPromptCaseAPI = {
  list: (params = {}) => request('/ai-video-i2v-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-video-i2v-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-video-i2v-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-video-i2v-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-video-i2v-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 文生视频描述模板（personal / team / official），独立于万能改图 / 文生图表 */
export const aiVideoT2vPromptCaseAPI = {
  list: (params = {}) => request('/ai-video-t2v-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-video-t2v-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-video-t2v-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-video-t2v-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-video-t2v-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** AI 图片裂变 · 参数模板库（personal / team / official） */
export const aiImageFissionPromptCaseAPI = {
  list: (params = {}) => request('/ai-image-fission-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-image-fission-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-image-fission-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-image-fission-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-image-fission-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** 主图创作模板（personal / team / official），独立于万能改图案例表 */
export const aiGoodsStudioPromptCaseAPI = {
  list: (params = {}) => request('/ai-goods-studio-prompt-cases', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      ownerUserId: params.ownerUserId,
      page: params.page,
      pageSize: params.pageSize,
      includeOfficial: params.includeOfficial,
    },
  }).then((res) => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    page: Number(res?.page) || Number(params.page) || 1,
    pageSize: Number(res?.pageSize) || Number(params.pageSize) || 20,
    caseScope: res?.caseScope || params.scope || '',
  })),
  getById: (id) => request(`/ai-goods-studio-prompt-cases/${encodeURIComponent(id)}`, {
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  create: (payload) => request('/ai-goods-studio-prompt-cases', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  update: (id, payload) => request(`/ai-goods-studio-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    case: res?.case || null,
  })),
  delete: (id) => request(`/ai-goods-studio-prompt-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** AI 文案模板（personal / team / official），独立于旧 title-presets */
export const aiCopywritingTemplateAPI = {
  list: (params = {}) => request('/ai-copywriting-templates', {
    tokenType: 'funding',
    query: {
      scope: params.scope,
      keyword: params.keyword,
      category: params.category,
      platform: params.platform
    }
  }).then(res => ({
    ...res,
    items: Array.isArray(res?.items) ? res.items : [],
    total: Number(res?.total) || 0,
    templateScope: res?.templateScope || params.scope || ''
  })),
  getById: id => request(`/ai-copywriting-templates/${encodeURIComponent(id)}`, {
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: res?.template || null
  })),
  create: payload => request('/ai-copywriting-templates', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: res?.template || null
  })),
  update: (id, payload) => request(`/ai-copywriting-templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: res?.template || null
  })),
  delete: id => request(`/ai-copywriting-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};

export const titleAPI = {
  getAll: (params = {}) => (() => {
    const {
      _wideScan,
      ...queryParams
    } = params || {};
    const isTaskQuery = (queryParams === null || queryParams === void 0 ? void 0 : queryParams.taskId) !== undefined && (queryParams === null || queryParams === void 0 ? void 0 : queryParams.taskId) !== null && String(queryParams.taskId).trim() !== '';
    const defaultPageSize = isTaskQuery ? _wideScan ? 5000 : 300 : 200;
    const pageSize = (queryParams === null || queryParams === void 0 ? void 0 : queryParams.pageSize) || defaultPageSize;
    const page = (queryParams === null || queryParams === void 0 ? void 0 : queryParams.page) || 1;
    return request('/titles', {
      query: {
        page,
        pageSize,
        ...queryParams
      },
      tokenType: 'funding'
    });
  })().then(res => {
    var _ref4, _res$titles;
    const parseJsonObject = value => {
      if (!value || typeof value !== 'string') return null;
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch (_) {
        return null;
      }
    };
    const normalizeTaskKey = value => String(value !== null && value !== void 0 ? value : '').trim().replace(/\s+/g, '').replace(/^titles?[-_:]?/i, '').toLowerCase();
    const isSameTask = (candidate, target) => {
      const a = normalizeTaskKey(candidate);
      const b = normalizeTaskKey(target);
      if (!a || !b) return false;
      return a === b || a.includes(b) || b.includes(a);
    };
    const normalizeRows = rawRows => {
      const list = Array.isArray(rawRows) ? rawRows : Object.values(rawRows || {});
      return list.map(row => {
        const dataObj = row && typeof row === 'object' ? row.data && typeof row.data === 'object' ? row.data : parseJsonObject(row.data) : null;
        const merged = dataObj ? {
          ...dataObj,
          ...(row || {})
        } : {
          ...(row || {})
        };
        // 兼容 snake_case / 历史字段
        if (merged.taskId == null && merged.task_id != null) merged.taskId = merged.task_id;
        if (merged.imageId == null && merged.image_id != null) merged.imageId = merged.image_id;
        if (merged.imageUrl == null && merged.image_url != null) merged.imageUrl = merged.image_url;
        if (merged.originalUrl == null && merged.original_url != null) merged.originalUrl = merged.original_url;
        if (merged.previewUrl == null && merged.preview_url != null) merged.previewUrl = merged.preview_url;
        if (merged.dataUrl == null && merged.data_url != null) merged.dataUrl = merged.data_url;
        if (merged.croppedImageUrl == null && merged.cropped_image_url != null) merged.croppedImageUrl = merged.cropped_image_url;
        if (merged.compositeUrl == null && merged.composite_url != null) merged.compositeUrl = merged.composite_url;
        return merged;
      });
    };
    const raw = (_ref4 = (_res$titles = res === null || res === void 0 ? void 0 : res.titles) !== null && _res$titles !== void 0 ? _res$titles : res === null || res === void 0 ? void 0 : res.data) !== null && _ref4 !== void 0 ? _ref4 : [];
    let titles = normalizeRows(raw);
    const taskId = params === null || params === void 0 ? void 0 : params.taskId;
    if (taskId !== undefined && taskId !== null && String(taskId).trim() !== '') {
      const target = String(taskId);
      titles = titles.filter(item => {
        var _ref5, _ref6, _ref7, _ref8, _ref9, _item$taskId, _item$task, _item$task2, _item$data, _item$data2;
        const candidateTaskId = (_ref5 = (_ref6 = (_ref7 = (_ref8 = (_ref9 = (_item$taskId = item === null || item === void 0 ? void 0 : item.taskId) !== null && _item$taskId !== void 0 ? _item$taskId : item === null || item === void 0 ? void 0 : item.task_id) !== null && _ref9 !== void 0 ? _ref9 : item === null || item === void 0 ? void 0 : (_item$task = item.task) === null || _item$task === void 0 ? void 0 : _item$task.id) !== null && _ref8 !== void 0 ? _ref8 : item === null || item === void 0 ? void 0 : (_item$task2 = item.task) === null || _item$task2 === void 0 ? void 0 : _item$task2.taskId) !== null && _ref7 !== void 0 ? _ref7 : item === null || item === void 0 ? void 0 : (_item$data = item.data) === null || _item$data === void 0 ? void 0 : _item$data.taskId) !== null && _ref6 !== void 0 ? _ref6 : item === null || item === void 0 ? void 0 : (_item$data2 = item.data) === null || _item$data2 === void 0 ? void 0 : _item$data2.task_id) !== null && _ref5 !== void 0 ? _ref5 : '';
        return isSameTask(candidateTaskId, target);
      });
    }
    return {
      ...res,
      titles
    };
  }),
  getByTask: (taskId, options = {}) => request('/titles', {
    query: {
      taskId,
      page: (options === null || options === void 0 ? void 0 : options.page) || 1,
      pageSize: (options === null || options === void 0 ? void 0 : options.pageSize) || (options !== null && options !== void 0 && options._wideScan ? 5000 : 300)
    },
    tokenType: 'funding'
  }).then(res => {
    var _ref0, _res$titles2;
    const parseJsonObject = value => {
      if (!value || typeof value !== 'string') return null;
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch (_) {
        return null;
      }
    };
    const normalizeTaskKey = value => String(value !== null && value !== void 0 ? value : '').trim().replace(/\s+/g, '').replace(/^titles?[-_:]?/i, '').toLowerCase();
    const isSameTask = (candidate, target) => {
      const a = normalizeTaskKey(candidate);
      const b = normalizeTaskKey(target);
      if (!a || !b) return false;
      return a === b || a.includes(b) || b.includes(a);
    };
    const normalizeRows = rawRows => {
      const list = Array.isArray(rawRows) ? rawRows : Object.values(rawRows || {});
      return list.map(row => {
        const dataObj = row && typeof row === 'object' ? row.data && typeof row.data === 'object' ? row.data : parseJsonObject(row.data) : null;
        const merged = dataObj ? {
          ...dataObj,
          ...(row || {})
        } : {
          ...(row || {})
        };
        if (merged.taskId == null && merged.task_id != null) merged.taskId = merged.task_id;
        if (merged.imageId == null && merged.image_id != null) merged.imageId = merged.image_id;
        if (merged.imageUrl == null && merged.image_url != null) merged.imageUrl = merged.image_url;
        if (merged.originalUrl == null && merged.original_url != null) merged.originalUrl = merged.original_url;
        if (merged.previewUrl == null && merged.preview_url != null) merged.previewUrl = merged.preview_url;
        if (merged.dataUrl == null && merged.data_url != null) merged.dataUrl = merged.data_url;
        if (merged.croppedImageUrl == null && merged.cropped_image_url != null) merged.croppedImageUrl = merged.cropped_image_url;
        if (merged.compositeUrl == null && merged.composite_url != null) merged.compositeUrl = merged.composite_url;
        return merged;
      });
    };
    const raw = (_ref0 = (_res$titles2 = res === null || res === void 0 ? void 0 : res.titles) !== null && _res$titles2 !== void 0 ? _res$titles2 : res === null || res === void 0 ? void 0 : res.data) !== null && _ref0 !== void 0 ? _ref0 : [];
    const list = normalizeRows(raw);
    const target = String(taskId !== null && taskId !== void 0 ? taskId : '');
    const titles = list.filter(item => {
      var _ref1, _ref10, _ref11, _ref12, _ref13, _item$taskId2, _item$task3, _item$task4, _item$data3, _item$data4;
      const candidateTaskId = (_ref1 = (_ref10 = (_ref11 = (_ref12 = (_ref13 = (_item$taskId2 = item === null || item === void 0 ? void 0 : item.taskId) !== null && _item$taskId2 !== void 0 ? _item$taskId2 : item === null || item === void 0 ? void 0 : item.task_id) !== null && _ref13 !== void 0 ? _ref13 : item === null || item === void 0 ? void 0 : (_item$task3 = item.task) === null || _item$task3 === void 0 ? void 0 : _item$task3.id) !== null && _ref12 !== void 0 ? _ref12 : item === null || item === void 0 ? void 0 : (_item$task4 = item.task) === null || _item$task4 === void 0 ? void 0 : _item$task4.taskId) !== null && _ref11 !== void 0 ? _ref11 : item === null || item === void 0 ? void 0 : (_item$data3 = item.data) === null || _item$data3 === void 0 ? void 0 : _item$data3.taskId) !== null && _ref10 !== void 0 ? _ref10 : item === null || item === void 0 ? void 0 : (_item$data4 = item.data) === null || _item$data4 === void 0 ? void 0 : _item$data4.task_id) !== null && _ref1 !== void 0 ? _ref1 : '';
      return isSameTask(candidateTaskId, target);
    });
    return {
      ...res,
      titles
    };
  }),
  create: payload => request('/titles', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    title: (res === null || res === void 0 ? void 0 : res.title) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  update: (id, payload) => request(`/titles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    title: (res === null || res === void 0 ? void 0 : res.title) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  }))
};
titleAPI.getAllByTask = async (taskId, options = {}) => {
  const tid = String(taskId || '').trim();
  if (!tid) return { titles: [] };
  const pageSize = Math.min(options.pageSize || IPE_TASK_RECORD_PAGE_SIZE, 500);
  const all = [];
  for (let page = 1; page <= 200; page += 1) {
    const res = await titleAPI.getByTask(tid, { ...options, page, pageSize });
    const batch = Array.isArray(res?.titles) ? res.titles : [];
    if (batch.length === 0) break;
    all.push(...batch);
    if (batch.length < pageSize) break;
  }
  return { titles: all };
};
export const templateAPI = {
  getAll: (params = {}) => {
    const { signal, ...query } = params && typeof params === 'object' ? params : {};
    return request('/templates', {
      query,
      tokenType: 'funding',
      signal,
    }).then(res => {
      const rawList = Array.isArray(res === null || res === void 0 ? void 0 : res.templates) ? res.templates : Array.isArray(res === null || res === void 0 ? void 0 : res.data) ? res.data : [];
      const type = query === null || query === void 0 ? void 0 : query.type;
      const templates = type ? rawList.filter(item => String((item === null || item === void 0 ? void 0 : item.type) || '').trim() === String(type).trim()) : rawList;
      return {
        ...res,
        templates
      };
    });
  },
  getById: (id, params = {}) => {
    const { signal, slideId, fullSlides, ...query } = params && typeof params === 'object' ? params : {};
    if (slideId) query.slideId = slideId;
    if (fullSlides) query.fullSlides = true;
    return request(`/templates/${encodeURIComponent(id)}`, {
      query,
      tokenType: 'funding',
      signal,
    }).then(res => {
      const template = (res === null || res === void 0 ? void 0 : res.template) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null);
      return {
        ...res,
        template
      };
    }).catch(error => {
      const status = error?.status ?? error?.response?.status ?? error?.data?.status;
      if (status === 404 || status === 403) {
        throw new Error('模板不存在或无权访问');
      }
      throw error;
    });
  },
  create: payload => request('/templates', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: (res === null || res === void 0 ? void 0 : res.template) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  /**
   * 原子保存：meta + template-images + dirtySlideCanvases（后端同一事务）。
   * Body: { template, images, dirtySlideCanvases? }
   */
  saveBundle: (templateId, payload) => request(
    `/templates/${encodeURIComponent(templateId)}/save-bundle`,
    {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      timeoutMs: 600000,
    },
  ),
  /** 外置多页画布整表 */
  getSlideCanvases: (templateId) => request(
    `/template-slide-canvases/${encodeURIComponent(templateId)}`,
    { tokenType: 'funding' },
  ).then((res) => {
    const data = res?.data || res || {};
    const canvases = data.canvases || data.slideCanvases || data;
    return canvases && typeof canvases === 'object' && !Array.isArray(canvases) ? canvases : {};
  }),
  /** 单页画布（详情 ?slideId=） */
  getSlideCanvas: async (templateId, slideId) => {
    const res = await request(`/templates/${encodeURIComponent(templateId)}`, {
      query: { slideId },
      tokenType: 'funding',
    });
    const template = res?.template || res?.data || res;
    const td = template?.templateData || {};
    const map = td.slideCanvases || {};
    return map[slideId] || td.fabricCanvas || null;
  },
  update: (id, payload) => request(`/templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: (res === null || res === void 0 ? void 0 : res.template) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  delete: id => request(`/templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};
export const templateGroupAPI = {
  getAll: () => request('/template-groups', {
    tokenType: 'funding'
  }),
  create: (type, name) => request('/template-groups', {
    method: 'POST',
    data: {
      type,
      name
    },
    tokenType: 'funding'
  }),
  delete: (type, name) => request('/template-groups', {
    method: 'DELETE',
    query: {
      type,
      name
    },
    tokenType: 'funding'
  })
};
export const templateImageAPI = {
  getByTemplate: templateId => request(`/template-images/${encodeURIComponent(templateId)}`, {
    tokenType: 'funding'
  }).then(res => {
    // 兼容多种后端返回格式：
    // 1) { images: {...} }
    // 2) { data: { images: {...} } }
    // 3) { data: {...} }（data 本身就是图片映射）
    const data = res === null || res === void 0 ? void 0 : res.data;
    let images = res === null || res === void 0 ? void 0 : res.images;
    if (!images && data && typeof data === 'object' && data.images && typeof data.images === 'object') {
      images = data.images;
    }
    if (!images && data && typeof data === 'object' && !Array.isArray(data)) {
      images = data;
    }
    return {
      ...res,
      images: images && typeof images === 'object' ? images : {}
    };
  }),
  batchSave: (templateId, images) => request(`/template-images/${encodeURIComponent(templateId)}/batch`, {
    method: 'POST',
    data: {
      images
    },
    tokenType: 'funding'
  }),
  deleteByTemplate: templateId => request(`/template-images/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  }),
  delete: (templateId, imageId) => request(`/template-images/${encodeURIComponent(templateId)}/${encodeURIComponent(imageId)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};
function normalizeCompositeRowsToMap(rows = []) {
  const map = {};
  rows.forEach((item, idx) => {
    var _item$templateId, _item$groupIndex;
    const templateId = (_item$templateId = item === null || item === void 0 ? void 0 : item.templateId) !== null && _item$templateId !== void 0 ? _item$templateId : item === null || item === void 0 ? void 0 : item.template_id;
    const groupIndex = (_item$groupIndex = item === null || item === void 0 ? void 0 : item.groupIndex) !== null && _item$groupIndex !== void 0 ? _item$groupIndex : item === null || item === void 0 ? void 0 : item.group_index;
    const key = templateId !== undefined && templateId !== null && groupIndex !== undefined && groupIndex !== null ? `${templateId}-${groupIndex}` : `_flat-${idx}`;
    if (!Array.isArray(map[key])) map[key] = [];
    map[key].push({
      ...item,
      compositeUrl: (item === null || item === void 0 ? void 0 : item.compositeUrl) || (item === null || item === void 0 ? void 0 : item.composite_url) || (item === null || item === void 0 ? void 0 : item.dataUrl) || (item === null || item === void 0 ? void 0 : item.data_url) || (item === null || item === void 0 ? void 0 : item.url) || ''
    });
  });
  return map;
}
function normalizeResponseToMap(res) {
  if (!res || typeof res !== 'object') return {};
  const directImages = res === null || res === void 0 ? void 0 : res.images;
  if (directImages && typeof directImages === 'object' && !Array.isArray(directImages)) {
    return directImages;
  }
  const data = res === null || res === void 0 ? void 0 : res.data;
  if (data && typeof data === 'object' && data.images != null) {
    const nested = data.images;
    if (Array.isArray(nested)) {
      return normalizeCompositeRowsToMap(nested);
    }
    if (typeof nested === 'object') {
      return nested;
    }
  }
  if (Array.isArray(directImages)) {
    return normalizeCompositeRowsToMap(directImages);
  }
  if (Array.isArray(data)) {
    const allRows = data.flatMap(row => Array.isArray(row === null || row === void 0 ? void 0 : row.images) ? row.images : []);
    return normalizeCompositeRowsToMap(allRows);
  }
  return {};
}
export const compositeImageAPI = {
  getByTask: async (taskId, { includeAllBatchRows = false } = {}) => {
    const base = await request(`/composite-images/${encodeURIComponent(taskId)}`, {
      tokenType: 'funding'
    });
    if (includeAllBatchRows) {
      try {
        const allRows = await compositeImageAPI.getAllRowsByTask(taskId);
        if (allRows.length > 0) {
          return {
            ...base,
            images: normalizeCompositeRowsToMap(allRows)
          };
        }
      } catch (_) {
        /* fall through */
      }
    }

    let images = normalizeResponseToMap(base);
    if (Object.keys(images).length > 0) {
      return {
        ...base,
        images
      };
    }

    try {
      const batchRes = await request(`/composite-images/${encodeURIComponent(taskId)}/batch`, {
        query: {
          page: 1,
          pageSize: 100
        },
        tokenType: 'funding'
      });
      images = normalizeResponseToMap(batchRes);
      return {
        ...base,
        images
      };
    } catch (_) {
      return {
        ...base,
        images: {}
      };
    }
  },
  /** 拉平任务下所有 batch 记录中的 images 行（分页，用于大任务详情） */
  getAllRowsByTask: async taskId => {
    const tid = String(taskId || '').trim();
    if (!tid) return [];
    const flattenBatchPayload = (batchRes) => {
      const all = [];
      const rows = Array.isArray(batchRes === null || batchRes === void 0 ? void 0 : batchRes.data)
        ? batchRes.data
        : [];
      rows.forEach(row => {
        if (Array.isArray(row === null || row === void 0 ? void 0 : row.images)) {
          row.images.forEach(img => all.push(img));
        } else if (row !== null && row !== void 0 && row.data && Array.isArray(row.data.images)) {
          row.data.images.forEach(img => all.push(img));
        } else if (row && (row.imageId || row.templateId || row.dataUrl || row.url)) {
          all.push(row);
        }
      });
      return all;
    };
    const all = [];
    const pageSize = 100;
    const maxPages = 10;
    for (let page = 1; page <= maxPages; page += 1) {
      try {
        const batchRes = await request(`/composite-images/${encodeURIComponent(tid)}/batch`, {
          query: { page, pageSize },
          tokenType: 'funding'
        });
        const chunk = flattenBatchPayload(batchRes);
        if (chunk.length === 0) break;
        all.push(...chunk);
        const total = Number(batchRes === null || batchRes === void 0 ? void 0 : batchRes.total) || 0;
        const rowCount = Array.isArray(batchRes === null || batchRes === void 0 ? void 0 : batchRes.data)
          ? batchRes.data.length
          : 0;
        if (total > 0 && page * pageSize >= total) break;
        if (rowCount < pageSize) break;
      } catch {
        break;
      }
    }
    if (all.length > 0) return all;
    try {
      const base = await request(`/composite-images/${encodeURIComponent(tid)}`, {
        tokenType: 'funding'
      });
      const images = normalizeResponseToMap(base);
      const flat = [];
      Object.values(images || {}).forEach(val => {
        if (Array.isArray(val)) flat.push(...val);
        else if (val && typeof val === 'object') flat.push(val);
      });
      return flat;
    } catch {
      return [];
    }
  },
  batchSave: (taskId, images, overwrite = false, billCredits = null) => {
    const data = {
      images,
      overwrite
    };
    if (billCredits === true || (billCredits !== false && overwrite === true)) {
      data.ipeBillCredits = true;
    }
    return request(`/composite-images/${encodeURIComponent(taskId)}/batch`, {
      method: 'POST',
      data,
      tokenType: 'funding'
    });
  },
  /** 删除任务关联的合成图 compat 记录（任务删除前 best-effort 清理） */
  deleteByTask: async taskId => {
    const tid = String(taskId || '').trim();
    if (!tid) return;
    try {
      const batchRes = await request(`/composite-images/${encodeURIComponent(tid)}/batch`, {
        query: {
          pageSize: 1000
        },
        tokenType: 'funding'
      });
      const rows = Array.isArray(batchRes === null || batchRes === void 0 ? void 0 : batchRes.data) ? batchRes.data : [];
      await Promise.allSettled(rows.map(row => {
        const recordId = row === null || row === void 0 ? void 0 : row.id;
        if (recordId == null) return Promise.resolve();
        return request(`/composite-images/${encodeURIComponent(tid)}/batch/${encodeURIComponent(String(recordId))}`, {
          method: 'DELETE',
          tokenType: 'funding'
        });
      }));
    } catch (_) {
      // 无合成图或已清理时忽略
    }
  }
};
export const replacedImageAPI = {
  getByComposite: (taskId, compositeId) => request(`/replaced-images/${encodeURIComponent(taskId)}/${encodeURIComponent(compositeId)}`, {
    tokenType: 'funding'
  }),
  batchSave: replacements => request('/replaced-images/batch', {
    method: 'POST',
    data: {
      replacements
    },
    tokenType: 'funding'
  })
};
const isCompatDuplicateError = error => {
  var _error$response, _error$response$data, _error$data;
  const msg = String((error === null || error === void 0 ? void 0 : error.message) || (error === null || error === void 0 ? void 0 : (_error$response = error.response) === null || _error$response === void 0 ? void 0 : (_error$response$data = _error$response.data) === null || _error$response$data === void 0 ? void 0 : _error$response$data.message) || (error === null || error === void 0 ? void 0 : (_error$data = error.data) === null || _error$data === void 0 ? void 0 : _error$data.message) || '');
  return msg.includes('Duplicate entry') || msg.includes('uk_compat_api_record');
};
function normalizeListingTemplateList(res) {
  const templates = Array.isArray(res?.templates)
    ? res.templates
    : Array.isArray(res?.data)
      ? res.data
      : [];
  return { ...res, templates };
}

/** 元数据列表分页拉取（默认 pageSize=100，上限 200；取消一次性 pageSize=1000） */
async function fetchListingTemplatesPaged(params = {}) {
  const { signal, ...queryParams } = params && typeof params === 'object' ? params : {};
  const pageSize = Math.min(200, Math.max(1, Number(queryParams.pageSize) || 100));
  if (queryParams.page != null) {
    const res = await request('/listing-templates', {
      query: { ...queryParams, page: queryParams.page, pageSize },
      tokenType: 'funding',
      signal,
    });
    return normalizeListingTemplateList(res);
  }

  let page = 1;
  let totalPages = 1;
  let templates = [];
  let lastRes = null;
  const maxPages = 50;
  do {
    if (signal?.aborted) throw createAbortError();
    lastRes = await request('/listing-templates', {
      query: { ...queryParams, page, pageSize },
      tokenType: 'funding',
      signal,
    });
    const batch = normalizeListingTemplateList(lastRes).templates;
    templates = templates.concat(batch);
    totalPages = Math.max(1, Number(lastRes?.totalPages) || 1);
    page += 1;
  } while (page <= totalPages && page <= maxPages);

  return {
    ...lastRes,
    templates,
    data: templates,
    total: templates.length,
    page: 1,
    pageSize: templates.length,
    totalPages: 1,
  };
}

export const listingTemplateAPI = {
  getAll: (params = {}) => fetchListingTemplatesPaged(params),
  getById: async id => {
    try {
      const res = await request(`/listing-templates/${encodeURIComponent(id)}`, {
        tokenType: 'funding',
      });
      const template = res?.template || res?.data || (res && res.id ? res : null);
      if (!template) {
        throw new Error('模板不存在或无权访问');
      }
      return { ...res, template };
    } catch (error) {
      const status = error?.status ?? error?.response?.status ?? error?.data?.status;
      if (status === 404 || status === 403) {
        throw new Error('模板不存在或无权访问');
      }
      throw error;
    }
  },
  create: async payload => {
    try {
      const res = await request('/listing-templates', {
        method: 'POST',
        data: payload,
        tokenType: 'funding'
      });
      return {
        ...res,
        template: (res === null || res === void 0 ? void 0 : res.template) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
      };
    } catch (error) {
      const id = payload === null || payload === void 0 ? void 0 : payload.id;
      if (id && isCompatDuplicateError(error)) {
        return listingTemplateAPI.update(id, payload);
      }
      throw error;
    }
  },
  update: (id, payload) => request(`/listing-templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }).then(res => ({
    ...res,
    template: (res === null || res === void 0 ? void 0 : res.template) || (res === null || res === void 0 ? void 0 : res.data) || (res && res.id ? res : null)
  })),
  delete: id => request(`/listing-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};

function normalizePodCompatList(res, aliasKey) {
  const list = Array.isArray(res?.[aliasKey])
    ? res[aliasKey]
    : Array.isArray(res?.data)
      ? res.data
      : [];
  return { ...res, [aliasKey]: list, data: list };
}

function wrapPodCompatRecordError(error, label = '记录') {
  const status = error?.status ?? error?.response?.status ?? error?.data?.status;
  if (status === 404 || status === 403) {
    throw new Error(`${label}不存在或无权访问`);
  }
  throw error;
}

/** POD 印花素材空间（compat/pod/material-space）：服务端 workspace 隔离 */
export const podMaterialSpaceAPI = {
  getAll: (params = {}) => {
    const { signal, ...query } = params && typeof params === 'object' ? params : {};
    return request('/pod/material-space', {
      query,
      tokenType: 'funding',
      signal,
    }).then((res) => normalizePodCompatList(res, 'materials'));
  },
  getById: async (id) => {
    try {
      const res = await request(`/pod/material-space/${encodeURIComponent(id)}`, {
        tokenType: 'funding',
      });
      const material = res?.material || res?.data || (res && res.id ? res : null);
      if (!material) {
        throw new Error('素材不存在或无权访问');
      }
      return { ...res, material };
    } catch (error) {
      wrapPodCompatRecordError(error, '素材');
    }
  },
  create: (payload) => request('/pod/material-space', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    material: res?.material || res?.data || (res && res.id ? res : null),
  })),
  update: (id, payload) => request(`/pod/material-space/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    material: res?.material || res?.data || (res && res.id ? res : null),
  })),
  delete: (id) => request(`/pod/material-space/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

/** POD 成品库（compat/pod/finished-products）：服务端 workspace 隔离 */
export const podFinishedProductsAPI = {
  getAll: (params = {}) => {
    const { signal, ...query } = params && typeof params === 'object' ? params : {};
    return request('/pod/finished-products', {
      query,
      tokenType: 'funding',
      signal,
    }).then((res) => normalizePodCompatList(res, 'products'));
  },
  getById: async (id) => {
    try {
      const res = await request(`/pod/finished-products/${encodeURIComponent(id)}`, {
        tokenType: 'funding',
      });
      const product = res?.product || res?.data || (res && res.id ? res : null);
      if (!product) {
        throw new Error('成品不存在或无权访问');
      }
      return { ...res, product };
    } catch (error) {
      wrapPodCompatRecordError(error, '成品');
    }
  },
  create: (payload) => request('/pod/finished-products', {
    method: 'POST',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    product: res?.product || res?.data || (res && res.id ? res : null),
  })),
  update: (id, payload) => request(`/pod/finished-products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding',
  }).then((res) => ({
    ...res,
    product: res?.product || res?.data || (res && res.id ? res : null),
  })),
  delete: (id) => request(`/pod/finished-products/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding',
  }),
};

function normalizeListingTemplateFilePayload(res) {
  if (!res || typeof res !== 'object') return null;
  const file = res.file ?? res.data ?? (res.fileData || res.file_data ? res : null);
  if (!file || typeof file !== 'object') return null;
  const fileData = file.fileData ?? file.file_data;
  if (!fileData) return null;
  return {
    ...file,
    id: file.id,
    templateId: file.templateId ?? file.template_id,
    fileData,
    fileName: file.fileName ?? file.file_name ?? 'template.xlsx',
    platform: file.platform ?? '',
    tableData: file.tableData ?? file.table_data ?? null,
    tableMetadata: file.tableMetadata ?? file.table_metadata ?? {},
  };
}

export const listingTemplateFileAPI = {
  /**
   * 按约定 recordId（templateId / file-{templateId}）直查；不再 pageSize=1000 扫全库。
   * 详情接口会从外置存储回填 fileData。
   */
  getByTemplate: async templateId => {
    const tid = String(templateId ?? '').trim();
    if (!tid) return { file: null };

    const fetchRecord = async recordId => {
      try {
        const res = await request(`/listing-template-files/${encodeURIComponent(recordId)}`, {
          tokenType: 'funding'
        });
        return normalizeListingTemplateFilePayload(res);
      } catch {
        return null;
      }
    };

    // 现行保存统一使用 file-{templateId}
    let file = await fetchRecord(`file-${tid}`);
    if (file?.fileData) return { file };

    file = await fetchRecord(tid);
    if (file?.fileData) return { file };

    return { file: null };
  },
  save: async payload => {
    try {
      return await request('/listing-template-files', {
        method: 'POST',
        data: payload,
        tokenType: 'funding'
      });
    } catch (error) {
      const id = payload === null || payload === void 0 ? void 0 : payload.id;
      if (id && isCompatDuplicateError(error)) {
        return request(`/listing-template-files/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          data: payload,
          tokenType: 'funding'
        });
      }
      throw error;
    }
  }
};
function normalizeCropImagesPayload(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'object') return [];
  if (Array.isArray(raw.images)) return raw.images;
  if (Array.isArray(raw.records)) return raw.records;
  if (Array.isArray(raw.items)) return raw.items;
  if (Array.isArray(raw.list)) return raw.list;
  if (Array.isArray(raw.rows)) return raw.rows;
  if (raw.imageId || raw.croppedImageUrl || raw.originalImageUrl || raw.cropped_image_url || raw.original_image_url) {
    return [raw];
  }
  const vals = Object.values(raw).filter(v => v && typeof v === 'object');
  if (vals.length > 0 && vals.every(v => v.imageId != null || v.croppedImageUrl != null || v.originalImageUrl != null)) {
    return vals;
  }
  return [];
}
function normalizeCropImagesFromApiResponse(res) {
  if (!res || typeof res !== 'object') return [];
  let raw = res.images;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const nested = normalizeCropImagesPayload(raw);
    if (nested.length > 0) return nested;
  }
  const data = res.data;
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    return normalizeCropImagesPayload(data);
  }
  return [];
}
function normalizeCropImageRowFromApi(row) {
  if (!row || typeof row !== 'object') return null;
  const imageId = row.imageId ?? row.image_id ?? row.id;
  if (imageId == null || String(imageId).trim() === '') return null;
  return {
    ...row,
    imageId: String(imageId).trim(),
    originalImageUrl: row.originalImageUrl ?? row.original_image_url ?? row.originalUrl ?? '',
    croppedImageUrl: row.croppedImageUrl ?? row.cropped_image_url ?? row.croppedUrl ?? row.url ?? '',
    status: row.status ?? row.crop_status ?? '',
  };
}

function isMeaningfulCropRow(row) {
  const n = normalizeCropImageRowFromApi(row);
  if (!n) return false;
  const url = String(n.croppedImageUrl || '').trim();
  if (url) return true;
  const st = String(n.status || '').toLowerCase();
  return ['failed', 'processing', 'success', 'partial'].includes(st);
}

function mergeCropRowsByImageId(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const n = normalizeCropImageRowFromApi(row);
    if (!n || !isMeaningfulCropRow(n)) return;
    const key = String(n.imageId);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, n);
      return;
    }
    const prevOk = Boolean(String(prev.croppedImageUrl || '').trim());
    const nextOk = Boolean(String(n.croppedImageUrl || '').trim());
    if (nextOk && !prevOk) map.set(key, n);
  });
  return Array.from(map.values());
}

const CROP_IMAGES_BATCH_PAGE_SIZE = 100;
/** 任务子记录（裁剪/增强/标题等）分页默认每页条数 */
export const IPE_TASK_RECORD_PAGE_SIZE = CROP_IMAGES_BATCH_PAGE_SIZE;

async function fetchPaginatedApiRecords(path, {
  pageSize = IPE_TASK_RECORD_PAGE_SIZE,
  tokenType = 'funding',
  extraQuery = {},
  normalizePage = () => [],
  maxPages = 200,
} = {}) {
  const all = [];
  const safeSize = Math.max(1, Math.min(pageSize, 500));
  for (let page = 1; page <= maxPages; page += 1) {
    try {
      const res = await request(path, {
        query: { page, pageSize: safeSize, ...extraQuery },
        tokenType,
      });
      const chunk = normalizePage(res);
      if (!chunk.length) break;
      all.push(...chunk);
      const total = Number(res?.total) || 0;
      const rowCount = Array.isArray(res?.data) ? res.data.length : chunk.length;
      if (total > 0 && page * safeSize >= total) break;
      if (rowCount < safeSize) break;
    } catch {
      break;
    }
  }
  return all;
}

/** 分页拉取任务下全部裁剪记录（1000 张约 10 次请求） */
async function fetchAllCropImagesByTaskBatch(taskId, { pageSize = CROP_IMAGES_BATCH_PAGE_SIZE } = {}) {
  const tid = String(taskId || '').trim();
  if (!tid) return [];
  const rows = await fetchPaginatedApiRecords(
    `/crop-images/${encodeURIComponent(tid)}/batch`,
    {
      pageSize,
      normalizePage: (res) => normalizeCropImagesFromApiResponse(res),
    },
  );
  return mergeCropRowsByImageId(rows);
}

function trimCropApiString(value) {
  return value == null ? '' : String(value).trim();
}

/** T3-1.2：提交批任务前规范化 images（去重、过滤空字段） */
export function normalizeCropTaskSubmitPayload({ taskId, images, mode = 'qwen', force = false } = {}) {
  const tid = trimCropApiString(taskId);
  const normalizedMode = trimCropApiString(mode) || 'qwen';
  const out = [];
  const seen = new Set();
  if (Array.isArray(images)) {
    for (const item of images) {
      const imageId = trimCropApiString(item?.imageId ?? item?.id);
      const imageUrl = trimCropApiString(item?.imageUrl ?? item?.url ?? item?.originalImageUrl);
      if (!imageId || !imageUrl || seen.has(imageId)) continue;
      seen.add(imageId);
      out.push({ imageId, imageUrl });
    }
  }
  return {
    taskId: tid,
    images: out,
    mode: normalizedMode,
    force: Boolean(force),
  };
}

function requireCropTaskId(taskId) {
  const tid = trimCropApiString(taskId);
  if (!tid) throw new Error('taskId is required');
  return tid;
}

export const cropImageAPI = {
  getByTask: async (taskId, { imageIds } = {}) => {
    const normalizeOne = (baseRes) => {
      const images = normalizeCropImagesFromApiResponse(baseRes);
      return { ...baseRes, images };
    };

    const ids = Array.isArray(imageIds)
      ? [...new Set(imageIds.filter(Boolean).map(String))]
      : [];
    const idSet = ids.length > 0 ? new Set(ids) : null;

    let rows = [];
    try {
      rows = await fetchAllCropImagesByTaskBatch(taskId);
    } catch (_) {
      // ignore
    }

    if (rows.length === 0) {
      try {
        const base = await request(`/crop-images/${encodeURIComponent(taskId)}`, {
          tokenType: 'funding',
        });
        rows = normalizeOne(base).images;
      } catch (_) {
        // ignore
      }
    }

    rows = mergeCropRowsByImageId(rows);
    if (idSet) {
      rows = rows.filter((row) => idSet.has(String(row.imageId)));
    }

    return { images: rows };
  },
  /** T2-3.1 / T3-1.2：提交 Qwen 图案裁剪批任务（服务端 Worker 异步处理） */
  submitTask: ({ taskId, images, mode = 'qwen' } = {}) => {
    const payload = normalizeCropTaskSubmitPayload({ taskId, images, mode });
    if (!payload.taskId) throw new Error('taskId is required');
    if (payload.images.length === 0) throw new Error('images is required');
    return request('/crop-images/tasks', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
    });
  },
  /** T2-3.2 / T3-1.2：分页拉取任务裁剪记录（单页） */
  listBatchPage: (taskId, { page = 1, pageSize = CROP_IMAGES_BATCH_PAGE_SIZE } = {}) => {
    const tid = requireCropTaskId(taskId);
    const safePage = Math.max(1, Number(page) || 1);
    const safeSize = Math.max(1, Math.min(Number(pageSize) || CROP_IMAGES_BATCH_PAGE_SIZE, 500));
    return request(
      `/crop-images/${encodeURIComponent(tid)}/batch`,
      {
        query: { page: safePage, pageSize: safeSize },
        tokenType: 'funding',
      },
    );
  },
  /** T2-3 / T3-1.2：单张同步裁剪（legacy；Qwen 批任务重试请用 retryQwenWorkerItem / cropByBbox） */
  cropSingle: ({ taskId, imageId, imageUrl } = {}) => {
    const tid = requireCropTaskId(taskId);
    const iid = trimCropApiString(imageId);
    const url = trimCropApiString(imageUrl);
    if (!iid) throw new Error('imageId is required');
    if (!url) throw new Error('imageUrl is required');
    return request('/crop-images/crop', {
      method: 'POST',
      data: {
        taskId: tid,
        imageId: iid,
        imageUrl: url,
      },
      tokenType: 'funding',
    });
  },
  /** 旧版整任务单条记录：GET /crop-images/{taskId} */
  getTaskAggregate: async (taskId) => {
    try {
      const res = await request(`/crop-images/${encodeURIComponent(taskId)}`, {
        tokenType: 'funding',
      });
      return res?.data ?? res;
    } catch {
      return null;
    }
  },
  save: (taskId, payload) => {
    const imageId = payload?.imageId != null ? String(payload.imageId).trim() : '';
    const path = imageId
      ? `/crop-images/${encodeURIComponent(taskId)}/${encodeURIComponent(imageId)}`
      : `/crop-images/${encodeURIComponent(taskId)}`;
    return request(path, {
      method: 'POST',
      data: {
        ...payload,
        id: imageId || payload?.id,
      },
      tokenType: 'funding',
    });
  },
  updateName: (imageId, name) => request(`/crop-images/name/${encodeURIComponent(imageId)}`, {
    method: 'PATCH',
    data: {
      name
    },
    tokenType: 'funding'
  }),
  deleteImage: (taskId, imageId) => {
    const tid = requireCropTaskId(taskId);
    const iid = trimCropApiString(imageId);
    if (!iid) throw new Error('imageId is required');
    return request(
      `/crop-images/${encodeURIComponent(tid)}/${encodeURIComponent(iid)}`,
      { method: 'DELETE', tokenType: 'funding' },
    );
  },
  /** T2-3.3：已知 bbox 单张裁剪（重试 / 调试） */
  cropByBbox: ({
    taskId,
    imageId,
    imageUrl,
    bbox,
    confidence,
    label,
    printIndex,
  }) => request('/crop-images/crop-by-bbox', {
    method: 'POST',
    data: {
      taskId,
      imageId,
      imageUrl,
      bbox,
      confidence,
      label,
      printIndex: printIndex == null ? undefined : Number(printIndex),
    },
    tokenType: 'funding',
  }),
  /** T3-1.1：Qwen Worker 单张重试（detect + crop，无 bbox 时） */
  retryQwenWorkerItem: ({ taskId, imageId, imageUrl } = {}) => {
    const tid = requireCropTaskId(taskId);
    const iid = trimCropApiString(imageId);
    const url = trimCropApiString(imageUrl);
    if (!iid) throw new Error('imageId is required');
    if (!url) throw new Error('imageUrl is required');
    return request('/crop-images/retry', {
      method: 'POST',
      data: {
        taskId: tid,
        imageId: iid,
        imageUrl: url,
      },
      tokenType: 'funding',
    });
  },
  /**
   * 删除任务关联的裁剪元数据（不删图片空间文件）。
   * 一次 DELETE 清理子记录 + 聚合；禁止再分页拉全量后逐条 DELETE（易卡死主线程）。
   */
  deleteByTask: async (taskId) => {
    const tid = String(taskId || '').trim();
    if (!tid) return;
    try {
      await request(`/crop-images/${encodeURIComponent(tid)}`, {
        method: 'DELETE',
        tokenType: 'funding',
      });
    } catch (_) {
      // 无裁剪记录或已清理时忽略
    }
  },
};

/** T3-1.2：独立 export，等价于 cropImageAPI.submitTask */
export async function submitImageCropTaskRequest(params) {
  return cropImageAPI.submitTask(params);
}

/** T3-1.2：独立 export，等价于 cropImageAPI.listBatchPage */
export async function pollImageCropBatchPage(taskId, page = 1, options = {}) {
  return cropImageAPI.listBatchPage(taskId, { page, ...options });
}

/** T3-1.2：独立 export，等价于 cropImageAPI.cropSingle */
export async function retryImageCropSingleRequest(params) {
  return cropImageAPI.cropSingle(params);
}

/** IPE 内置抠图（不依赖灵图） */
export const cutoutAPI = {
  health: () => request('/cutout/health', { tokenType: 'funding' }),
  process: (imageUrl, requestId) => request('/cutout/process', {
    method: 'POST',
    data: { imageUrl, requestId },
    tokenType: 'funding',
  }),
};
export const lingtuImageAPI = {
  detectSubmit: (imageUrl, requestId) => request('/lingtu-image/detect/submit', {
    method: 'POST',
    data: {
      imageUrl,
      requestId
    },
    tokenType: 'funding'
  }),
  cutoutSubmit: ({
    imageUrl,
    image,
    requestId,
    type = 0,
    formats = ['png'],
    enableCrop = false,
    mode = 'basic',
  } = {}) => {
    const url = imageUrl || image;
    return request('/lingtu-image/cutout/submit', {
      method: 'POST',
      data: {
        imageUrl: url,
        image: url,
        requestId,
        type,
        formats,
        enableCrop,
        mode,
      },
      tokenType: 'funding',
    });
  },
  getResult: jobId => request('/lingtu-image/result', {
    method: 'POST',
    data: {
      jobId
    },
    tokenType: 'funding'
  }),
  upscaleSubmit: ({
    imageUrl,
    image,
    requestId,
    type = '0',
    multiple = 2,
    denoise = false,
  } = {}) => {
    const url = imageUrl || image;
    return request('/lingtu-image/upscale/submit', {
      method: 'POST',
      data: {
        imageUrl: url,
        image: url,
        requestId,
        type,
        multiple,
        denoise,
      },
      tokenType: 'funding',
    });
  },
};
export const lingtuTitleAPI = {
  submitAndWait: (payloadOrImage, prompt, imageId) => {
    const payload = payloadOrImage && typeof payloadOrImage === 'object' && !Array.isArray(payloadOrImage) ? payloadOrImage : {
      image: payloadOrImage,
      prompt,
      imageId
    };
    return request('/lingtu-title/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding'
    });
  },
  cancel: (jobId, reason) => request('/lingtu-title/cancel', {
    method: 'POST',
    data: {
      jobId,
      reason
    },
    tokenType: 'funding'
  })
};

/**
 * 灵图开放 AIGC 透传（后端 LingtuOpenApiController → 网关签名）。
 * 需配置 LINGTU_BASE_URL、LINGTU_APP_ID、LINGTU_APP_SECRET 后才会真正转发灵图。
 */
export const lingtuOpenAPI = {
  text2img: data => request('/lingtu-open/text2img', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  text2imgPro: data => request('/lingtu-open/text2img-pro', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  img2img: data => request('/lingtu-open/img2img', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  expand: data => request('/lingtu-open/expand', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  img2text: data => request('/lingtu-open/img2text', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  jobResult: jobId => request('/lingtu-open/job/result', {
    method: 'POST',
    data: {
      jobId
    },
    tokenType: 'funding'
  }),
  jobCancel: (jobId, reason) => request('/lingtu-open/job/cancel', {
    method: 'POST',
    data: {
      reason,
      jobId
    },
    tokenType: 'funding'
  }),
  synthesisDress: data => request('/lingtu-open/synthesis/dress', {
    method: 'POST',
    data,
    tokenType: 'funding'
  }),
  listSynthesisBgs: () => request('/lingtu-open/synthesis/change-background/bgs', {
    tokenType: 'funding'
  }),
  listModelFaces: () => request('/lingtu-open/synthesis/model/faces', {
    tokenType: 'funding'
  })
};

/** 万能改图：描述词 + 目标图片（最多 500 张公网 URL），每次生成 1 张 */
export const img2imgAPI = {
  submitAndWait: (payload, { timeoutMs = 600000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/img2img/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** AI 扩图：灵图 /aigc/expand（type=1 倍数 / type=2 宽高比） */
export const aiOutpaintAPI = {
  submitAndWait: (payload, { timeoutMs = 600000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/ai-outpaint/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** AI 扩图：灵图 /aigc/expand */
export const aiTextToImageAPI = {
  submitAndWait: (payload, { timeoutMs = 600000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/ai-text-to-image/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** AI 文案：Qwen-VL-Plus 图像理解 + 用户指令 */
export const aiCopywritingAPI = {
  submitAndWait: (payload, { timeoutMs = 300000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/ai-copywriting/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 主图创作：qwen-image-2.0 + 固定功能词 */
export const goodsStudioAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/goods-studio/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 模特试衣：qwen-image-2.0 + 后端功能提示词；系统模特列表见 systemModelsAPI */
export const modelTryOnAPI = {
  /** 兼容旧 manifest 预设图：静态 assets 或 systemModels 已带 displayUrl 时无需调用 */
  fetchPresetImageBlob: async ({ id, modelType, garmentType, assetRev } = {}) => {
    await waitForIpeAuthReady();
    const path = '/model-try-on/presets/image';
    const { token, ipeCall } = pickAuthForRequest('funding', path);
    if (!token) {
      throw new Error('请先登录后再使用此功能');
    }
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    if (ipeCall) {
      headers['X-IPE-Workspace'] = getIpeWorkspaceMode();
    }
    const query = { id, modelType, garmentType };
    if (assetRev != null && String(assetRev).trim()) {
      query.v = String(assetRev).trim();
    }
    const response = await fetch(buildUrl(path, query), {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error('系统模特图加载失败');
    }
    return response.blob();
  },
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/model-try-on/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 手持商品：qwen-image-2.0 + 后端功能提示词 */
export const modelHandheldAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/model-handheld/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 姿势裂变：qwen-image-2.0 + 后端功能提示词 */
export const modelPoseFissionAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/model-pose-fission/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 万物穿戴：qwen-image-2.0 + 后端功能提示词 */
export const modelUniversalWearAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/model-universal-wear/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 印花提取：qwen-image-2.0 + 后端功能提示词，同步等待任务结果 */
export const stampExtractAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/stamp-extract/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** 抠图精修 · 局部区域千问重生（按智能抠图模块扣费） */
export const smartCutoutRefineAPI = {
  regionalRegenerate: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/smart-cutout-refine/regional-regenerate', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

/** @deprecated 智能抠图已迁移至 cutout-service，请使用 /api/lingtu-image/cutout/submit */
export const smartCutoutQwenAPI = {
  submitAndWait: (payload, { timeoutMs = 900000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/smart-cutout-qwen/submit-and-wait', {
      method: 'POST',
      data: payload,
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
};

export const imageEnhanceAPI = {
  getEngineStatus: () => request('/image-enhance/engine-status', { tokenType: 'funding' }),
  submit: ({
    requestId,
    imageUrl,
    image,
    taskId,
    imageId,
  } = {}) => request('/image-enhance/submit', {
    method: 'POST',
    data: {
      requestId,
      imageUrl: imageUrl || image,
      image: imageUrl || image,
      taskId,
      imageId,
    },
    tokenType: 'funding',
  }),
  getResult: (jobId) => request('/image-enhance/result', {
    method: 'POST',
    data: { jobId },
    tokenType: 'funding',
  }),
  process: (taskId, imageId, imageUrl, _multipleIgnored, { timeoutMs = 600000 } = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return request('/image-enhance/process', {
      method: 'POST',
      data: {
        taskId,
        imageId,
        image: imageUrl,
      },
      tokenType: 'funding',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
  },
  getByTask: async (taskId, { pageSize = IPE_TASK_RECORD_PAGE_SIZE } = {}) => {
    const tid = String(taskId || '').trim();
    if (!tid) return { images: [], total: 0 };
    const images = await fetchPaginatedApiRecords(
      `/image-enhance/task/${encodeURIComponent(tid)}`,
      {
        pageSize,
        normalizePage: (res) => (Array.isArray(res?.images) ? res.images : []),
      },
    );
    return { images, total: images.length };
  },
  /** 删除任务关联的增强记录（任务删除前 best-effort 清理） */
  deleteByTask: async (taskId) => {
    const tid = String(taskId || '').trim();
    if (!tid) return;
    try {
      const res = await request(`/image-enhance/task/${encodeURIComponent(tid)}`, {
        tokenType: 'funding',
      });
      const images = res?.images;
      const rows = Array.isArray(images) ? images : Object.values(images || {});
      await Promise.allSettled(
        rows.map((row) => {
          const imageId = row?.imageId ?? row?.image_id ?? row?.id;
          if (imageId == null || String(imageId).trim() === '') return Promise.resolve();
          return request(`/image-enhance/image/${encodeURIComponent(String(imageId).trim())}`, {
            method: 'DELETE',
            tokenType: 'funding',
          });
        }),
      );
    } catch {
      // ignore compat cleanup errors
    }
  },
};
function isObjectStorageHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return (h.includes('.oss-') && h.includes('aliyuncs.com')) || h.includes('.myqcloud.com');
}

function normalizePersistedUploadUrl(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim().replace(/\\/g, '/');
  if (!s || s.startsWith('data:')) return '';
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      if (u.pathname.startsWith('/uploads/')) return u.pathname;
      if (isObjectStorageHost(u.hostname)) return s;
    } catch {
      return '';
    }
    return '';
  }
  if (s.startsWith('/uploads/')) return s;
  if (s.startsWith('uploads/')) return `/${s}`;
  const idx = s.indexOf('/uploads/');
  if (idx >= 0) return s.slice(idx);
  return '';
}

function normalizeUploadResponseUrl(result) {
  if (!result || typeof result !== 'object') return result;
  const raw = result.url ?? result.data?.url;
  if (!raw || typeof raw !== 'string') return result;
  const persisted = normalizePersistedUploadUrl(raw);
  if (persisted) return { ...result, url: persisted };
  return result;
}

async function registerUploadedImageToSpace({
  url,
  id,
  name,
  source,
  size,
  width,
  height,
  contentHash,
  registerImageSpace = true,
  ingestKind = 'upload',
  fileMeta = {},
}) {
  if (registerImageSpace === false) return null;
  const persisted = normalizePersistedUploadUrl(url);
  if (!persisted) return null;

  // 后端 upload/complete 已写入图片空间并回传 id 时，不再拉 pageSize=5000 去重
  const serverId = id != null && id !== '' ? String(id) : '';
  if (serverId) {
    const niceName = toImageSpaceDisplayName(name || fileMeta.name || '') || name || fileMeta.name || '';
    if (niceName) {
      await syncImageSpaceDisplayName(serverId, niceName);
    }
    const { appendImageSpaceDedupeCache } = await import('./imageSpaceDedupe');
    appendImageSpaceDedupeCache([{
      id: serverId,
      name: niceName || serverId,
      url: persisted,
      contentHash: contentHash || fileMeta.contentHash || null,
      size: size ?? fileMeta.size ?? null,
      width: width ?? fileMeta.width ?? null,
      height: height ?? fileMeta.height ?? null,
      source,
    }]);
    return serverId;
  }

  const { findImageSpaceDuplicate, loadExistingImagesForDedupe, appendImageSpaceDedupeCache } = await import('./imageSpaceDedupe');
  const hash = contentHash || fileMeta.contentHash || null;
  const displayName = name || fileMeta.name || '';
  const existing = await loadExistingImagesForDedupe();
  const duplicate = findImageSpaceDuplicate(existing, {
    contentHash: hash,
    fileName: displayName,
    url: persisted,
    excludeId: id,
  });
  if (duplicate?.id) {
    return String(duplicate.id);
  }

  const recordId = id || `img-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const niceName = toImageSpaceDisplayName(displayName) || displayName || recordId;
  const [record] = enrichImageSpaceRecordsForIngest([{
    id: recordId,
    name: niceName,
    url: persisted,
    source,
    size,
    width,
    height,
    contentHash: hash || null,
    uploadedAt: now,
    createdAt: now,
  }], { ingestKind, fileMeta });
  try {
    const saveResult = await imageSpaceAPI.batchSave([record], { skipIngestAspect: true, dedupe: false });
    const saved = Array.isArray(saveResult?.images) ? saveResult.images[0] : null;
    const savedId = saved?.id ? String(saved.id) : recordId;
    appendImageSpaceDedupeCache([saved || record]);
    return savedId;
  } catch (e) {
    console.warn('[image-storage] 写入图片空间失败', e);
    return null;
  }
}
/** 与后端 ImageSpaceIngestService.recordIdFromStorageKey 对齐：从落盘 URL 推导图片空间 id */
function imageSpaceIdFromStoredUrl(url) {
  const persisted = normalizePersistedUploadUrl(String(url || ''));
  if (!persisted) return '';
  const path = persisted.split('?')[0];
  const filename = path.substring(path.lastIndexOf('/') + 1).trim();
  if (!filename) return '';
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.substring(0, dot) : filename;
}

/** 图片空间展示名：保留上传原名并去掉后缀（18.png → 18） */
function toImageSpaceDisplayName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const base = raw.includes('/') ? raw.split('/').pop() : raw;
  const dot = base.lastIndexOf('.');
  if (dot > 0 && dot < base.length - 1) return base.slice(0, dot);
  return base;
}

function isOpaqueImageSpaceName(name) {
  const base = toImageSpaceDisplayName(name);
  if (!base) return true;
  if (/^img-[a-f0-9]{16,}$/i.test(base)) return true;
  if (/^[a-f0-9]{32}$/i.test(base)) return true;
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(base)) return true;
  if (/^(本地上传|图案裁剪|智能抠图|超分提质|印花提取|智能工具)-/i.test(String(name || '').trim())) {
    return true;
  }
  return false;
}

/** 后端已登记时仍用原名回写展示名（覆盖「本地上传-xxxx」占位） */
async function syncImageSpaceDisplayName(id, rawName) {
  const key = String(id || '').trim();
  const display = toImageSpaceDisplayName(rawName);
  if (!key || !display || isOpaqueImageSpaceName(display) || isOpaqueImageSpaceName(rawName)) {
    return display;
  }
  try {
    await request(`/admin/image-space/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      data: { name: display },
      tokenType: 'funding',
    });
  } catch {
    /* best-effort：列表仍可能短暂显示占位名 */
  }
  return display;
}

async function syncBatchUploadImageSpace(batchResult, compressed, {
  registerImageSpace = true,
  source,
  ingestKind = 'upload',
} = {}) {
  if (registerImageSpace === false || !Array.isArray(batchResult?.urls) || batchResult.urls.length === 0) {
    return batchResult;
  }

  const urls = batchResult.urls;
  const backendIds = Array.isArray(batchResult.ids) ? batchResult.ids : [];
  const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
  const { appendImageSpaceDedupeCache } = await import('./imageSpaceDedupe');

  // signed-image upload/batch-upload 后端已 registerStoredUpload；优先用回传 id，否则从 URL 推导，不再二次 POST /image-space/batch
  const resolvedIds = urls.map((url, index) => {
    const fromBackend = backendIds[index] != null && String(backendIds[index]).trim()
      ? String(backendIds[index]).trim()
      : '';
    return fromBackend || imageSpaceIdFromStoredUrl(url);
  });

  if (resolvedIds.every((id) => id)) {
    const cached = [];
    for (let index = 0; index < urls.length; index += 1) {
      const persisted = normalizePersistedUploadUrl(String(urls[index] || ''));
      if (!persisted) continue;
      const fileSize = compressed[index]?.size;
      const rawName = compressed[index]?.name || `upload-${index + 1}`;
      const niceName = toImageSpaceDisplayName(rawName) || rawName;
      if (niceName) {
        await syncImageSpaceDisplayName(resolvedIds[index], niceName);
      }
      cached.push({
        id: resolvedIds[index],
        name: niceName,
        url: persisted,
        source: source || 'upload',
        ...(fileSize > 0 ? { size: fileSize } : {}),
        uploadedAt: now,
        createdAt: now,
      });
    }
    appendImageSpaceDedupeCache(cached);
    return {
      ...batchResult,
      ids: resolvedIds,
    };
  }

  // 极少数无法推导 id（非 image-space 路径等）时才回退 batchSave
  const records = urls
    .map((url, index) => {
      const persisted = normalizePersistedUploadUrl(String(url || ''));
      if (!persisted) return null;
      const fileSize = compressed[index]?.size;
      return enrichImageSpaceRecordsForIngest([{
        id: resolvedIds[index] || `img-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
        name: compressed[index]?.name || `upload-${index + 1}`,
        url: persisted,
        source: source || 'upload',
        ...(fileSize > 0 ? { size: fileSize } : {}),
        uploadedAt: now,
        createdAt: now,
      }], { ingestKind })[0];
    })
    .filter(Boolean);

  if (records.length === 0) return batchResult;

  try {
    const saveResult = await imageSpaceAPI.batchSave(records, {
      skipIngestAspect: true,
      dedupe: false,
    });
    const saved = Array.isArray(saveResult?.images) ? saveResult.images : records;
    appendImageSpaceDedupeCache(saved);
    return {
      ...batchResult,
      ids: saved.map((item) => (item?.id != null ? String(item.id) : null)),
    };
  } catch (e) {
    console.warn('[image-storage] 批量写入图片空间失败', e);
    return batchResult;
  }
}

export const imageStorageAPI = {
  save: async (input, options = {}) => {
    const {
      filename,
      folder = getImageSpaceUploadFolder(),
      signal,
      id,
      name: imageName,
      source,
      size,
      width,
      height,
      registerImageSpace = false,
      ingestKind = 'upload',
    } = options;
    const uploadFolder = String(folder || getImageSpaceUploadFolder()).trim() || getImageSpaceUploadFolder();
    const appendFolder = (formData) => {
      formData.append('folder', uploadFolder);
    };

    const finalizeUpload = async (result, displayName, fileMeta = {}) => {
      const url = result?.url;
      if (!url) return result;
      // 后端已入库：优先用回传 id，否则从 URL 推导（与 registerStoredUpload 一致），跳过 pageSize=5000
      const backendId = result?.id != null && result.id !== ''
        ? String(result.id)
        : imageSpaceIdFromStoredUrl(url);
      const niceName = toImageSpaceDisplayName(imageName || displayName || fileMeta.name || '')
        || toImageSpaceDisplayName(displayName)
        || '';
      if (backendId && registerImageSpace !== false) {
        if (niceName) {
          await syncImageSpaceDisplayName(backendId, niceName);
        }
        const { appendImageSpaceDedupeCache } = await import('./imageSpaceDedupe');
        appendImageSpaceDedupeCache([{
          id: backendId,
          name: niceName || backendId,
          url: normalizePersistedUploadUrl(url) || url,
          contentHash: fileMeta.contentHash || null,
          size: size ?? fileMeta.size ?? null,
          width: width ?? fileMeta.width ?? null,
          height: height ?? fileMeta.height ?? null,
          source,
        }]);
        return { ...result, id: backendId, name: niceName || result?.name };
      }
      const recordId = await registerUploadedImageToSpace({
        url,
        id: backendId || id,
        name: niceName || imageName || displayName,
        source,
        size: size ?? fileMeta.size,
        width: width ?? fileMeta.width,
        height: height ?? fileMeta.height,
        contentHash: fileMeta.contentHash,
        registerImageSpace,
        ingestKind: ingestKind || 'upload',
        fileMeta,
      });
      return recordId ? { ...result, id: recordId, name: niceName || undefined } : result;
    };

    const uploadFile = async (file) => runWithRetry(async () => {
      const normalized = normalizeUploadFileName(file);
      const formatError = validateIpeUploadFile(normalized);
      if (formatError) {
        throw new Error(formatError);
      }
      const compressed = normalizeUploadFileName(await compressFileForUpload(normalized));

      const tryDirect = UPLOAD_CONFIG.directOss !== false;
      if (tryDirect) {
        try {
          const caps = await fetchUploadCapabilities(request);
          if (caps.directOss) {
            const result = normalizeUploadResponseUrl(await directUploadOneFile({
              request,
              file: compressed,
              folder: uploadFolder,
              source,
              signal,
            }));
            return finalizeUpload(result, file.name, { size: file.size || null });
          }
        } catch (error) {
          if (signal?.aborted || error?.name === 'AbortError' || error?.kind === 'aborted') {
            throw error;
          }
          // 队列满 / 背压：交给 runWithRetry 退避，禁止改 multipart
          if (isUploadBackpressureError(error) || (error?.kind === 'complete' && Number(error?.status) === 429)) {
            throw error;
          }
          // 直传不可用或 CORS/凭证失败时回退 multipart
          resetUploadCapabilitiesCache();
          console.warn('[image-storage] OSS 直传失败，回退 multipart', error?.message || error);
        }
      }

      const formData = new FormData();
      formData.append('file', compressed);
      appendFolder(formData);
      let multipartResult = await request('/signed-image/upload', {
        method: 'POST',
        data: formData,
        tokenType: 'funding',
        timeoutMs: UPLOAD_CONFIG.timeoutMs,
        signal,
      });
      // Flag 开：multipart 也入队 → 202 processing，与直传 complete 同契约
      const { isAsyncProcessingComplete, resolveCompleteToReady } = await import('./uploadSanitizeWait');
      if (isAsyncProcessingComplete(multipartResult)) {
        multipartResult = await resolveCompleteToReady(request, multipartResult, { signal });
      }
      const result = normalizeUploadResponseUrl(multipartResult);
      return finalizeUpload(result, file.name, { size: file.size || null });
    });

    if (typeof input === 'string') {
      const trimmed = input.trim();
      const persisted = normalizePersistedUploadUrl(trimmed);
      if (persisted) {
        return { url: persisted };
      }
    }
    if (input instanceof File) {
      return uploadFile(input);
    }
    let blob = null;
    let dataUrlHint = '';
    let preferredName = filename || '';
    if (input instanceof Blob) {
      blob = input;
      if (!preferredName && input instanceof File && input.name) {
        preferredName = input.name;
      }
    } else if (typeof input === 'string') {
      const trimmed = input.trim();
      if (!trimmed) {
        throw new Error('无效图片地址');
      }
      if (trimmed.startsWith('data:')) {
        dataUrlHint = trimmed;
      }
      const response = await fetch(trimmed);
      if (!response.ok) {
        throw new Error(`读取图片失败(${response.status})`);
      }
      blob = await response.blob();
    } else {
      throw new Error('不支持的图片上传类型');
    }
    const file = await buildUploadFileFromBlob(blob, {
      filename: preferredName,
      dataUrlHint,
    });
    return uploadFile(file);
  },
  saveBatch: async (files, options = {}) => {
    const {
      folder = getImageSpaceUploadFolder(),
      signal,
      registerImageSpace = true,
      source,
      ingestKind = 'upload',
      /** 调用方已压缩时跳过，避免图片空间「压完再压」造成每批停顿 */
      skipCompress = false,
    } = options;
    const uploadFolder = String(folder || getImageSpaceUploadFolder()).trim() || getImageSpaceUploadFolder();
    const list = Array.isArray(files) ? files.filter(Boolean) : [];
    if (list.length === 0) {
      return { saved: 0, failed: 0, urls: [] };
    }
    const compressed = skipCompress
      ? list
      : await compressFilesForUpload(list);
    const appendFolder = (formData) => {
      formData.append('folder', uploadFolder);
    };

    const runMultipartBatch = async (fileList) => {
      const { mapWithUploadConcurrency, UPLOAD_CHUNK_CONCURRENCY } = await import('./globalUploadBudget');
      const {
        isAsyncProcessingComplete,
        resolveBatchCompleteToReady,
      } = await import('./uploadSanitizeWait');
      const chunkSize = Math.max(1, UPLOAD_CONFIG.chunkSize || 20);
      const chunkConcurrency = Math.max(1, UPLOAD_CONFIG.chunkConcurrency || UPLOAD_CHUNK_CONCURRENCY || 2);
      const overlap = UPLOAD_CONFIG.pipelineOverlap === true;
      const offsets = [];
      for (let offset = 0; offset < fileList.length; offset += chunkSize) {
        offsets.push(offset);
      }
      /** @type {Map<number, object>} */
      const resultsByOffset = new Map();
      /** @type {Promise<void>[]} */
      const readyWaiters = [];

      await mapWithUploadConcurrency(
        offsets,
        chunkConcurrency,
        async (offset) => {
          if (signal?.aborted) {
            resultsByOffset.set(offset, { saved: 0, failed: 0, urls: [], lastError: '已取消' });
            return;
          }
          const chunk = fileList.slice(offset, offset + chunkSize);
          const part = await runWithRetry(async () => {
            const formData = new FormData();
            chunk.forEach((file) => formData.append('files', file));
            appendFolder(formData);
            return request('/signed-image/batch-upload', {
              method: 'POST',
              data: formData,
              tokenType: 'funding',
              timeoutMs: UPLOAD_CONFIG.batchTimeoutMs,
              signal,
            });
          });
          if (!isAsyncProcessingComplete(part)) {
            resultsByOffset.set(offset, part);
            return;
          }
          if (!overlap) {
            resultsByOffset.set(
              offset,
              await resolveBatchCompleteToReady(request, part, { signal }),
            );
            return;
          }
          resultsByOffset.set(offset, part);
          readyWaiters.push(
            resolveBatchCompleteToReady(request, part, { signal }).then((ready) => {
              resultsByOffset.set(offset, ready);
            }),
          );
        },
      );

      if (readyWaiters.length > 0) {
        await Promise.all(readyWaiters);
      }

      const chunkResults = offsets
        .map((offset) => resultsByOffset.get(offset))
        .filter(Boolean);

      const allUrls = [];
      const allIds = [];
      let saved = 0;
      let failed = 0;
      let lastError = '';
      for (const part of chunkResults) {
        const urls = Array.isArray(part?.urls) ? part.urls : [];
        allUrls.push(...urls);
        if (Array.isArray(part?.ids)) {
          allIds.push(...part.ids);
        }
        saved += Number(part?.saved) || urls.length;
        failed += Number(part?.failed) || 0;
        if (part?.lastError) {
          lastError = part.lastError;
        }
      }
      const { mergeUploadViewUrlMaps } = await import('./uploadBatchViewUrls');
      const mergedViews = mergeUploadViewUrlMaps(chunkResults);
      const batchResult = {
        saved,
        failed,
        urls: allUrls,
        viewUrls: mergedViews.viewUrls,
        displayUrls: mergedViews.displayUrls,
      };
      if (allUrls.length > 0) {
        batchResult.url = allUrls[0];
      }
      if (allIds.length > 0) {
        batchResult.ids = allIds;
      }
      if (mergedViews.viewUrl) {
        batchResult.viewUrl = mergedViews.viewUrl;
      }
      if (mergedViews.displayUrl) {
        batchResult.displayUrl = mergedViews.displayUrl;
      }
      if (lastError) {
        batchResult.lastError = lastError;
      }
      if (overlap) {
        batchResult.pipelineOverlap = true;
      }
      return batchResult;
    };

    const mergeUploadResults = (primary, secondary) => {
      const a = primary && typeof primary === 'object' ? primary : {};
      const b = secondary && typeof secondary === 'object' ? secondary : {};
      const urls = [...(Array.isArray(a.urls) ? a.urls : []), ...(Array.isArray(b.urls) ? b.urls : [])];
      const ids = [...(Array.isArray(a.ids) ? a.ids : []), ...(Array.isArray(b.ids) ? b.ids : [])];
      const viewUrls = { ...(a.viewUrls || {}), ...(b.viewUrls || {}) };
      const displayUrls = { ...(a.displayUrls || {}), ...(b.displayUrls || {}) };
      const merged = {
        saved: (Number(a.saved) || 0) + (Number(b.saved) || 0),
        failed: (Number(a.failed) || 0) + (Number(b.failed) || 0),
        urls,
        viewUrls,
        displayUrls,
        lastError: b.lastError || a.lastError || '',
      };
      if (urls.length > 0) merged.url = urls[0];
      if (ids.length > 0) merged.ids = ids;
      if (a.viewUrl || b.viewUrl) merged.viewUrl = a.viewUrl || b.viewUrl;
      if (a.displayUrl || b.displayUrl) merged.displayUrl = a.displayUrl || b.displayUrl;
      return merged;
    };

    const tryDirect = UPLOAD_CONFIG.directOss !== false;
    if (tryDirect) {
      try {
        const caps = await fetchUploadCapabilities(request);
        if (caps.directOss) {
          const batchResult = await runWithRetry(async () => directUploadFilesBatch({
            request,
            files: compressed,
            folder: uploadFolder,
            source,
            signal,
            chunkSize: caps.maxFilesPerRequest || UPLOAD_CONFIG.chunkSize,
            chunkConcurrency: UPLOAD_CONFIG.chunkConcurrency,
            pipelineOverlap: UPLOAD_CONFIG.pipelineOverlap === true,
          }));
          const failedFiles = Array.isArray(batchResult?.failedFiles)
            ? batchResult.failedFiles.filter(Boolean)
            : [];
          let merged = batchResult;
          if (failedFiles.length > 0) {
            console.warn(
              `[image-storage] multipartFallbackReason=put_partial files=${failedFiles.length}`,
            );
            const mp = await runMultipartBatch(failedFiles);
            merged = mergeUploadResults(batchResult, mp);
          }
          return syncBatchUploadImageSpace(merged, compressed, {
            registerImageSpace,
            source,
            ingestKind,
          });
        }
      } catch (error) {
        if (signal?.aborted || error?.name === 'AbortError' || error?.kind === 'aborted') {
          throw error;
        }
        // 队列满 / 背压：向上抛（runWithRetry 已退避耗尽），禁止整批改 multipart
        if (isUploadBackpressureError(error) || (error?.kind === 'complete' && Number(error?.status) === 429)) {
          throw error;
        }
        // 仅 capabilities / CORS / 预签名等致命错误才整批 multipart
        if (isDirectUploadFatalError(error) || error?.kind === 'presign' || error?.kind === 'cors') {
          resetUploadCapabilitiesCache();
          console.warn(
            `[image-storage] multipartFallbackReason=${error?.kind || 'fatal'}`,
            error?.message || error,
          );
        } else {
          resetUploadCapabilitiesCache();
          console.warn('[image-storage] OSS 批量直传失败，回退 multipart 分片', error?.message || error);
        }
      }
    }

    // multipart 回退：按 chunkSize 分片 + 有界并行，禁止一次塞 300+ 张拖垮 API
    const batchResult = await runMultipartBatch(compressed);
    return syncBatchUploadImageSpace(batchResult, compressed, {
      registerImageSpace,
      source,
      ingestKind,
    });
  },
};
export const baiduTranslateAPI = {
  /** @deprecated 兼容旧名；实际上走 DeepSeek 导出翻译接口 */
  translate: (text, target = 'en', sourceLanguage = '') => {
    // 延迟解析，避免与 exportUtils/downloadCenter 形成循环依赖时拿到 undefined
    const api = listingExportTranslateAPI;
    if (!api || typeof api.translate !== 'function') {
      return Promise.reject(new Error('翻译接口未加载，请刷新页面后重试'));
    }
    return api.translate(text, target, sourceLanguage);
  },
};

/** 上架模板导出：DeepSeek 标题翻译（不改原标题） */
export const listingExportTranslateAPI = {
  translate: (text, targetLanguage = 'en', sourceLanguage = '') => request('/listing-export/translate-title', {
    method: 'POST',
    data: {
      text,
      targetLanguage,
      sourceLanguage: sourceLanguage || undefined,
    },
    tokenType: 'funding',
    timeoutMs: 120000,
  }),
};

/** 词条翻译映射库：查库优先，未命中 DeepSeek 翻译落库 */
export const glossaryAPI = {
  resolveTermTranslations: (payload = {}) => request('/glossary/term-translations/resolve', {
    method: 'POST',
    data: {
      scope: payload.scope || 'tiktok_size_chart_measure',
      targetLang: payload.targetLang || 'zh',
      items: Array.isArray(payload.items) ? payload.items : [],
    },
    tokenType: 'funding',
    timeoutMs: 120000,
  }),
};
export const automationTextAPI = {
  getAll: () => request('/automation-texts', {
    tokenType: 'funding',
    query: {
      page: 1,
      pageSize: 200
    }
  }),
  create: payload => request('/automation-texts', {
    method: 'POST',
    data: payload,
    tokenType: 'funding'
  }),
  update: (id, payload) => request(`/automation-texts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    data: payload,
    tokenType: 'funding'
  }),
  delete: id => request(`/automation-texts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    tokenType: 'funding'
  })
};

export const agentAPI = {
  getFeatureQuota: () => request('/agent/feature-quota', { tokenType: 'funding' }),
  listConversations: () => request('/agent/conversations', { tokenType: 'funding' }),
  getConversation: (conversationId) => request(
    `/agent/conversations/${encodeURIComponent(conversationId)}`,
    { tokenType: 'funding' },
  ),
  createConversation: ({
    mode,
    message,
    attachmentsJson,
    imageParamsJson,
    videoParamsJson,
    webSearchEnabled,
    signal,
    requestId,
  }) => request('/agent/conversations', {
    method: 'POST',
    data: {
      mode,
      message,
      attachmentsJson,
      imageParamsJson,
      videoParamsJson,
      webSearchEnabled: Boolean(webSearchEnabled),
    },
    tokenType: 'funding',
    signal,
    headers: requestId ? { 'X-Agent-Request-Id': requestId } : undefined,
  }),
  sendMessage: (conversationId, {
    mode,
    message,
    attachmentsJson,
    imageParamsJson,
    videoParamsJson,
    webSearchEnabled,
    signal,
    requestId,
  }) => request(
    `/agent/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: 'POST',
      data: {
        mode,
        message,
        attachmentsJson,
        imageParamsJson,
        videoParamsJson,
        webSearchEnabled: Boolean(webSearchEnabled),
      },
      tokenType: 'funding',
      signal,
      headers: requestId ? { 'X-Agent-Request-Id': requestId } : undefined,
    },
  ),
  cancelRequest: (requestId) => request(
    `/agent/requests/${encodeURIComponent(requestId)}/cancel`,
    {
      method: 'POST',
      tokenType: 'funding',
    },
  ),
  deleteConversation: (conversationId) => request(
    `/agent/conversations/${encodeURIComponent(conversationId)}`,
    {
      method: 'DELETE',
      tokenType: 'funding',
    },
  ),
};
